import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { format, subMonths, subDays, parseISO, formatISO } from "date-fns";
import { toDate } from "date-fns-tz";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useDispatch } from "react-redux";
import { API } from "../utils/apiUrls";
import { fetchWalletBalance } from "../redux/slices/walletSlice";
import { OrderBusinessStatusEnum, getOrderStatusName } from "../utils/enums";
import { formatInIST } from "../utils/dateHelpers";
import { formatCarrierName, formatErrorMessage, getErrorCategory } from "../utils/carrierUtils";
import CustomDateRangePicker from "../utils/CustomDateRangePicker";
import SOURCE_MAP from "../config/sourceMap";
import {
  Check,
  ChevronDown,
  Package,
  Warehouse,
  Truck,
  Navigation,
  PackageCheck,
  XCircle,
  Undo2,
  PackageX,
  RotateCcw,
  ClipboardCheck,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";

import ClaimUploadModal from "../components/ClaimUploadModal.jsx";
import CreateTicketModal from "../components/support/CreateTicketModal.jsx";
import ticketService from "../services/ticketService.js";
import { downloadShipmentInvoice } from "../services/shipmentInvoiceService.js";
import { downloadRemoteFile, openInNewTab } from "../utils/download.js";
import useAuth from "../hooks/useAuth.js";

const CSVIcon = {
  Download: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 10l5 5 5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 15V3" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  X: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Search: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <circle cx="11" cy="11" r="8" strokeWidth="2" />
      <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  ChevronUp: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M18 15l-6-6-6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  ChevronDown: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  GripVertical: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <circle cx="9" cy="12" r="1" fill="currentColor" />
      <circle cx="9" cy="5" r="1" fill="currentColor" />
      <circle cx="9" cy="19" r="1" fill="currentColor" />
      <circle cx="15" cy="12" r="1" fill="currentColor" />
      <circle cx="15" cy="5" r="1" fill="currentColor" />
      <circle cx="15" cy="19" r="1" fill="currentColor" />
    </svg>
  ),
  Info: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <circle cx="12" cy="12" r="10" strokeWidth="2" />
      <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="8" r="0.5" fill="currentColor" strokeWidth="1" />
    </svg>
  ),
  Check: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Eye: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeWidth="2" />
      <circle cx="12" cy="12" r="3" strokeWidth="2" />
    </svg>
  ),
};

// Tab colour law: a count badge goes loud (solid) only on the active tab with count > 0;
// inactive counts > 0 get a soft tint; zero counts stay quiet slate on every tab.
const TABS = [
  { key: "all", label: "Orders", Icon: Package, color: "text-[#2A59BD]", accent: "bg-[#3877FC]", badgeSolid: "bg-[#3877FC]", badgeTint: "bg-[#3877FC]/10 text-[#2A59BD] ring-1 ring-[#3877FC]/25" },
  { key: "pickupPending", label: "Pickups", Icon: Warehouse, color: "text-amber-600", accent: "bg-amber-500", badgeSolid: "bg-amber-500", badgeTint: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
  { key: "inTransit", label: "Transit", Icon: Truck, color: "text-yellow-700", accent: "bg-yellow-500", badgeSolid: "bg-yellow-500", badgeTint: "bg-yellow-50 text-yellow-700 ring-1 ring-yellow-200" },
  { key: "outForDelivery", label: "OFD", Icon: Navigation, color: "text-indigo-600", accent: "bg-indigo-500", badgeSolid: "bg-indigo-500", badgeTint: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200" },
  { key: "delivered", label: "Delivered", Icon: PackageCheck, color: "text-emerald-600", accent: "bg-emerald-500", badgeSolid: "bg-emerald-500", badgeTint: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
  { key: "failed", label: "Failed", Icon: XCircle, color: "text-rose-600", accent: "bg-rose-500", badgeSolid: "bg-rose-500", badgeTint: "bg-rose-50 text-rose-700 ring-1 ring-rose-200" },
  { key: "rto", label: "RTO", Icon: Undo2, color: "text-orange-600", accent: "bg-orange-500", badgeSolid: "bg-orange-500", badgeTint: "bg-orange-50 text-orange-700 ring-1 ring-orange-200" },
  { key: "lostDamaged", label: "Lost/Damaged", Icon: PackageX, color: "text-red-600", accent: "bg-red-500", badgeSolid: "bg-red-500", badgeTint: "bg-red-50 text-red-700 ring-1 ring-red-200" },
  { key: "rvp", label: "RVP", Icon: RotateCcw, color: "text-purple-600", accent: "bg-purple-500", badgeSolid: "bg-purple-500", badgeTint: "bg-purple-50 text-purple-700 ring-1 ring-purple-200" },
  { key: "confirmation", label: "Confirmation", Icon: ClipboardCheck, color: "text-cyan-700", accent: "bg-cyan-500", badgeSolid: "bg-cyan-600", badgeTint: "bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200" },
  { key: "unprocessed", label: "Error", Icon: AlertTriangle, color: "text-pink-600", accent: "bg-pink-500", badgeSolid: "bg-pink-600", badgeTint: "bg-pink-50 text-pink-700 ring-1 ring-pink-200" },
];

/** Count badge class per the tab colour law above. */
const tabBadgeClass = (tab, count, isActive) => {
  if (!count) return "bg-slate-100 text-slate-400 ring-1 ring-slate-200";
  if (isActive) return `${tab.badgeSolid} text-white shadow-sm`;
  return tab.badgeTint;
};

const COURIER_OPTIONS = [
  { value: "DELHIVERY", label: "Delhivery" },
  { value: "BLUEDART", label: "Bluedart" },
  { value: "DTDC", label: "DTDC" },
  { value: "SHADOWFAX", label: "Shadowfax" },
  { value: "XPRESSBEES", label: "XpressBees" },
  { value: "EKART", label: "Ekart" },
  { value: "AMAZON", label: "Amazon" },
  { value: "ATS", label: "Amazon ATS" },
];

const lottieSrc = "/animations/loader.lottie";

const STATUS_MAP = {
  all: [],
  failed: [OrderBusinessStatusEnum.FAILED_DELIVERY.code],
  pickupPending: [
    OrderBusinessStatusEnum.PICKUP_PENDING.code,
    OrderBusinessStatusEnum.OUT_FOR_PICKUP.code,
    OrderBusinessStatusEnum.PICKUP_FAILED.code,
    OrderBusinessStatusEnum.ORDER_PLACED.code,
    OrderBusinessStatusEnum.AWB_REGISTERED.code,
  ],
  inTransit: [
    OrderBusinessStatusEnum.INTRANSIT.code,
    OrderBusinessStatusEnum.SHIPMENT_DELAYED.code,
    OrderBusinessStatusEnum.SHIPMENT_HELD.code,
    OrderBusinessStatusEnum.PICKED_UP.code,
  ],
  delivered: [OrderBusinessStatusEnum.DELIVERED.code],
  rto: [
    OrderBusinessStatusEnum.RTO_REQUESTED.code,
    OrderBusinessStatusEnum.RTO.code,
    OrderBusinessStatusEnum.RTO_OUT_FOR_DELIVERY.code,
    OrderBusinessStatusEnum.RTO_DELIVERED.code,
    OrderBusinessStatusEnum.RTO_FAILED.code,
    OrderBusinessStatusEnum.RTO_INTRANSIT.code,
    OrderBusinessStatusEnum.RTO_CONTACT_CUSTOMER_CARE.code,
    OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code,
  ],
  outForDelivery: [OrderBusinessStatusEnum.OUT_FOR_DELIVERY.code],
  lostDamaged: [
    OrderBusinessStatusEnum.LOST.code,
    OrderBusinessStatusEnum.DAMAGED.code,
  ],
  unprocessed: [],
  rvp: [],
  confirmation: [],
};

// Lifecycle-ordered groups for the ORDER STATUS dropdown - the list reads as the
// shipment journey (pre-shipment -> pickup -> transit -> delivery -> RTO -> rest)
// instead of enum-definition order. Codes absent here land in a trailing "Other".
const STATUS_GROUPS = [
  { label: "Pre-shipment", codes: ["10", "20", "23", "24", "25", "26", "30", "310", "270"] },
  { label: "Pickup", codes: ["40", "240", "50", "360", "370"] },
  { label: "In transit", codes: ["60", "340", "350", "70", "200", "210", "220", "330"] },
  { label: "Delivery", codes: ["80", "100", "110", "90"] },
  { label: "RTO", codes: ["130", "140", "230", "260", "250", "150", "160", "170"] },
  { label: "Exchange", codes: ["280", "290", "300"] },
  { label: "Cancellation", codes: ["320", "120"] },
  { label: "Exceptions", codes: ["180", "190", "0", "9999"] },
];

const ConfirmModal = ({ open, title, message, onConfirm, onCancel }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        </div>
        <p className="text-gray-600 mb-6 leading-relaxed">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onCancel();
            }}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-semibold transition-all shadow-lg"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};
const StatusDropdown = ({ value, onChange, statuses }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const filteredStatuses = statuses.filter(status =>
    status.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    status.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedStatus = statuses.find(s => s.code === value);
  const selectedTone = selectedStatus ? statusTone(selectedStatus.code) : null;

  return (
    <div ref={dropdownRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-9 px-3 text-sm border rounded-lg transition-colors bg-white flex items-center justify-between gap-1.5 group ${isOpen
          ? 'ring-2 ring-[#3877FC]/25 border-[#3877FC]'
          : 'border-slate-200'
          }`}
      >
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {selectedStatus ? (
            <>
              <span className={`h-2 w-2 rounded-full flex-shrink-0 ${selectedTone.dot}`}></span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ring-1 truncate ${selectedTone.chip}`}>
                {selectedStatus.name}
              </span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <span className="text-slate-400 font-normal text-sm">All Statuses</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedStatus && (
            // span, not button: this sits inside the trigger <button>, and HTML forbids
            // nested buttons (React flags it as a hydration hazard).
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setIsOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange('');
                  setIsOpen(false);
                }
              }}
              className="p-0.5 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              title="Clear selection"
              aria-label="Clear selection"
            >
              <svg className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          )}
          <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-[9999] mt-2 w-full bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-scale-in">
          <div className="p-2.5 border-b border-slate-100 bg-slate-50/60">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onBlur={(e) => setSearchTerm(e.target.value.trim())}
                placeholder="Search status..."
                className="w-full h-9 pl-10 pr-4 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#3877FC]/25 focus:border-[#3877FC] outline-none transition-colors bg-white"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[320px] overflow-y-auto custom-scrollbar">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
                setSearchTerm('');
              }}
              className={`w-full px-3 min-h-[2.5rem] py-2 flex items-center gap-2.5 text-sm transition-colors border-b border-slate-100 ${!value
                ? 'bg-[#3877FC]/[0.06] text-[#2A59BD] font-semibold'
                : 'hover:bg-[#3877FC]/[0.05] text-slate-700'
                }`}
            >
              <span className="flex-1 text-left">All Statuses</span>
              {!value && <Check className="w-4 h-4 text-[#3877FC] flex-shrink-0" strokeWidth={2.5} />}
            </button>

            {filteredStatuses.length > 0 ? (
              (() => {
                const claimed = new Set();
                const grouped = [];
                STATUS_GROUPS.forEach((group) => {
                  const members = group.codes
                    .map((code) => filteredStatuses.find((st) => st.code === code))
                    .filter(Boolean);
                  members.forEach((m) => claimed.add(m.code));
                  if (members.length) grouped.push({ label: group.label, members });
                });
                const leftovers = filteredStatuses.filter((st) => !claimed.has(st.code));
                if (leftovers.length) grouped.push({ label: "Other", members: leftovers });
                const showHeaders = grouped.length > 1;
                return grouped.map((group) => (
                  <div key={group.label}>
                    {showHeaders && (
                      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-50/60 border-y border-slate-100">
                        {group.label}
                      </div>
                    )}
                    {group.members.map((status) => {
                      const tone = statusTone(status.code);
                      const isSelected = value === status.code;
                      return (
                        <button
                          key={status.code}
                          type="button"
                          onClick={() => {
                            onChange(status.code);
                            setIsOpen(false);
                            setSearchTerm('');
                          }}
                          className={`w-full px-3 min-h-[2.5rem] py-2 flex items-center gap-2.5 text-sm transition-colors ${isSelected
                            ? 'bg-[#3877FC]/[0.06]'
                            : 'hover:bg-[#3877FC]/[0.05]'
                            }`}
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tone.dot}`}></span>
                          <span className={`flex-1 text-left leading-snug ${isSelected ? 'text-[#2A59BD] font-semibold' : 'text-slate-700'}`}>
                            {status.name}
                          </span>
                          {isSelected && <Check className="w-4 h-4 text-[#3877FC] flex-shrink-0" strokeWidth={2.5} />}
                        </button>
                      );
                    })}
                  </div>
                ));
              })()
            ) : (
              <div className="px-4 py-8 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-gray-500 font-medium">No statuses found</p>
                <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Copy affordance with the copied beat — check-swap for 1.4s, same reveal classes as before. */
const CellCopy = ({ value, title, className }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText?.(String(value));
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      }}
      className={className}
      title={title}
      aria-label={title}
    >
      {copied ? (
        <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
};

/** Custom select — one dropdown flavour everywhere (native pickers can't be styled). */
const SelectMenu = ({ value, onChange, options, disabled = false, buttonClassName = "h-9", dropUp = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setIsOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((o) => !o)}
        className={`w-full ${buttonClassName} px-3 text-sm border rounded-lg bg-white flex items-center justify-between gap-1.5 transition-colors ${disabled
          ? 'bg-slate-50 text-slate-400 cursor-not-allowed border-slate-200'
          : isOpen
            ? 'ring-2 ring-[#3877FC]/25 border-[#3877FC] text-slate-800'
            : 'border-slate-200 text-slate-800'
          }`}
      >
        <span className={`truncate ${selected?.value === '' ? 'text-slate-400' : ''}`}>{selected?.label}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} strokeWidth={2} />
      </button>

      {isOpen && (
        <div className={`absolute z-[9999] w-full min-w-[10rem] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden animate-scale-in ${dropUp ? 'bottom-full mb-2' : 'mt-2'}`}>
          <div className="max-h-64 overflow-y-auto custom-scrollbar py-1">
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onChange(opt.value); setIsOpen(false); }}
                  className={`w-full px-3 min-h-9 py-2 flex items-center gap-2.5 text-sm transition-colors ${isSelected
                    ? 'bg-[#3877FC]/[0.06] text-[#2A59BD] font-semibold'
                    : 'hover:bg-[#3877FC]/[0.05] text-slate-700'
                    }`}
                >
                  <span className="flex-1 text-left leading-snug">{opt.label}</span>
                  {isSelected && <Check className="w-4 h-4 text-[#3877FC] flex-shrink-0" strokeWidth={2.5} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ tab, onReset }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="w-32 h-32 mb-6 opacity-30">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-full h-full text-gray-400">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
      </svg>
    </div>
    <h3 className="text-lg font-bold text-gray-700 mb-2">No shipments found</h3>
    <p className="text-gray-500 mb-6 text-center max-w-md">
      {tab === "all"
        ? "No shipments match your current filters. Try adjusting your search criteria."
        : `No shipments found in the "${TABS.find(t => t.key === tab)?.label}" category.`}
    </p>
    <button
      onClick={onReset}
      className="px-6 py-2.5 bg-[#3877FC] text-white rounded-lg hover:bg-[#2A59BD] transition-colors font-medium"
    >
      Reset Filters
    </button>
  </div>
);

const TableSkeleton = () => (
  <div className="space-y-3 p-4">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="animate-pulse flex space-x-4 bg-gray-50 p-4 rounded-lg">
        <div className="h-4 w-4 bg-gray-300 rounded"></div>
        <div className="flex-1 space-y-3">
          <div className="h-4 bg-gray-300 rounded w-3/4"></div>
          <div className="grid grid-cols-5 gap-4">
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
            <div className="h-3 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ActionMenu = ({ shipment, onAction, currentPage, onOpenChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const isCancelled =
    String(shipment.latest_order_status_code) ===
    OrderBusinessStatusEnum.CANCELLED_ORDER.code;


  const isConfirmationAwaited =
    String(shipment.latest_order_status_code) ===
    OrderBusinessStatusEnum.ORDER_CONFIRMATION_AWAITED.code;

  const actions = [
    ...(isConfirmationAwaited ? [{
      label: "Place Order",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      color: "text-cyan-600 hover:bg-cyan-50",
      action: "place_order",
    }] : []),
    {
      label: "View Details",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943
               9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
      color: "text-slate-600 hover:bg-slate-50",
      action: "details",
    },
    {
      label: "Download Label",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0
               011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
          />
        </svg>
      ),
      color: "text-emerald-600 hover:bg-emerald-50",
      action: "label",
    },
    {
      label: "Download Invoice",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      color: shipment.order_reference_number
        ? "text-amber-700 hover:bg-amber-50"
        : "text-gray-400",
      action: "invoice",
      disabled: !shipment.order_reference_number,
    },
    {
      label: "Add Claim",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0
               2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464
               0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
      color: "text-purple-600 hover:bg-purple-50",
      action: "claim",
    },
    {
      label: "QC Image",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 7.5A2.5 2.5 0 015.5 5h2.379a1 1 0 00.707-.293l1.414-1.414A1 1 0 0110.707 3h2.586a1 1 0 01.707.293l1.414 1.414a1 1 0 00.707.293H18.5A2.5 2.5 0 0121 7.5v9A2.5 2.5 0 0118.5 19h-13A2.5 2.5 0 013 16.5v-9zM12 16a4 4 0 100-8 4 4 0 000 8z"
          />
        </svg>
      ),
      color: shipment.awb_number ? "text-indigo-600 hover:bg-indigo-50" : "text-gray-400",
      action: "download_qc_image",
      disabled: !shipment.awb_number,
    },
    {
      label: "Proof of Delivery",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      ),
      color: shipment.awb_number ? "text-teal-600 hover:bg-teal-50" : "text-gray-400",
      action: "download_epod_image",
      disabled: !shipment.awb_number,
    },
    {
      label: "Sorter Image",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      ),
      color: shipment.awb_number ? "text-amber-600 hover:bg-amber-50" : "text-gray-400",
      action: "download_sorter_image",
      disabled: !shipment.awb_number,
    },
    {
      label: "Support",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
      ),
      color: "text-[#2A59BD] hover:bg-[#3877FC]/[0.06]",
      action: "support",
    },
    {
      label: "Clone Order",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0
               012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0
               002-2v-8a2 2 0 00-2-2h-8a2 2 0
               00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
      color: "text-indigo-600 hover:bg-indigo-50",
      action: "clone",
    },
    {
      label: "Cancel Order",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      ),
      color: isCancelled ? "text-gray-400" : "text-red-600 hover:bg-red-50",
      action: "cancel",
      disabled: isCancelled,
    },
  ];

  const computePosition = useCallback(() => {
    if (!buttonRef.current || !menuRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const menuHeight = menuRef.current.offsetHeight || 260;
    const menuWidth = menuRef.current.offsetWidth || 224;

    const margin = 8;

    let top = rect.bottom + margin;
    let placeAbove = false;

    if (top + menuHeight > viewportHeight - margin) {
      top = rect.top - menuHeight - margin;
      placeAbove = true;
    }

    if (top < margin) top = margin;
    if (top + menuHeight > viewportHeight - margin) {
      top = viewportHeight - menuHeight - margin;
    }

    let left = rect.right - menuWidth;

    if (left < margin) left = margin;
    if (left + menuWidth > viewportWidth - margin) {
      left = viewportWidth - menuWidth - margin;
    }

    setPosition({ top, left });
  }, []);

  const handleToggle = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    onOpenChange?.(willOpen);
  };

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (!isOpen) return;

    requestAnimationFrame(() => {
      computePosition();
    });

    const handleWindowChange = () => computePosition();
    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);

    return () => {
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [isOpen, computePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, closeMenu]);

  // One calm dialect: monochrome rows, colour only for the primary (Place Order) and the
  // destructive (Cancel). Disabled rows explain themselves inline instead of just greying out.
  const DISABLED_REASONS = {
    invoice: "No order reference",
    download_qc_image: "Needs AWB",
    download_epod_image: "Needs AWB",
    download_sorter_image: "Needs AWB",
    cancel: "Already cancelled",
  };
  const GROUP_START = new Set(["label", "claim", "cancel"]);
  const rowTone = (action) => {
    if (action.disabled) return "text-slate-300 cursor-not-allowed [&_svg]:text-slate-300";
    if (action.action === "cancel") return "text-rose-600 hover:bg-rose-50 [&_svg]:text-rose-500";
    if (action.action === "place_order") return "text-[#2A59BD] hover:bg-[#3877FC]/[0.06] [&_svg]:text-[#3877FC]";
    return "text-slate-700 hover:bg-slate-50 [&_svg]:text-slate-400";
  };

  const menuNode =
    isOpen &&
    ReactDOM.createPortal(
      <div
        ref={menuRef}
        className="fixed z-[9999] w-60 bg-white rounded-xl shadow-xl ring-1 ring-slate-200 py-1.5 animate-scale-in"
        style={{ top: position.top, left: position.left }}
      >
        {actions.map((action, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && GROUP_START.has(action.action) && <div className="my-1 h-px bg-slate-100" aria-hidden="true"></div>}
          <button
            onClick={() => {
              if (!action.disabled) {
                onAction(action.action, shipment, currentPage);
                closeMenu();
              }
            }}
            disabled={action.disabled}
            title={action.disabled ? DISABLED_REASONS[action.action] : undefined}
            className={`w-full h-9 px-3 flex items-center gap-2.5 text-[13px] font-medium transition-colors ${rowTone(action)}`}
          >
            {action.icon}
            <span>{action.label}</span>
            {action.disabled && DISABLED_REASONS[action.action] && (
              <span className="ml-auto text-[10px] font-normal text-slate-300 whitespace-nowrap">{DISABLED_REASONS[action.action]}</span>
            )}
          </button>
          </React.Fragment>
        ))}
      </div>,
      document.body
    );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        title="Actions"
        aria-label="Row actions"
        className={`h-7 w-7 rounded-md border transition-[color,opacity] flex items-center justify-center ${isOpen
          ? "border-slate-300 bg-slate-100 text-slate-900 opacity-100"
          : "border-slate-200 bg-white text-slate-500 hover:text-slate-900"}`}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
        </svg>
      </button>
      {menuNode}
    </div>
  );
};

/**
 * Table typography system (main tab):
 * - PRIMARY line per cell: text-sm font-medium text-slate-900 (one per cell)
 * - MUTED subline: text-xs text-slate-500 (at most one)
 * - Identifiers (ref/AWB/SKU): font-mono text-[13px]; AWB stays the brand-blue link
 * - Numbers: tabular-nums, right-aligned column; the ₹ amount is the only semibold number
 * - Header: bg-slate-50, text-[11px] font-semibold uppercase tracking-wide text-slate-500,
 *   inset hairline bottom (shadow survives sticky, unlike collapsed borders)
 * - One saturated element per row: the status chip + its 3px status-tinted left edge
 */

/** "26 Aug, 11:34 pm" in IST; year only when it isn't the current year. Pure. */
const formatShortIST = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const now = new Date();
  const sameYear = date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric" }) ===
    now.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric" });
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    ...(sameYear ? {} : { year: "numeric" }),
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** "City - PIN" from whichever parts exist; muted em dash when neither does. Pure. */
const cityPinLine = (city, pin) => [city, pin].filter(Boolean).join(" - ") || "—";

/** 3px row edge tint - mirrors the StatusBadge chip mapping family-for-family. Pure. */
// ---- Status tone system: ONE source of truth for edge + chip + dot + journey stage ----
const STATUS_TONE_FAMILIES = {
  delivered: { chip: "bg-emerald-100 text-emerald-800 ring-emerald-200", dot: "bg-emerald-500", edge: "bg-emerald-400" },
  rto: { chip: "bg-orange-100 text-orange-800 ring-orange-200", dot: "bg-orange-500", edge: "bg-orange-400" },
  cancel: { chip: "bg-rose-100 text-rose-700 ring-rose-200", dot: "bg-rose-500", edge: "bg-rose-400" },
  failed: { chip: "bg-red-100 text-red-800 ring-red-200", dot: "bg-red-500", edge: "bg-red-400" },
  ofd: { chip: "bg-indigo-100 text-indigo-800 ring-indigo-200", dot: "bg-indigo-500", edge: "bg-indigo-400" },
  transit: { chip: "bg-yellow-100 text-yellow-800 ring-yellow-300", dot: "bg-yellow-500", edge: "bg-yellow-400" },
  pickup: { chip: "bg-amber-100 text-amber-800 ring-amber-200", dot: "bg-amber-500", edge: "bg-amber-400" },
  exchange: { chip: "bg-purple-100 text-purple-800 ring-purple-200", dot: "bg-purple-500", edge: "bg-purple-400" },
  payment: { chip: "bg-pink-100 text-pink-800 ring-pink-200", dot: "bg-pink-500", edge: "bg-pink-400" },
  confirmation: { chip: "bg-cyan-100 text-cyan-800 ring-cyan-200", dot: "bg-cyan-500", edge: "bg-cyan-400" },
  attention: { chip: "bg-amber-100 text-amber-800 ring-amber-200", dot: "bg-amber-500", edge: "bg-amber-400" },
  neutral: { chip: "bg-slate-100 text-slate-700 ring-slate-200", dot: "bg-slate-400", edge: "bg-slate-200" },
};

// Every OrderBusinessStatusEnum code, explicitly: [family, journeyStage 1-5 | null, stageLabel]
const STATUS_CODE_TONE = {
  "0":    ["neutral", null],
  "10":   ["payment", null],
  "20":   ["payment", null],
  "23":   ["confirmation", null],
  "24":   ["failed", null],
  "25":   ["confirmation", null],
  "26":   ["cancel", null],
  "30":   ["pickup", 1, "Order placed"],
  "270":  ["pickup", 1, "AWB registered"],
  "310":  ["pickup", 1, "Manifested"],
  "40":   ["pickup", 2, "Pickup pending"],
  "240":  ["pickup", 2, "Out for pickup"],
  "50":   ["failed", null],
  "60":   ["transit", 3, "Picked up"],
  "70":   ["transit", 3, "In transit"],
  "200":  ["transit", 3, "Delayed / misroute"],
  "220":  ["transit", 3, "Shipment held"],
  "330":  ["transit", 3, "At destination hub"],
  "340":  ["transit", 3, "At origin hub"],
  "350":  ["transit", 3, "Left origin hub"],
  "90":   ["failed", null],
  "80":   ["ofd", 4, "Out for delivery"],
  "100":  ["delivered", 5, "Delivered"],
  "110":  ["failed", null],
  "120":  ["cancel", null],
  "320":  ["cancel", null],
  "360":  ["cancel", null],
  "370":  ["cancel", null],
  "130":  ["rto", 3, "RTO requested"],
  "140":  ["rto", 3, "Returning to origin"],
  "230":  ["rto", 3, "RTO in transit"],
  "260":  ["rto", 3, "RTO delayed"],
  "250":  ["rto", 3, "RTO - contact care"],
  "150":  ["rto", 4, "RTO out for delivery"],
  "160":  ["rto", 5, "Returned to origin"],
  "170":  ["failed", null],
  "180":  ["failed", null],
  "190":  ["failed", null],
  "210":  ["attention", null],
  "280":  ["exchange", null],
  "290":  ["exchange", null],
  "300":  ["exchange", null],
  "9999": ["neutral", null],
};

const statusTone = (code) => {
  const entry = STATUS_CODE_TONE[String(code)];
  const family = STATUS_TONE_FAMILIES[entry?.[0]] || STATUS_TONE_FAMILIES.neutral;
  return { ...family, stage: entry?.[1] ?? null, stageLabel: entry?.[2] || null };
};

const statusEdgeClass = (code) => statusTone(code).edge;

/** Carrier EDD strings vary; parse leniently, else show the raw value. */
const fmtEdd = (edd) => {
  const d = new Date(edd);
  return Number.isNaN(d.getTime()) ? String(edd) : format(d, "dd MMM yyyy");
};

/** Compact "how long ago" for table timestamps - "just now" / "34m ago" / "5h ago" / "12d ago". */
const relTime = (input) => {
  if (!input) return null;
  const t = new Date(input).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/** Day-relative EDD phrasing - "by today" / "by tomorrow" / "in N days" / "N days overdue". */
const eddRelPhrase = (edd) => {
  const d = new Date(edd);
  if (Number.isNaN(d.getTime())) return null;
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(new Date())) / 86400000);
  if (days === 0) return "by today";
  if (days === 1) return "by tomorrow";
  if (days > 1) return `in ${days} days`;
  return `${-days} day${days === -1 ? "" : "s"} overdue`;
};

/** Verdict-line EDD date: drops the year inside the current year ("29 Aug") so date + verdict fit one line. */
const fmtEddShort = (edd) => {
  const d = new Date(edd);
  if (Number.isNaN(d.getTime())) return String(edd);
  return format(d, d.getFullYear() === new Date().getFullYear() ? "dd MMM" : "dd MMM yy");
};

// Mirrors the BE EddLadder gate (post-pickup forward / RTO legs / terminal events) so a row
// can never show an EDD before its own chip says the parcel moved - even if stale or racy
// data slips extra fields into a pre-pickup row.
const EDD_FORWARD_CODES = new Set(["60", "70", "200", "210", "220", "330", "340", "350", "80"]);
const EDD_RTO_LEG_CODES = new Set(["130", "140", "230", "260", "250", "150"]);
const eddVisibleForCode = (statusCode, kind, isVerdict) => {
  const code = String(statusCode ?? "");
  if (isVerdict) return code === "100" || code === "160";
  return kind === "RETURN" ? EDD_RTO_LEG_CODES.has(code) : EDD_FORWARD_CODES.has(code);
};

/** Grams -> "1.25 kg"; null-safe. Pure. */
const gramsToKg = (g) => (g != null ? `${(Number(g) / 1000).toFixed(2)} kg` : "—");


/** "DELHIVERY SURFACE" -> "Delhivery Surface" — calmer, ~15% narrower than caps. */
const titleCaseName = (name) =>
  name ? String(name).toLowerCase().replace(/(^|[\s/-])\w/g, (c) => c.toUpperCase()) : name;

/** "Chennai -> East Delhi" city pair for the route line. */
const cityLabel = (city) => (city ? titleCaseName(city) : null);

/**
 * The row's EDD fact from the search response, gated twice: the BE ladder decides which
 * rows carry the fields, and eddVisibleForCode re-checks against the row's own status so
 * an EDD can never appear pre-pickup. Verdicts stay one line (the chip above already says
 * Delivered/RTO Delivered); in-flight dates get the details-page label-over-value dialect
 * with an explicit "Expected delivery" wording. Palette matches the hero fact strip:
 * verdict emerald/rose, courier EDD blue, estimate amber, return orange.
 */
const RowEddFact = ({ shipment, statusCode }) => {
  const date = shipment?.expected_delivery_date;
  if (!date) return null;
  const delta = shipment?.delivery_delta_days;
  const kind = shipment?.edd_kind || "DELIVERY";
  const source = shipment?.edd_source || "CARRIER";
  if (!eddVisibleForCode(statusCode, kind, delta != null)) return null;
  const vsBase = `${source === "CARRIER" ? "courier" : "estimated"} ${kind === "RETURN" ? "return EDD" : "EDD"}`;

  const label = kind === "RETURN"
    ? (source === "ESTIMATED" ? "Est. return" : "Expected return")
    : (source === "ESTIMATED" ? "Est. delivery" : "Expected delivery");

  if (delta != null) {
    const n = Math.abs(delta);
    const verdict = delta > 0 ? `${n} day${n === 1 ? "" : "s"} late`
      : delta < 0 ? `${n} day${n === 1 ? "" : "s"} early` : "On time";
    return (
      <span className="mt-2 flex flex-col leading-tight" title={`${verdict} vs ${vsBase} ${fmtEdd(date)}`}>
        <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
        <span className="text-[11px] truncate">
          <span className="font-medium text-slate-700">{fmtEddShort(date)}</span>
          <span className="text-slate-400"> · </span>
          <span className={`font-semibold ${delta > 0 ? "text-rose-600" : "text-emerald-600"}`}>{verdict}</span>
        </span>
      </span>
    );
  }
  const toneCls = kind === "RETURN" ? "text-orange-600"
    : source === "ESTIMATED" ? "text-amber-600" : "text-blue-700";
  const fullLabel = kind === "RETURN"
    ? (source === "ESTIMATED" ? "Estimated return to origin" : "Courier return EDD")
    : (source === "ESTIMATED" ? "Estimated delivery" : "Courier expected delivery");
  const rel = eddRelPhrase(date);
  const overdue = rel != null && rel.endsWith("overdue");
  return (
    <span className="mt-2 flex flex-col leading-tight" title={`${fullLabel}: ${fmtEdd(date)}`}>
      <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <span className={`text-[11px] font-semibold truncate ${toneCls}`}>
        {fmtEddShort(date)}
        {rel && (
          <span className={`font-medium ${overdue ? "text-rose-500" : "text-slate-400"}`}> ({rel})</span>
        )}
      </span>
    </span>
  );
};

const StatusBadge = ({ statusCode, shipment, onSupportClick }) => {
  const statusName = getOrderStatusName(statusCode);
  const formattedLatestStatusTime = formatShortIST(shipment?.latest_status_timestamp);

  const tone = statusTone(statusCode);

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1">
        <span className={`inline-flex w-fit min-w-0 items-center px-2 py-0.5 rounded-full text-xs font-semibold leading-snug ring-1 ${tone.chip}`} title={statusName}>
          <span className="line-clamp-2">{statusName}</span>
        </span>
      </div>
      {tone.stage != null && (
        <div
          className="flex items-center gap-0.5 max-w-[120px]"
          title={`${tone.stageLabel} · stage ${tone.stage} of 5`}
          aria-hidden="true"
        >
          {[1, 2, 3, 4, 5].map((seg) => (
            <span
              key={seg}
              className={`h-[3px] flex-1 rounded-full ${seg <= tone.stage ? tone.dot : 'bg-slate-200'}`}
            ></span>
          ))}
        </div>
      )}
      <span className="text-[11px] text-slate-500">
        {formattedLatestStatusTime}
        {relTime(shipment?.latest_status_timestamp) && (
          <span className="text-slate-400"> ({relTime(shipment?.latest_status_timestamp)})</span>
        )}
      </span>
      <RowEddFact shipment={shipment} statusCode={statusCode} />
    </div>
  );
};


// ===== Column Configuration System =====
const MAIN_TABLE_COLUMNS = [
  { key: 'order_details', label: 'Order', category: 'order', icon: '📋', description: 'Order reference, AWB, date and tracking', defaultVisible: true, defaultOrder: 0 },
  { key: 'awb_tracking', label: 'Courier', category: 'shipping', icon: '🚚', description: 'Courier, order type and sales channel', defaultVisible: true, defaultOrder: 1 },
  { key: 'route', label: 'Route', category: 'shipping', icon: '🧭', description: 'Pickup to delivery, names and cities', defaultVisible: true, defaultOrder: 2 },
  { key: 'payment', label: 'Value', category: 'financial', icon: '💳', description: 'COD collectible or declared invoice value, with payment mode', defaultVisible: true, defaultOrder: 3 },
  { key: 'package', label: 'Package', category: 'package', icon: '📦', description: 'Charged weight, secondary weight, and category', defaultVisible: true, defaultOrder: 4 },
  { key: 'items', label: 'Items', category: 'product', icon: '🛍️', description: 'Item names with +N more for multi-item orders', defaultVisible: false, defaultOrder: 5 },
  { key: 'status', label: 'Status', category: 'delivery', icon: '📊', description: 'Current delivery status with support option', defaultVisible: true, defaultOrder: 6 },
  { key: 'pickup', label: 'Pickup', category: 'pickup', icon: '🏭', description: 'Pickup warehouse name and location', defaultVisible: false, defaultOrder: 7 },
  { key: 'delivery', label: 'Delivery', category: 'customer', icon: '📍', description: 'Recipient name, city, and pincode', defaultVisible: false, defaultOrder: 8 },
  { key: 'invoice_number', label: 'Invoice No.', category: 'order', icon: '🧾', description: 'Invoice reference number', defaultVisible: false, defaultOrder: 9 },
  { key: 'order_type', label: 'Order Type', category: 'order', icon: '↔️', description: 'Forward or Reverse order', defaultVisible: false, defaultOrder: 10 },
  { key: 'drop_phone', label: 'Customer Phone', category: 'customer', icon: '📞', description: "Recipient's contact number", defaultVisible: false, defaultOrder: 12 },
  { key: 'drop_state', label: 'Delivery State', category: 'customer', icon: '🗺️', description: 'State of the delivery destination', defaultVisible: false, defaultOrder: 13 },
  { key: 'drop_pincode', label: 'Delivery Pincode', category: 'customer', icon: '📮', description: 'Delivery destination pincode', defaultVisible: false, defaultOrder: 14 },
  { key: 'invoice_value', label: 'Invoice Value', category: 'financial', icon: '💰', description: 'Total invoice / order value in ₹', defaultVisible: false, defaultOrder: 15, numeric: true },
  { key: 'shipping_charges', label: 'Shipping Charges', category: 'financial', icon: '🚢', description: 'Total shipping cost charged', defaultVisible: false, defaultOrder: 16, numeric: true },
  { key: 'dead_weight', label: 'Dead Weight', category: 'package', icon: '⚖️', description: 'Physical weight in grams', defaultVisible: false, defaultOrder: 17, numeric: true },
  { key: 'volumetric_weight', label: 'Vol. Weight', category: 'package', icon: '📏', description: 'Calculated volumetric weight in grams', defaultVisible: false, defaultOrder: 18, numeric: true },
  { key: 'marketplace_channel', label: 'Sales Channel', category: 'order', icon: '🛒', description: 'Marketplace or sales channel name', defaultVisible: false, defaultOrder: 19 },
  { key: 'pickup_city', label: 'Pickup City', category: 'pickup', icon: '🏙️', description: 'City of the pickup warehouse', defaultVisible: false, defaultOrder: 20 },
  { key: 'shipment_age', label: 'Shipment Age', category: 'analytics', icon: '⏱️', description: 'Number of days since order was placed', defaultVisible: false, defaultOrder: 21, numeric: true },
  { key: 'item_sku_col', label: 'Item SKU', category: 'product', icon: '🏷️', description: 'SKU codes with +N more for multi-item orders', defaultVisible: false, defaultOrder: 22 },
];

/**
 * MAIN table column widths — the no-horizontal-scroll contract.
 *
 * The table is `table-fixed`, so column widths come from these th classes
 * (first row wins; box-sizing is border-box, so each width INCLUDES the
 * cell's horizontal padding). Budget for the DEFAULT visible set, sized for
 * the worst-case laptop content viewport (1280 - 256 sidebar - page px ≈ 1006px):
 *
 *   checkbox 40 + order_details 148 + awb_tracking 116 + route 164
 *   + payment 92 + package 100 + items 108 + status 136 + actions 96  = 1000px
 *
 * ≤ ~1006px available → no horizontal scroll with the default set; on wider
 * laptops the surplus stretches the columns (fixed layout distributes extra).
 * Non-default columns also carry explicit widths (sized so their nowrap
 * headers fit): enabling them pushes the fixed-layout sum past the container,
 * which is exactly when horizontal scroll is expected and fine.
 * Unknown keys fall back to auto (no width class).
 */
const MAIN_COL_WIDTHS = {
  order_details: 'w-[208px]',
  awb_tracking: 'w-[128px]',
  route: 'w-[184px]',
  payment: 'w-[84px]',
  package: 'w-[104px]',
  items: 'w-[100px]',
  status: 'w-[176px]',
  pickup: 'w-[160px]',
  delivery: 'w-[160px]',
  invoice_number: 'w-[140px]',
  order_type: 'w-[110px]',
  drop_phone: 'w-[136px]',
  drop_state: 'w-[140px]',
  drop_pincode: 'w-[150px]',
  invoice_value: 'w-[130px]',
  shipping_charges: 'w-[150px]',
  dead_weight: 'w-[120px]',
  volumetric_weight: 'w-[120px]',
  marketplace_channel: 'w-[140px]',
  pickup_city: 'w-[130px]',
  shipment_age: 'w-[130px]',
  item_sku_col: 'w-[140px]',
};

const UNPROCESSED_TABLE_COLUMNS = [
  { key: 'order_ref', label: 'Order Reference', category: 'order', icon: '📋', description: 'Order ID, internal ID, and last updated date', defaultVisible: true, defaultOrder: 0 },
  { key: 'pickup', label: 'Pickup', category: 'pickup', icon: '🏭', description: 'Pickup contact name and city', defaultVisible: true, defaultOrder: 1 },
  { key: 'delivery', label: 'Delivery', category: 'customer', icon: '📍', description: 'Delivery contact name and city', defaultVisible: true, defaultOrder: 2 },
  { key: 'invoice', label: 'Invoice', category: 'financial', icon: '💰', description: 'Invoice value', defaultVisible: true, defaultOrder: 3 },
  { key: 'items', label: 'Items', category: 'product', icon: '🛍️', description: 'Item names in this order', defaultVisible: true, defaultOrder: 4 },
  { key: 'error_summary', label: 'Error Summary', category: 'operational', icon: '⚠️', description: 'Carrier allocation attempt errors', defaultVisible: true, defaultOrder: 5 },
  { key: 'pickup_pincode', label: 'Pickup Pincode', category: 'pickup', icon: '📮', description: 'Postal code for pickup location', defaultVisible: false, defaultOrder: 6 },
  { key: 'drop_pincode', label: 'Delivery Pincode', category: 'customer', icon: '📮', description: 'Postal code for delivery location', defaultVisible: false, defaultOrder: 7 },
  { key: 'shipment_order_id', label: 'Shipment Order ID', category: 'order', icon: '🔢', description: 'Internal shipment order identifier', defaultVisible: false, defaultOrder: 8 },
  { key: 'attempt_count', label: 'Attempt Count', category: 'operational', icon: '#️⃣', description: 'Total number of carrier allocation attempts', defaultVisible: false, defaultOrder: 9 },
];

const CONFIRMATION_TABLE_COLUMNS = [
  { key: 'order_ref', label: 'Order Reference', category: 'order', icon: '📋', description: 'Order ID, created date, and order type', defaultVisible: true, defaultOrder: 0 },
  { key: 'pickup', label: 'Pickup', category: 'pickup', icon: '🏭', description: 'Pickup name and location', defaultVisible: true, defaultOrder: 1 },
  { key: 'delivery', label: 'Delivery', category: 'customer', icon: '📍', description: 'Recipient name and delivery location', defaultVisible: true, defaultOrder: 2 },
  { key: 'items', label: 'Items', category: 'product', icon: '🛍️', description: 'Item names and quantities', defaultVisible: true, defaultOrder: 3 },
  { key: 'amount', label: 'Amount', category: 'financial', icon: '💰', description: 'Total order amount', defaultVisible: true, defaultOrder: 4 },
  { key: 'status', label: 'Status', category: 'delivery', icon: '📊', description: 'Confirmation status and WhatsApp status', defaultVisible: true, defaultOrder: 5 },
  { key: 'order_type', label: 'Order Type', category: 'order', icon: '↔️', description: 'COD or Prepaid', defaultVisible: false, defaultOrder: 6 },
  { key: 'drop_phone', label: 'Customer Phone', category: 'customer', icon: '📞', description: "Recipient's contact number", defaultVisible: false, defaultOrder: 7 },
  { key: 'pickup_pincode', label: 'Pickup Pincode', category: 'pickup', icon: '📮', description: 'Pickup location postal code', defaultVisible: false, defaultOrder: 8 },
  { key: 'whatsapp_status', label: 'WhatsApp', category: 'operational', icon: '💬', description: 'Whether WhatsApp confirmation was sent', defaultVisible: false, defaultOrder: 9 },
];

function useColumnConfig(tableId, registry) {
  const storageKey = `wayuvega_col_${tableId}_v1`;
  const getDefaults = useCallback(() => {
    const ordered = [...registry].sort((a, b) => a.defaultOrder - b.defaultOrder);
    return {
      visibleKeys: ordered.filter(c => c.defaultVisible).map(c => c.key),
      columnOrder: ordered.map(c => c.key),
    };
  }, [registry]);
  const loadConfig = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return getDefaults();
      const saved = JSON.parse(raw);
      if (!saved || !Array.isArray(saved.visibleKeys) || !Array.isArray(saved.columnOrder)) return getDefaults();
      const registryKeys = new Set(registry.map(c => c.key));
      const savedOrderSet = new Set(saved.columnOrder);
      const savedVisible = saved.visibleKeys.filter(k => registryKeys.has(k));
      const newCols = registry.filter(c => !savedOrderSet.has(c.key)).sort((a, b) => a.defaultOrder - b.defaultOrder);
      // Splice unseen keys at their defaultOrder-relative slot (not the tail), and don't
      // auto-show route for users who already surface pickup/delivery themselves.
      const order = saved.columnOrder.filter(k => registryKeys.has(k));
      newCols.forEach(c => order.splice(Math.min(c.defaultOrder, order.length), 0, c.key));
      const newVisible = newCols
        .filter(c => c.defaultVisible)
        .filter(c => c.key !== 'route' || !(savedVisible.includes('pickup') || savedVisible.includes('delivery')))
        .map(c => c.key);
      return {
        visibleKeys: [...new Set([...savedVisible, ...newVisible])],
        columnOrder: [...new Set(order)],
      };
    } catch { return getDefaults(); }
  }, [storageKey, registry, getDefaults]);
  const [config, setConfigState] = useState(() => loadConfig());
  const updateConfig = useCallback((newConfig) => {
    localStorage.setItem(storageKey, JSON.stringify({ version: 1, ...newConfig }));
    setConfigState(newConfig);
  }, [storageKey]);
  const resetConfig = useCallback(() => {
    localStorage.removeItem(storageKey);
    setConfigState(getDefaults());
  }, [storageKey, getDefaults]);
  const orderedVisibleColumns = useMemo(() => {
    return config.columnOrder
      .filter(k => config.visibleKeys.includes(k))
      .map(k => registry.find(c => c.key === k))
      .filter(Boolean);
  }, [config, registry]);
  return { ...config, updateConfig, resetConfig, orderedVisibleColumns };
}

function ColumnCustomizerModal({ open, onClose, registry, categories, config, onSave, tableLabel }) {
  const [localVisible, setLocalVisible] = useState(config.visibleKeys);
  const [localOrder, setLocalOrder] = useState(config.columnOrder);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [draggedKey, setDraggedKey] = useState(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setCategoryFilter('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const filteredCols = registry.filter(col => {
    const matchSearch = !search ||
      col.label.toLowerCase().includes(search.toLowerCase()) ||
      (col.description || '').toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === 'all' || col.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const orderedFiltered = [
    ...localOrder.map(k => filteredCols.find(c => c.key === k)).filter(Boolean),
    ...filteredCols.filter(c => !localOrder.includes(c.key)),
  ];

  const uniqueCategories = ['all', ...Array.from(new Set(registry.map(c => c.category)))];

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-2 md:p-4 lg:p-6 overflow-hidden"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-none sm:rounded-2xl shadow-xl w-full h-full sm:h-auto sm:max-w-3xl sm:max-h-[90vh] overflow-hidden flex flex-col sm:ring-1 sm:ring-slate-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 md:px-6 py-4 flex items-center justify-between border-b border-slate-200/80">
          <div className="flex items-center gap-3">
            <span className="logo-shimmer inline-flex flex-shrink-0 rounded-md">
              <img
                src={(SOURCE_MAP[localStorage.getItem("source")] || SOURCE_MAP.wayuvega).logo}
                alt="Brand logo"
                className="h-8 w-auto rounded-md"
              />
            </span>
            <div>
            <h3 className="text-base font-semibold text-slate-900">Customize columns</h3>
            <p className="text-xs text-slate-500 mt-0.5">{tableLabel} · drag to reorder, check to show. Affects this table view only — CSV export lets you pick its columns separately.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 flex items-center justify-center transition-colors"
          >
            <CSVIcon.X className="h-4 w-4" />
          </button>
        </div>

        {/* Search + category filter */}
        <div className="px-4 md:px-6 py-3 border-b border-slate-200/80">
          <div className="flex items-center gap-2 mb-2.5">
            <div className="flex-1 relative">
              <CSVIcon.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search columns..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onBlur={e => setSearch(e.target.value.trim())}
                className="w-full h-9 pl-9 pr-4 rounded-lg border border-slate-200 focus:border-[#3877FC] focus:ring-2 focus:ring-[#3877FC]/25 outline-none text-sm text-slate-800 placeholder:text-slate-400 bg-white transition-colors"
              />
            </div>
            <button
              onClick={() => setLocalVisible(registry.map(c => c.key))}
              className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-slate-900 font-semibold text-xs transition-colors"
            >
              All
            </button>
            <button
              onClick={() => {
                setLocalVisible(registry.filter(c => c.defaultVisible).map(c => c.key));
                setLocalOrder(registry.map(c => c.key));
              }}
              className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-slate-900 font-semibold text-xs transition-colors"
            >
              Reset
            </button>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {uniqueCategories.map(cat => {
              const catMeta = categories ? categories.find(c => c.key === cat) : null;
              const label = catMeta ? catMeta.label : (cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1));
              const count = cat === 'all' ? registry.length : registry.filter(c => c.category === cat).length;
              const isActive = categoryFilter === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-2.5 py-1 rounded-lg font-medium text-xs transition-colors border flex items-center gap-1 ${isActive
                      ? 'bg-[#3877FC]/10 text-[#2A59BD] border-[#3877FC]/25'
                      : 'bg-white text-slate-600 border-slate-200 hover:text-slate-900'
                    }`}
                >
                  <span>{label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold tabular-nums ${isActive ? 'bg-[#3877FC] text-white' : 'bg-slate-100 text-slate-500'}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Column list */}
        <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-1.5 custom-scrollbar">
          {orderedFiltered.length === 0 ? (
            <div className="text-center py-12">
              <CSVIcon.Search className="h-8 w-8 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">No columns found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filter</p>
            </div>
          ) : orderedFiltered.map(col => {
            const isVisible = localVisible.includes(col.key);
            return (
              <div
                key={col.key}
                draggable
                onDragStart={e => { setDraggedKey(col.key); e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                onDrop={e => {
                  e.preventDefault();
                  if (draggedKey && draggedKey !== col.key) {
                    const newOrder = [...localOrder];
                    const fromIdx = newOrder.indexOf(draggedKey);
                    const toIdx = newOrder.indexOf(col.key);
                    if (fromIdx !== -1 && toIdx !== -1) {
                      newOrder.splice(fromIdx, 1);
                      newOrder.splice(toIdx, 0, draggedKey);
                      setLocalOrder(newOrder);
                    }
                  }
                  setDraggedKey(null);
                }}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors cursor-move ${isVisible
                    ? 'border-[#3877FC]/25 bg-[#3877FC]/[0.04]'
                    : 'border-slate-200 bg-white'
                  } ${draggedKey === col.key ? 'opacity-40' : ''}`}
              >
                <div className="flex-shrink-0 text-slate-300 group-hover:text-slate-500 transition-colors cursor-grab">
                  <CSVIcon.GripVertical className="h-4 w-4" />
                </div>
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={e => {
                    if (e.target.checked) {
                      setLocalVisible(prev => [...prev, col.key]);
                      if (!localOrder.includes(col.key)) setLocalOrder(prev => [...prev, col.key]);
                    } else {
                      setLocalVisible(prev => prev.filter(k => k !== col.key));
                    }
                  }}
                  className="h-4 w-4 accent-[#3877FC] cursor-pointer flex-shrink-0 rounded"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-sm ${isVisible ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>{col.label}</span>
                    {col.defaultVisible && (
                      <span className="text-[9px] px-1 py-px rounded uppercase tracking-wide font-semibold ring-1 ring-slate-200 text-slate-400">Default</span>
                    )}
                  </div>
                  {col.description && <p className="text-xs text-slate-500 leading-snug mt-0.5">{col.description}</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 md:px-6 py-3.5 border-t border-slate-200/80 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-500">
            <span className="font-semibold text-slate-800 tabular-nums">{localVisible.length}</span>
            <span className="mx-1">of</span>
            <span className="font-semibold text-slate-800 tabular-nums">{registry.length}</span>
            <span className="ml-1">columns visible</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="h-9 px-4 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-slate-900 font-semibold text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { onSave({ visibleKeys: localVisible, columnOrder: localOrder }); onClose(); }}
              disabled={localVisible.length === 0}
              className="h-9 px-4 rounded-lg bg-[#3877FC] hover:bg-[#2A59BD] text-white font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply changes
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ---------- The list's view (tab, page, sort, applied filters) lives in the URL ----------
   Opening a shipment and pressing Back must land on the same list the merchant left. React state dies
   with the page, so the applied view is mirrored into the query string (replace, never a new history
   entry) and read back on mount. Only values that differ from the defaults are written, so the plain
   /all-shipments URL stays plain. */
const LIST_FILTER_KEYS = [
  "orderReferenceNumber", "awbNumber", "paymentMode", "customerPhone", "customerEmail",
  "itemName", "sku", "courierPartnerName", "orderStatus", "orderType", "fromDate", "toDate",
];
const LIST_TAB_KEYS = new Set(TABS.map((t) => t.key));
const DEFAULT_SORT_FIELD = "shipment_order_date_time_utc";
const DEFAULT_LIMIT = 50;

function readListViewFromUrl(sp, { fallbackPage, defaultFilters }) {
  const tab = sp.get("tab");
  const page = Number(sp.get("page"));
  const limit = Number(sp.get("limit"));
  const filters = { ...defaultFilters };
  LIST_FILTER_KEYS.forEach((key) => {
    const value = sp.get(key);
    if (value) filters[key] = value;
  });
  return {
    selectedTab: tab && LIST_TAB_KEYS.has(tab) ? tab : "all",
    currentPage: Number.isInteger(page) && page > 0 ? page : fallbackPage,
    limit: Number.isInteger(limit) && limit > 0 && limit <= 500 ? limit : DEFAULT_LIMIT,
    sortField: sp.get("sort") || DEFAULT_SORT_FIELD,
    sortDirection: sp.get("dir") === "ASC" ? "ASC" : "DESC",
    useDateFilter: sp.get("dates") !== "off",
    filters,
  };
}

function writeListViewToUrl(view, defaultFilters) {
  const sp = new URLSearchParams();
  if (view.selectedTab !== "all") sp.set("tab", view.selectedTab);
  if (view.currentPage > 1) sp.set("page", String(view.currentPage));
  if (view.limit !== DEFAULT_LIMIT) sp.set("limit", String(view.limit));
  if (view.sortField !== DEFAULT_SORT_FIELD) sp.set("sort", view.sortField);
  if (view.sortDirection !== "DESC") sp.set("dir", view.sortDirection);
  if (!view.useDateFilter) sp.set("dates", "off");
  LIST_FILTER_KEYS.forEach((key) => {
    const value = view.filters[key];
    if (!value || value === defaultFilters[key]) return;
    sp.set(key, value);
  });
  return sp;
}

export default function AllShipments() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const defaultFrom = format(subMonths(new Date(), 1), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const DEFAULT_FILTERS = {
    orderReferenceNumber: "",
    awbNumber: "",
    paymentMode: "",
    customerPhone: "",
    customerEmail: "",
    itemName: "",
    sku: "",
    courierPartnerName: "",
    orderStatus: "",
    orderType: "",
    fromDate: defaultFrom,
    toDate: defaultTo,
  };
  // Read once, at mount: the URL (a Back navigation) wins over the legacy location.state page.
  const [initialView] = useState(() =>
    readListViewFromUrl(searchParams, { fallbackPage: location.state?.page ?? 1, defaultFilters: DEFAULT_FILTERS })
  );
  const initialPage = initialView.currentPage;
  const [useDateFilter, setUseDateFilter] = useState(initialView.useDateFilter);

  const [filters, setFilters] = useState(initialView.filters);

  const SPECIFIC_FIELDS = [
    "orderReferenceNumber",
    "awbNumber",
    "paymentMode",
    "customerPhone",
    "customerEmail",
    "itemName",
    "sku",
    "courierPartnerName",
  ];

  const hasSpecificFilters = (f = filters) =>
    Boolean(
      f.orderReferenceNumber ||
      f.awbNumber ||
      f.paymentMode ||
      f.customerPhone ||
      f.customerEmail ||
      f.itemName ||
      f.sku ||
      f.courierPartnerName ||
      f.orderStatus
    );

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialView.limit);
  const [sortField, setSortField] = useState(initialView.sortField);
  const [sortDirection, setSortDirection] = useState(initialView.sortDirection);

  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const [statusCounts, setStatusCounts] = useState(Object.fromEntries(TABS.map(t => [t.key, 0])));
  const [expandedRows, setExpandedRows] = useState({});

  const cachedUnprocessedDataRef = useRef(null);

  const [selectedTab, setSelectedTab] = useState(initialView.selectedTab);

  const [selectedRows, setSelectedRows] = useState(new Set());
  const headerCheckboxRef = useRef(null);
  const [loadingDownloadSelected, setLoadingDownloadSelected] = useState(false);

  const [showCSVColumnModal, setShowCSVColumnModal] = useState(false);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [csvColumnSearch, setCsvColumnSearch] = useState("");
  const [csvColumnCategory, setCsvColumnCategory] = useState("all");
  const [selectedCSVColumns, setSelectedCSVColumns] = useState([]);
  const [csvColumnOrder, setCsvColumnOrder] = useState([]);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [csvExportFormat, setCsvExportFormat] = useState("csv");
  const [showColCustomizer, setShowColCustomizer] = useState(false);

  const allCSVColumns = [
    { key: "order_reference_number", label: "Order Reference Number", category: "identification", description: "Unique order identifier", defaultSelected: true, icon: "🔢" },
    { key: "awb_number", label: "AWB Number", category: "identification", description: "Airway bill number for tracking", defaultSelected: true, icon: "📦" },
    { key: "marketplace_channel_order_number", label: "Marketplace Channel Order Number", category: "identification", description: "Marketplace/channel order number from search order API", defaultSelected: true, icon: "🛒" },
    { key: "merchant_id", label: "Merchant ID", category: "identification", description: "Merchant identifier", defaultSelected: false, icon: "🏪" },
    { key: "carrier", label: "Carrier", category: "shipping", description: "Shipping carrier name", defaultSelected: true, icon: "🚚" },
    { key: "order_type", label: "Order Type", category: "shipping", description: "Forward or reverse order", defaultSelected: true, icon: "↔️" },
    { key: "latest_order_status", label: "Order Status", category: "shipping", description: "Current shipment status", defaultSelected: true, icon: "📊" },
    { key: "order_placed_date_time", label: "Order Placed Date", category: "shipping", description: "When the order was placed", defaultSelected: true, icon: "📅" },
    { key: "order_label_url", label: "Label URL", category: "shipping", description: "Link to shipping label", defaultSelected: false, icon: "🏷️" },
    { key: "drop_name", label: "Recipient Name", category: "delivery", description: "Delivery recipient name", defaultSelected: true, icon: "👤" },
    { key: "drop_pincode", label: "Pincode", category: "delivery", description: "Delivery pincode", defaultSelected: true, icon: "📍" },
    { key: "drop_city", label: "City", category: "delivery", description: "Delivery city", defaultSelected: true, icon: "🏙️" },
    { key: "drop_state", label: "State", category: "delivery", description: "Delivery state", defaultSelected: true, icon: "🗺️" },
    { key: "drop_phone", label: "Customer Phone", category: "delivery", description: "Recipient contact number", defaultSelected: false, icon: "📞" },
    { key: "drop_address", label: "Delivery Address", category: "delivery", description: "Full delivery address", defaultSelected: false, icon: "🏠" },
    { key: "drop_organisation", label: "Delivery Organisation", category: "delivery", description: "Delivery organisation name", defaultSelected: false, icon: "🏢" },
    { key: "pickup_name", label: "Pickup Name", category: "pickup", description: "Pickup location name", defaultSelected: false, icon: "🏭" },
    { key: "pickup_organisation", label: "Pickup Organisation", category: "pickup", description: "Pickup organisation name", defaultSelected: false, icon: "🏗️" },
    { key: "pickup_address", label: "Pickup Address", category: "pickup", description: "Full pickup address", defaultSelected: false, icon: "📍" },
    { key: "pickup_city_csv", label: "Pickup City", category: "pickup", description: "Pickup city", defaultSelected: false, icon: "🏙️" },
    { key: "pickup_state", label: "Pickup State", category: "pickup", description: "Pickup state", defaultSelected: false, icon: "🗺️" },
    { key: "pickup_pincode", label: "Pickup Pincode", category: "pickup", description: "Pickup pincode", defaultSelected: false, icon: "📮" },
    { key: "pickup_phone", label: "Pickup Phone", category: "pickup", description: "Pickup contact number", defaultSelected: false, icon: "📱" },
    { key: "marketplace_channel_name", label: "Sales Channel", category: "identification", description: "Marketplace or sales channel (e.g. SHOPIFY)", defaultSelected: false, icon: "🛒" },
    { key: "shipment_order_type", label: "Shipment Type", category: "shipping", description: "FORWARD or RVP", defaultSelected: false, icon: "↔️" },
    { key: "charged_weight_category", label: "Charged Weight Category", category: "package", description: "DEAD_WEIGHT or VOLUMETRIC_WEIGHT", defaultSelected: false, icon: "⚖️" },
    { key: "payment_mode", label: "Payment Mode", category: "payment", description: "COD, Prepaid, etc.", defaultSelected: true, icon: "💳" },
    { key: "invoice_number", label: "Invoice Number", category: "payment", description: "Invoice reference", defaultSelected: false, icon: "🧾" },
    { key: "invoice_value", label: "Invoice Value", category: "payment", description: "Total invoice amount", defaultSelected: true, icon: "💰" },
    { key: "total_shipping_charge", label: "Shipping Charge", category: "payment", description: "Total shipping cost", defaultSelected: false, icon: "📦" },
    { key: "total_shipping_tax", label: "Shipping Tax", category: "payment", description: "Tax on shipping", defaultSelected: false, icon: "📋" },
    { key: "dead_weight_in_grams", label: "Dead Weight (g)", category: "package", description: "Physical weight in grams", defaultSelected: false, icon: "⚖️" },
    { key: "volumetric_weight_in_grams", label: "Volumetric Weight (g)", category: "package", description: "Calculated volumetric weight", defaultSelected: false, icon: "📏" },
    { key: "item_sku", label: "Item SKU", category: "items", description: "Product SKU codes (semicolon-separated for multiple items)", defaultSelected: true, icon: "🏷️" },
    { key: "item_name", label: "Item Name", category: "items", description: "Product names (semicolon-separated for multiple items)", defaultSelected: true, icon: "📦" },
    { key: "item_quantity", label: "Item Quantity", category: "items", description: "Quantity per item (semicolon-separated, e.g. 1;4;8)", defaultSelected: true, icon: "🔢" },
    { key: "item_unit_price", label: "Unit Price", category: "items", description: "Unit price per item (semicolon-separated)", defaultSelected: true, icon: "💵" },
    { key: "total_quantity", label: "Total Quantity", category: "items", description: "Sum of all item quantities", defaultSelected: true, icon: "📊" },
  ];

  const csvColumnCategories = [
    { key: "all", label: "All Columns", icon: "📋", color: "slate" },
    { key: "identification", label: "Order Identification", icon: "🔢", color: "indigo" },
    { key: "shipping", label: "Shipping Details", icon: "🚚", color: "blue" },
    { key: "delivery", label: "Delivery Address", icon: "📍", color: "emerald" },
    { key: "payment", label: "Payment Information", icon: "💳", color: "amber" },
    { key: "package", label: "Package Details", icon: "📦", color: "purple" },
    { key: "items", label: "Item Details", icon: "🛍️", color: "rose" },
  ];

  const mainColConfig = useColumnConfig('main', MAIN_TABLE_COLUMNS);

  const unprocessedColConfig = useColumnConfig('unprocessed', UNPROCESSED_TABLE_COLUMNS);
  const confirmationColConfig = useColumnConfig('confirmation', CONFIRMATION_TABLE_COLUMNS);

  useEffect(() => {
    if (selectedCSVColumns.length === 0) {
      const defaultColumns = allCSVColumns
        .filter(col => col.defaultSelected)
        .map(col => col.key);
      setSelectedCSVColumns(defaultColumns);
      setCsvColumnOrder(defaultColumns);
    }
  }, []);

  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimShipment, setClaimShipment] = useState(null);
  const [claimDefaultItem, setClaimDefaultItem] = useState(null);

  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportOrderContext, setSupportOrderContext] = useState(null);
  const { getMerchantId } = useAuth();


  const [showFilters, setShowFilters] = useState(false);

  const [viewMode] = useState('compact');

  // Actions-column chrome (sticky + seam + opaque bg) is only justified when the table
  // actually overflows horizontally; in the default no-scroll view it dissolves into the row.
  const mainScrollRef = useRef(null);
  const [hasHScroll, setHasHScroll] = useState(false);
  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return undefined;
    const measure = () => setHasHScroll(el.scrollWidth > el.clientWidth + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mainColConfig.orderedVisibleColumns, viewMode, shipments.length]);

  const [openActionMenuRow, setOpenActionMenuRow] = useState(null);

  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });

  const [analyticsDateRange, setAnalyticsDateRange] = useState('last30days');

  const [unprocessedFilters, setUnprocessedFilters] = useState({
    orderReferenceNumber: "",
    pickupPincode: "",
    dropPincode: "",
    allocationStatus: "false",
    fromDate: defaultFrom,
    toDate: defaultTo,
  });
  const [expandedAllocLogs, setExpandedAllocLogs] = useState({});

  const [confirmationFilters, setConfirmationFilters] = useState({
    orderReferenceNumber: "",
    customerPhone: "",
    confirmationStatus: "",
    fromDate: defaultFrom,
    toDate: defaultTo,
  });

  const openClaimModalFor = (shipment) => {
    const items = (shipment?.shipment_packages ?? []).flatMap(p => p.items ?? []);
    const firstItem = items.length ? items[0] : null;
    setClaimShipment(shipment);
    setClaimDefaultItem(firstItem);
    setClaimModalOpen(true);
  };


  // Carrier EDD per order ref - fetched lazily on first expand (single-order tracking history).
  const [eddByRef, setEddByRef] = useState({});
  const fetchRowEdd = useCallback(async (ref) => {
    if (!ref) return;
    setEddByRef((prev) => (ref in prev ? prev : { ...prev, [ref]: undefined }));
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      if (!merchantId || !authToken) return;
      const res = await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/order/tracking/history`,
        { order_reference_number: ref },
        { headers: { "x-merchant-id": merchantId, Authorization: authToken, "Content-Type": "application/json" } }
      );
      if (res.data?.expected_delivery_date || res.data?.delivery_delta_days != null) {
        setEddByRef((prev) => ({
          ...prev,
          [ref]: {
            date: res.data.expected_delivery_date || null,
            source: res.data.edd_source || "CARRIER",
            kind: res.data.edd_kind || "DELIVERY",
            deltaDays: res.data.delivery_delta_days ?? null,
            server: true,
          },
        }));
        return;
      }
      const entries = res.data?.order_processing_business_status_codes || [];
      let best = null;
      let bestMs = -Infinity;
      entries.forEach((e) => {
        if (!e?.carrier_partner_edd) return;
        const ms = new Date(e.order_status_time || e.created_date_time || 0).getTime() || 0;
        if (ms >= bestMs) { bestMs = ms; best = e.carrier_partner_edd; }
      });
      setEddByRef((prev) => ({ ...prev, [ref]: best ? { date: best, source: "CARRIER", kind: "DELIVERY", deltaDays: null, server: false } : null }));
    } catch {
      setEddByRef((prev) => ({ ...prev, [ref]: null }));
    }
  }, []);

  // Whole row opens the shipment detail page - EXCEPT real interactive elements
  // (buttons/links/inputs/role=button), the marked no-navigation zones (checkbox +
  // actions cells, AWB live-tracking), and never mid text-selection (copy flows).
  const handleRowNavigate = (e, s) => {
    const ref = s?.order_reference_number;
    if (!ref) return;
    if (e.target.closest("button, a, input, select, textarea, [role='button'], [data-row-noclick]")) return;
    if (window.getSelection()?.toString()) return;
    const url = `/shipment-details/${encodeURIComponent(ref)}?page=${currentPage}`;
    if (e.metaKey || e.ctrlKey) window.open(url, "_blank");
    else navigate(url);
  };

  const toggleRow = (idx) => {
    setExpandedRows(prev => {
      const opening = !prev[idx];
      if (opening) {
        const s = shipments[idx];
        const ref = s?.order_reference_number;
        if (ref && !(ref in eddByRef)) {
          if (s?.expected_delivery_date || s?.delivery_delta_days != null) {
            // Search rows already carry the ladder output - seed the cache, skip the fetch.
            setEddByRef((p) => (ref in p ? p : {
              ...p,
              [ref]: {
                date: s.expected_delivery_date || null,
                source: s.edd_source || "CARRIER",
                kind: s.edd_kind || "DELIVERY",
                deltaDays: s.delivery_delta_days ?? null,
                server: true,
              },
            }));
          } else {
            fetchRowEdd(ref);
          }
        }
      }
      return { ...prev, [idx]: !prev[idx] };
    });
  };

  useEffect(() => {
    if (!hasSpecificFilters() && !useDateFilter) {
      setUseDateFilter(true);
      setFilters((prev) => ({
        ...prev,
        fromDate: defaultFrom,
        toDate: defaultTo,
      }));
    }
  }, [
    filters.orderReferenceNumber,
    filters.awbNumber,
    filters.paymentMode,
    filters.customerPhone,
    filters.customerEmail,
    filters.itemName,
    filters.sku,
    filters.courierPartnerName,
    filters.orderStatus,
    useDateFilter,
  ]);

  // When the list was last fetched successfully — the merchant can see how fresh it is, and the
  // caption re-renders on a 30 s tick (a timer, never a request).
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNowTick(Date.now()), 30000);
    return () => window.clearInterval(t);
  }, []);
  const updatedAgo = (() => {
    if (!lastFetchedAt) return null;
    const s = Math.max(0, Math.round((nowTick - lastFetchedAt) / 1000));
    if (s < 60) return "just now";
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min ago`;
    return `${Math.round(m / 60)} h ago`;
  })();

  const fetchData = useCallback(
    async (tabKey, overrides = {}) => {
      setLoading(true);
      setError(null);
      setShipments([]);
      setTotalRecords(0);
      try {

        const merchantId = localStorage.getItem("merchantId");
        const authToken = localStorage.getItem("authToken");
        if (!authToken) return;

        if (tabKey === "unprocessed") {
          const uf = overrides._unprocessedFilters || unprocessedFilters;
          const fromUTC = uf.fromDate
            ? formatISO(toDate(parseISO(`${uf.fromDate}T00:00:00+05:30`)))
            : null;
          const toUTC = uf.toDate
            ? formatISO(toDate(parseISO(`${uf.toDate}T23:59:59+05:30`)))
            : null;

          const body = {
            merchant_id: merchantId,
            ...(uf.orderReferenceNumber && { order_reference_number: uf.orderReferenceNumber }),
            ...(uf.pickupPincode && { pickup_pincode: uf.pickupPincode }),
            ...(uf.dropPincode && { drop_pincode: uf.dropPincode }),
            is_successful_allocation: uf.allocationStatus !== "" ? uf.allocationStatus === "true" : false,
            ...(fromUTC && { from_date: fromUTC }),
            ...(toUTC && { to_date: toUTC }),
            page: overrides.page || currentPage,
            limit: overrides.limit || limit,
            sort_field: overrides.sort_field || "updated_date_time",
            sort_direction: overrides.sort_direction || sortDirection,
          };

          const res = await axios.post(
            API.searchUnprocessedOrders(),
            body,
            { headers: { Authorization: authToken, "Content-Type": "application/json" } }
          );

          const data = res.data;
          const list = Array.isArray(data.orders) ? data.orders : [];
          setShipments(list);
          setTotalRecords(data.total_elements ?? 0);
          setLastFetchedAt(Date.now());
          return list;
        }

        if (tabKey === "confirmation") {
          const cf = overrides._confirmationFilters || confirmationFilters;
          const fromUTC = cf.fromDate
            ? formatISO(toDate(parseISO(`${cf.fromDate}T00:00:00+05:30`)))
            : null;
          const toUTC = cf.toDate
            ? formatISO(toDate(parseISO(`${cf.toDate}T23:59:59+05:30`)))
            : null;

          const body = {
            merchant_id: merchantId,
            ...(cf.orderReferenceNumber && { order_reference_number: cf.orderReferenceNumber }),
            ...(cf.customerPhone && { customer_phone: cf.customerPhone }),
            ...(cf.confirmationStatus && { confirmation_status: [cf.confirmationStatus] }),
            ...(fromUTC && { from_order_creation_datetime_utc: fromUTC }),
            ...(toUTC && { to_order_creation_datetime_utc: toUTC }),
            limit: overrides.limit || limit,
            skip: overrides.skip ?? (currentPage - 1) * limit,
            sort_direction: sortDirection,
          };

          const res = await axios.post(
            API.searchConfirmationOrders(),
            body,
            { headers: { Authorization: authToken, "Content-Type": "application/json" } }
          );

          const data = res.data;
          const list = Array.isArray(data.orders) ? data.orders : [];
          setShipments(list);
          setTotalRecords(data.orders_count ?? 0);
          setLastFetchedAt(Date.now());
          return list;
        }

        const fromUTC = useDateFilter
          ? formatISO(toDate(parseISO(`${filters.fromDate}T00:00:00+05:30`)))
          : null;
        const toUTC = useDateFilter
          ? formatISO(toDate(parseISO(`${filters.toDate}T23:59:59+05:30`)))
          : null;

        // An explicit ORDER STATUS pick always beats the tab's family - on the RTO tab,
        // "RTO Delivered" must narrow to that one status, not drown in the whole family.
        let statusPayload = STATUS_MAP[tabKey]?.length ? STATUS_MAP[tabKey] : null;
        if (filters.orderStatus) statusPayload = [filters.orderStatus];

        const shipmentOrderType = tabKey === "rvp"
          ? "REVERSE"
          : (filters.orderType || null);

        const body = {
          merchant_id: merchantId,
          order_reference_number: filters.orderReferenceNumber || null,
          awb_number: filters.awbNumber || null,
          payment_mode: filters.paymentMode || null,
          customer_phone: filters.customerPhone || null,
          customer_email: filters.customerEmail || null,
          item_name: filters.itemName || null,
          sku: filters.sku || null,
          courier_partner_name: filters.courierPartnerName || null,
          order_status: statusPayload,
          ...(useDateFilter && fromUTC && {
            from_shipment_order_creation_datetime_utc: fromUTC
          }),
          ...(useDateFilter && toUTC && {
            to_shipment_order_creation_datetime_utc: toUTC
          }),
          limit,
          skip: (currentPage - 1) * limit,
          sort_field: sortField,
          sort_direction: sortDirection,
          ...overrides,
          ...(tabKey === "rvp" && { shipment_order_type: "REVERSE" }),
          ...(tabKey !== "rvp" && !("shipment_order_type" in overrides) && shipmentOrderType && { shipment_order_type: shipmentOrderType }),
        };

        const res = await axios.post(
          `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_SEARCH_ORDERS}`,
          body,
          { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
        );

        const data = res.data;
        const list = Array.isArray(data.orders) ? data.orders : [];
        setShipments(list);
        setTotalRecords(data.totalCount ?? 0);
        setLastFetchedAt(Date.now());
        return list;
      } catch (err) {
        const errMsg = tabKey === "unprocessed" ? "Failed to fetch unprocessed orders."
          : tabKey === "confirmation" ? "Failed to fetch confirmation orders."
            : "Failed to fetch shipments.";
        setError(errMsg);
        toast.error(errMsg);
      } finally {
        setLoading(false);
      }
    },
    [filters, currentPage, limit, sortField, sortDirection, useDateFilter, unprocessedFilters, confirmationFilters]
  );

  // Manual refresh: the applied view on the same page — one list query, no tab counts (that
  // aggregate is the heavier query), and a 1-minute cooldown so the button can't hammer the
  // database. A bar under the button drains with the cooldown so the merchant can see when the
  // next refresh opens; the 1 s tick behind it runs only while cooling and never makes a request.
  const REFRESH_COOLDOWN_MS = 60 * 1000;
  const [refreshAvailableAt, setRefreshAvailableAt] = useState(0);
  const [cooldownNow, setCooldownNow] = useState(() => Date.now());
  useEffect(() => {
    if (!refreshAvailableAt) return undefined;
    let t = null;
    const tick = () => {
      const now = Date.now();
      setCooldownNow(now);
      if (now >= refreshAvailableAt && t) window.clearInterval(t);
    };
    t = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(t);
  }, [refreshAvailableAt]);
  const cooldownLeftMs = Math.max(0, refreshAvailableAt - cooldownNow);
  const refreshCooling = cooldownLeftMs > 0;
  const cooldownLeftS = Math.ceil(cooldownLeftMs / 1000);
  const cooldownLeftLabel = `${Math.floor(cooldownLeftS / 60)}:${String(cooldownLeftS % 60).padStart(2, "0")}`;
  // What the refresh changed on this page — a diff of the rows already on screen against the new
  // response (reference, status, last status time), so the merchant gets an answer instead of a
  // silent swap: "No changes" or "3 updated · 2 new", with the changed rows washed for a moment.
  // Page-level only (these rows), zero extra requests.
  const [refreshSummary, setRefreshSummary] = useState(null);
  const [changedRefs, setChangedRefs] = useState(() => new Set());
  const refreshSummaryTimer = useRef(null);
  const rowSignature = (s) => `${s.latest_order_status_code ?? ""}|${s.latest_status_timestamp ?? ""}|${s.awb_number ?? ""}`;
  const handleRefresh = useCallback(async () => {
    if (loading || refreshCooling) return;
    setRefreshAvailableAt(Date.now() + REFRESH_COOLDOWN_MS);
    const before = new Map(shipments.map((s) => [s.order_reference_number, rowSignature(s)]));
    const next = await fetchData(selectedTab, selectedTab === "unprocessed" ? { page: currentPage } : { skip: (currentPage - 1) * limit });
    if (!Array.isArray(next)) return; // the fetch failed; the toast already said so
    const changed = new Set();
    let updated = 0;
    let added = 0;
    next.forEach((s) => {
      const ref = s.order_reference_number;
      if (!before.has(ref)) { added += 1; changed.add(ref); }
      else if (before.get(ref) !== rowSignature(s)) { updated += 1; changed.add(ref); }
    });
    setRefreshSummary({ updated, added });
    setChangedRefs(changed);
    if (refreshSummaryTimer.current) window.clearTimeout(refreshSummaryTimer.current);
    refreshSummaryTimer.current = window.setTimeout(() => { setRefreshSummary(null); setChangedRefs(new Set()); }, 5000);
  }, [loading, refreshCooling, fetchData, selectedTab, currentPage, limit, REFRESH_COOLDOWN_MS, shipments]);
  useEffect(() => () => { if (refreshSummaryTimer.current) window.clearTimeout(refreshSummaryTimer.current); }, []);
  const refreshSummaryLabel = refreshSummary
    ? (refreshSummary.updated === 0 && refreshSummary.added === 0
      ? "No changes"
      : [refreshSummary.updated ? `${refreshSummary.updated} updated` : null, refreshSummary.added ? `${refreshSummary.added} new` : null].filter(Boolean).join(" · "))
    : null;

  const fetchStatusCounts = useCallback(async () => {
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      if (!authToken || !merchantId) return;

      const today = new Date();
      let from, to;

      if (analyticsDateRange === 'thisMonth') {
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = today;
      } else {
        from = new Date(today);
        from.setDate(today.getDate() - 30);
        to = today;
      }

      const fromUTC = formatISO(toDate(parseISO(`${from.toLocaleDateString("en-CA")}T00:00:00+05:30`)));
      const toUTC = formatISO(toDate(parseISO(`${to.toLocaleDateString("en-CA")}T23:59:59+05:30`)));

      const body = {
        merchant_id: merchantId,
        from_date_time: fromUTC,
        to_date_time: toUTC,
        include_no_status: false,
      };

      const res = await axios.post(
        API.orderStatusAnalytics(),
        body,
        { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
      );

      const statusCountsData = res.data?.status_counts || [];
      const summary = res.data?.summary || {};

      const counts = {
        all: summary.total_orders || 0,
        failed: 0,
        unprocessed: 0,
        pickupPending: 0,
        inTransit: 0,
        delivered: 0,
        rto: 0,
        outForDelivery: 0,
        lostDamaged: 0,
        rvp: 0,
        confirmation: 0,
      };

      statusCountsData.forEach(item => {
        const code = String(item.status_code);
        const count = item.count || 0;

        if (STATUS_MAP.failed.includes(code)) {
          counts.failed += count;
        }
        if (STATUS_MAP.pickupPending.includes(code)) {
          counts.pickupPending += count;
        }
        if (STATUS_MAP.inTransit.includes(code)) {
          counts.inTransit += count;
        }
        if (STATUS_MAP.delivered.includes(code)) {
          counts.delivered += count;
        }
        if (STATUS_MAP.rto.includes(code)) {
          counts.rto += count;
        }
        if (STATUS_MAP.outForDelivery.includes(code)) {
          counts.outForDelivery += count;
        }
        if (STATUS_MAP.lostDamaged.includes(code)) {
          counts.lostDamaged += count;
        }
        if (code === OrderBusinessStatusEnum.ORDER_CONFIRMATION_AWAITED.code) {
          counts.confirmation += count;
        }
      });

      try {
        const rvpBody = {
          merchant_id: merchantId,
          shipment_order_type: "REVERSE",
          from_shipment_order_creation_datetime_utc: fromUTC,
          to_shipment_order_creation_datetime_utc: toUTC,
          limit: 1,
          skip: 0,
        };

        const rvpRes = await axios.post(
          `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_SEARCH_ORDERS}`,
          rvpBody,
          { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
        );

        counts.rvp = rvpRes.data?.totalCount || 0;
      } catch (rvpErr) {
      }

      try {
        const unprocessedBody = {
          merchant_id: merchantId,
          is_successful_allocation: false,
          from_date: fromUTC,
          to_date: toUTC,
          page: 1,
          limit: limit, // fetch full first page so we can cache it
          sort_field: "updated_date_time",
          sort_direction: "DESC",
        };

        const unprocessedRes = await axios.post(
          API.searchUnprocessedOrders(),
          unprocessedBody,
          { headers: { Authorization: authToken, "Content-Type": "application/json" } }
        );

        counts.unprocessed = unprocessedRes.data?.total_elements || 0;

        cachedUnprocessedDataRef.current = {
          orders: Array.isArray(unprocessedRes.data?.orders) ? unprocessedRes.data.orders : [],
          total_elements: unprocessedRes.data?.total_elements || 0,
          timestamp: Date.now(),
        };
      } catch (unprocessedErr) {
      }

      setStatusCounts(counts);
    } catch (err) {
    }
  }, [analyticsDateRange]);

  useEffect(() => {
    // The tab and page the merchant left (from the URL), not always "all" / page 1.
    fetchData(
      initialView.selectedTab,
      initialView.selectedTab === "unprocessed" ? { page: initialPage } : { skip: (initialPage - 1) * initialView.limit }
    );
    fetchStatusCounts();
  }, []);

  // Mirror the APPLIED view into the URL each time a fetch starts (replace — no history entry per click).
  // Keyed on the fetch, not on the inputs, so filter text that was typed but never applied is not mirrored.
  useEffect(() => {
    if (!loading) return;
    setSearchParams(
      writeListViewToUrl({ selectedTab, currentPage, limit, sortField, sortDirection, useDateFilter, filters }, DEFAULT_FILTERS),
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    fetchStatusCounts();
  }, [analyticsDateRange, fetchStatusCounts]);

  const handleTabClick = async (key) => {
    setSelectedTab(key);
    setCurrentPage(1);
    setExpandedRows({});
    setExpandedAllocLogs({});
    setSelectedRows(new Set());

    if (key === "unprocessed") {
      const freshFilters = {
        orderReferenceNumber: "",
        pickupPincode: "",
        dropPincode: "",
        allocationStatus: "false",
        fromDate: defaultFrom,
        toDate: defaultTo,
      };
      setUnprocessedFilters(freshFilters);

      const cache = cachedUnprocessedDataRef.current;
      if (cache && (Date.now() - cache.timestamp < 60000)) {
        setShipments(cache.orders);
        setTotalRecords(cache.total_elements);
        setLoading(false);
        cachedUnprocessedDataRef.current = null;
        return;
      }

      await fetchData(key, { page: 1, _unprocessedFilters: freshFilters });
      return;
    }

    if (key === "confirmation") {
      const freshFilters = {
        orderReferenceNumber: "",
        customerPhone: "",
        confirmationStatus: "",
        fromDate: defaultFrom,
        toDate: defaultTo,
      };
      setConfirmationFilters(freshFilters);
      await fetchData(key, { skip: 0, _confirmationFilters: freshFilters });
      return;
    }

    setFilters((prev) => {
      const base = {
        ...prev,
        orderReferenceNumber: "",
        awbNumber: "",
        paymentMode: "",
        customerPhone: "",
        customerEmail: "",
        itemName: "",
        sku: "",
        courierPartnerName: "",
        fromDate: defaultFrom,
        toDate: defaultTo,
        orderType: key === "rvp" ? "REVERSE" : "",
      };

      return key !== "all"
        ? { ...base, orderStatus: "" }
        : base;
    });

    setUseDateFilter(true);

    const overrides = {
      skip: 0,
      order_reference_number: null,
      awb_number: null,
      payment_mode: null,
      customer_phone: null,
      item_name: null,
      sku: null,
      courier_partner_name: null,
      // Explicit family override: setFilters above is async, so without this a stale
      // filters.orderStatus from the previous tab would narrow this tab's first fetch.
      order_status: STATUS_MAP[key]?.length ? STATUS_MAP[key] : null,
      ...(key !== "rvp" ? { shipment_order_type: null } : {}),
    };

    await fetchData(key, overrides);
  };


  const handleFilterClick = async () => {
    setCurrentPage(1);
    // Collapse on apply - the chips row below the toolbar becomes the applied-state
    // view (each chip removable, Clear all beside them), so the bulky form gets out
    // of the way of the results it just filtered.
    setShowFilters(false);
    await fetchData(selectedTab, { skip: 0 });
    toast.success("Filters applied successfully");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (["fromDate", "toDate"].includes(name)) {
      const from = new Date(name === "fromDate" ? value : filters.fromDate);
      const to = new Date(name === "toDate" ? value : filters.toDate);
      if ((to - from) / (1000 * 60 * 60 * 24) > 30) {
        toast.error("Date range cannot exceed 1 month");
        return;
      }
    }
    if (SPECIFIC_FIELDS.includes(name) && value) {
      setUseDateFilter(false);
    }
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({
      orderReferenceNumber: "",
      awbNumber: "",
      paymentMode: "",
      customerPhone: "",
      customerEmail: "",
      itemName: "",
      sku: "",
      courierPartnerName: "",
      orderStatus: "",
      orderType: selectedTab === "rvp" ? "REVERSE" : "",
      fromDate: defaultFrom,
      toDate: defaultTo,
    });
    setUseDateFilter(true);
    setCurrentPage(1);
    toast.info("Filters reset");
  };

  // ---- Active filter chips (main tabs) ----
  // fetchData reads `filters` from its closure, so every chip removal ALSO passes an explicit
  // request-body override — the refetch never depends on state having flushed.
  const CHIP_OVERRIDES = {
    orderReferenceNumber: { order_reference_number: null },
    awbNumber: { awb_number: null },
    paymentMode: { payment_mode: null },
    customerPhone: { customer_phone: null },
    customerEmail: { customer_email: null },
    itemName: { item_name: null },
    sku: { sku: null },
    courierPartnerName: { courier_partner_name: null },
  };

  const activeFilterChips = useMemo(() => {
    const chips = [];
    const add = (key, label, value) => chips.push({ key, label, value });
    if (filters.orderReferenceNumber) add("orderReferenceNumber", "Ref", filters.orderReferenceNumber);
    if (filters.awbNumber) add("awbNumber", "AWB", filters.awbNumber);
    if (filters.paymentMode) add("paymentMode", "Payment", filters.paymentMode === "PREPAID" ? "Prepaid" : filters.paymentMode);
    if (filters.customerPhone) add("customerPhone", "Phone", filters.customerPhone);
    if (filters.customerEmail) add("customerEmail", "Email", filters.customerEmail);
    if (filters.itemName) add("itemName", "Item", filters.itemName);
    if (filters.sku) add("sku", "SKU", filters.sku);
    if (filters.courierPartnerName)
      add("courierPartnerName", "Courier", COURIER_OPTIONS.find((c) => c.value === filters.courierPartnerName)?.label || filters.courierPartnerName);
    if (filters.orderStatus)
      add("orderStatus", "Status", Object.values(OrderBusinessStatusEnum).find((st) => st.code === filters.orderStatus)?.name || filters.orderStatus);
    if (filters.orderType && selectedTab !== "rvp")
      add("orderType", "Type", filters.orderType === "FORWARD" ? "Forward" : "Reverse");
    if (useDateFilter && (filters.fromDate !== defaultFrom || filters.toDate !== defaultTo))
      add("dateRange", "Date", `${format(parseISO(filters.fromDate), "dd MMM")} - ${format(parseISO(filters.toDate), "dd MMM")}`);
    return chips;
  }, [filters, useDateFilter, selectedTab, defaultFrom, defaultTo]);

  const removeFilterChip = (key) => {
    setCurrentPage(1);
    if (key === "dateRange") {
      // Back to the default window, not date-off - "remove my custom range".
      setFilters((prev) => ({ ...prev, fromDate: defaultFrom, toDate: defaultTo }));
      fetchData(selectedTab, {
        skip: 0,
        from_shipment_order_creation_datetime_utc: formatISO(toDate(parseISO(`${defaultFrom}T00:00:00+05:30`))),
        to_shipment_order_creation_datetime_utc: formatISO(toDate(parseISO(`${defaultTo}T23:59:59+05:30`))),
      });
      return;
    }
    if (key === "orderStatus") {
      setFilters((prev) => ({ ...prev, orderStatus: "" }));
      fetchData(selectedTab, { skip: 0, order_status: STATUS_MAP[selectedTab]?.length ? STATUS_MAP[selectedTab] : null });
      return;
    }
    if (key === "orderType") {
      setFilters((prev) => ({ ...prev, orderType: "" }));
      fetchData(selectedTab, { skip: 0, shipment_order_type: null });
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: "" }));
    fetchData(selectedTab, { skip: 0, ...(CHIP_OVERRIDES[key] || {}) });
  };

  // One-click date presets: set the draft AND apply immediately (explicit overrides again,
  // so the stale fetchData closure is irrelevant).
  const DATE_PRESETS = [
    { label: "Today", days: 1 },
    { label: "7D", days: 7 },
    { label: "30D", days: 30 },
  ];

  const presetRange = (days) => ({
    from: format(subDays(new Date(), days - 1), "yyyy-MM-dd"),
    to: format(new Date(), "yyyy-MM-dd"),
  });

  const isPresetActive = (days) => {
    const { from, to } = presetRange(days);
    return useDateFilter && filters.fromDate === from && filters.toDate === to;
  };

  const applyDatePreset = (days) => {
    const { from, to } = presetRange(days);
    setUseDateFilter(true);
    setFilters((prev) => ({ ...prev, fromDate: from, toDate: to }));
    setCurrentPage(1);
    fetchData(selectedTab, {
      skip: 0,
      from_shipment_order_creation_datetime_utc: formatISO(toDate(parseISO(`${from}T00:00:00+05:30`))),
      to_shipment_order_creation_datetime_utc: formatISO(toDate(parseISO(`${to}T23:59:59+05:30`))),
    });
  };

  const clearAllFilterChips = () => {
    resetFilters();
    fetchData(selectedTab, {
      skip: 0,
      order_reference_number: null,
      awb_number: null,
      payment_mode: null,
      customer_phone: null,
      customer_email: null,
      item_name: null,
      sku: null,
      courier_partner_name: null,
      order_status: STATUS_MAP[selectedTab]?.length ? STATUS_MAP[selectedTab] : null,
      shipment_order_type: selectedTab === "rvp" ? "REVERSE" : null,
      from_shipment_order_creation_datetime_utc: formatISO(toDate(parseISO(`${defaultFrom}T00:00:00+05:30`))),
      to_shipment_order_creation_datetime_utc: formatISO(toDate(parseISO(`${defaultTo}T23:59:59+05:30`))),
    });
  };

  const handleUnprocessedInputChange = (e) => {
    const { name, value } = e.target;
    setUnprocessedFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleUnprocessedFilterClick = async () => {
    setCurrentPage(1);
    await fetchData("unprocessed", { page: 1 });
    toast.success("Filters applied successfully");
  };

  const resetUnprocessedFilters = () => {
    const freshFilters = {
      orderReferenceNumber: "",
      pickupPincode: "",
      dropPincode: "",
      allocationStatus: "false",
      fromDate: defaultFrom,
      toDate: defaultTo,
    };
    setUnprocessedFilters(freshFilters);
    setCurrentPage(1);
    fetchData("unprocessed", { page: 1, _unprocessedFilters: freshFilters });
    toast.info("Filters reset");
  };

  const handleConfirmationInputChange = (e) => {
    const { name, value } = e.target;
    setConfirmationFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmationFilterClick = async () => {
    setCurrentPage(1);
    await fetchData("confirmation", { skip: 0 });
    toast.success("Filters applied successfully");
  };

  const resetConfirmationFilters = () => {
    const freshFilters = {
      orderReferenceNumber: "",
      customerPhone: "",
      confirmationStatus: "",
      fromDate: defaultFrom,
      toDate: defaultTo,
    };
    setConfirmationFilters(freshFilters);
    setCurrentPage(1);
    fetchData("confirmation", { skip: 0, _confirmationFilters: freshFilters });
    toast.info("Filters reset");
  };

  const handleConvertToDraft = async (order) => {
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      if (!authToken) { navigate("/login"); return; }

      const res = await axios.post(
        API.draftOrderFromOrder(order.shipment_order_id),
        {},
        { headers: { Authorization: authToken, "Content-Type": "application/json", merchantId } }
      );

      if (res.data?.metadata?.success) {
        toast.success("Order converted to draft successfully!");
        navigate("/draft-orders");
      } else {
        toast.error(res.data?.metadata?.message || "Failed to convert to draft");
      }
    } catch (err) {
      toast.error(err?.response?.data?.metadata?.message || "Failed to convert to draft");
    }
  };

  const [retryingOrderIds, setRetryingOrderIds] = useState(new Set());
  const handleRetryOrder = async (order) => {
    const orderRef = order.order_reference_number;
    if (!orderRef) { toast.error("No order reference number found"); return; }
    setRetryingOrderIds(prev => new Set(prev).add(orderRef));
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      if (!authToken) { navigate("/login"); return; }

      const res = await axios.post(
        API.searchOrders(),
        { merchant_id: merchantId, order_reference_number: orderRef, limit: 1, skip: 0 },
        { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
      );
      const fetched = res.data?.orders?.[0];
      if (!fetched) { toast.error("Order not found in search results"); return; }

      if (fetched.order_reference_number) {
        fetched.order_reference_number = fetched.order_reference_number + "_retry";
      }

      const orderType = (fetched.shipment_order_type || "").toUpperCase();
      const isReverse = orderType === "REVERSE" || orderType === "RVP";
      navigate(isReverse ? "/create-reverse-order" : "/create-forward-order", { state: { cloneFrom: fetched } });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to fetch order for retry");
    } finally {
      setRetryingOrderIds(prev => { const n = new Set(prev); n.delete(orderRef); return n; });
    }
  };

  const handleSort = (field) => {
    const newDirection =
      sortField === field && sortDirection === "ASC" ? "DESC" : "ASC";
    setSortField(field);
    setSortDirection(newDirection);
    setCurrentPage(1);
    fetchData(selectedTab, { sort_field: field, sort_direction: newDirection, skip: 0 });
  };

  const nextPage = () => {
    if (shipments.length === limit) {
      const next = currentPage + 1;
      setCurrentPage(next);
      if (selectedTab === "unprocessed") {
        fetchData(selectedTab, { page: next });
      } else {
        fetchData(selectedTab, { skip: (next - 1) * limit });
      }
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      const prev = currentPage - 1;
      setCurrentPage(prev);
      if (selectedTab === "unprocessed") {
        fetchData(selectedTab, { page: prev });
      } else {
        fetchData(selectedTab, { skip: (prev - 1) * limit });
      }
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = +e.target.value;
    setLimit(newLimit);
    setCurrentPage(1);
    fetchData(selectedTab, { limit: newLimit, skip: 0 });
  };

  const downloadLabel = (url) => {
    if (!url) {
      toast.error("No label available for this order");
      return;
    }
    window.open(url, "_blank");
    toast.success("Label downloaded successfully");
  };

  const handleCancelOrder = async (waybill) => {

    if (!waybill) {
      toast.error("No AWB to cancel");
      return;
    }

    const authToken = localStorage.getItem("authToken");
    if (!authToken) return navigate("/login");

    try {
      await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CANCEL_ORDER_URL}`,
        { waybill },
        { headers: { Authorization: authToken, "Content-Type": "application/json" } }
      );

      dispatch(fetchWalletBalance());
      new BroadcastChannel('wallet').postMessage('refresh-balance');
      await fetchData(selectedTab, { skip: (currentPage - 1) * limit });
      toast.success("Order cancelled successfully");
    } catch (err) {
      toast.error("Failed to cancel order");
    }
  };

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const awbMouseDownRef = useRef({ x: 0, y: 0, time: 0 });

  const handleAwbMouseDown = (e) => {
    awbMouseDownRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
  };

  const handleAwbMouseUp = (e, awb) => {
    const { x, y, time } = awbMouseDownRef.current;
    const dx = Math.abs(e.clientX - x);
    const dy = Math.abs(e.clientY - y);
    const elapsed = Date.now() - time;
    const hasTextSelection = window.getSelection()?.toString().length > 0;
    if (dx < 5 && dy < 5 && elapsed < 300 && !hasTextSelection) {
      const url = `https://tracking.wayuvega.in/track?awb=${encodeURIComponent(awb)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const rowKey = (s) => (s.awb_number ? String(s.awb_number) : `REF:${String(s.order_reference_number)}`);
  const isSelectedOnPage = (s) => selectedRows.has(rowKey(s));
  const pageSelectedCount = shipments.filter(isSelectedOnPage).length;
  const allOnPageSelected = shipments.length > 0 && shipments.every(isSelectedOnPage);
  const showMobileActions = pageSelectedCount > 0 || loadingDownloadSelected;

  const toggleSelectOne = (key) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAllOnPage = (checked) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (checked) shipments.forEach(s => next.add(rowKey(s)));
      else shipments.forEach(s => next.delete(rowKey(s)));
      return next;
    });
  };

  const clearSelection = () => setSelectedRows(new Set());

  useEffect(() => {
    const cb = headerCheckboxRef.current;
    if (!cb) return;
    cb.indeterminate = pageSelectedCount > 0 && !allOnPageSelected;
  }, [pageSelectedCount, allOnPageSelected, shipments]);

  const downloadShipmentLabelsServer = async (orderRefs) => {
    const authToken = localStorage.getItem('authToken');
    const merchantId = localStorage.getItem('merchantId');
    const OMS_BASE = process.env.REACT_APP_OMS_BASE_URL;
    if (!authToken || !merchantId || !OMS_BASE) {
      throw new Error("Missing config/env for bulk label API");
    }
    const apiUrl = `${OMS_BASE}/v2/oms/shipment-label/bulk`;
    const payload = {
      merchant_id: merchantId,
      order_reference_numbers: orderRefs,
    };
    const { data } = await axios.post(apiUrl, payload, {
      headers: { Authorization: authToken, "Content-Type": "application/json" },
    });

    const mergedUrl = data?.merged_shipment_label_presigned_url;
    const summary = data?.summary || {};
    const successCount = Number(summary.success_count) || 0;
    const failedCount = Number(summary.failed_count) || 0;
    const totalCount = Number(summary.total) || orderRefs.length;

    if (!mergedUrl) {
      const failedRefs = Array.isArray(data?.results)
        ? data.results.filter(r => r && r.success === false).map(r => r.order_reference_number)
        : [];
      const detail = failedRefs.length > 0 ? ` Failed: ${failedRefs.join(", ")}` : "";
      throw new Error(`No merged shipment label URL returned by server.${detail}`);
    }

    openInNewTab(mergedUrl);

    return { mergedUrl, summary: { total: totalCount, success_count: successCount, failed_count: failedCount } };
  };

  const handleDownloadSelectedLabels = async () => {
    const selectedOnPage = shipments.filter(isSelectedOnPage);
    const orderRefs = selectedOnPage.map(s => s.order_reference_number).filter(Boolean);

    if (orderRefs.length === 0) {
      toast.error("No order references in selected rows");
      return;
    }

    setLoadingDownloadSelected(true);
    try {
      const result = await downloadShipmentLabelsServer(orderRefs);
      const { success_count: successCount = orderRefs.length, failed_count: failedCount = 0 } = result?.summary || {};
      if (failedCount > 0) {
        toast.info(
          `${successCount} label(s) downloaded, ${failedCount} failed`);
      } else {
        toast.success(`${successCount} label(s) downloaded successfully`);
      }
    } catch (err) {

      const withLabels = selectedOnPage.filter(s => !!s.order_label_url);
      if (withLabels.length === 0) {
        toast.error("Selected rows have no label URLs yet");
      } else {
        withLabels.forEach((s, idx) => {
          const a = document.createElement("a");
          a.href = s.order_label_url;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.download = `${s.awb_number || s.order_reference_number || "label"}.pdf`;
          document.body.appendChild(a);
          setTimeout(() => {
            a.click();
            document.body.removeChild(a);
          }, idx * 200);
        });
        const skipped = selectedOnPage.length - withLabels.length;
        setTimeout(() => {
          toast.success(
            skipped > 0
              ? `${withLabels.length} labels downloaded, ${skipped} skipped`
              : `${withLabels.length} labels downloaded`,
            skipped > 0 ? "info" : "success"
          );
        }, withLabels.length * 200 + 100);
      }
    } finally {
      setLoadingDownloadSelected(false);
    }
  };

  const handleOpenCSVModal = () => {
    const selectedOnPage = shipments.filter(isSelectedOnPage);
    if (selectedOnPage.length === 0) {
      toast.error("No rows selected to export");
      return;
    }
    setShowCSVColumnModal(true);
  };

  const handleDownloadSelectedCSV = () => {
    const selectedOnPage = shipments.filter(isSelectedOnPage);
    if (selectedOnPage.length === 0) {
      toast.error("No rows selected to export");
      return;
    }

    const columnsToExport = csvColumnOrder.length > 0
      ? csvColumnOrder.filter(key => selectedCSVColumns.includes(key))
      : selectedCSVColumns.length > 0
        ? selectedCSVColumns
        : allCSVColumns.filter(col => col.defaultSelected).map(col => col.key);

    // Get column definitions in the correct order
    const orderedColumns = columnsToExport
      .map(key => allCSVColumns.find(col => col.key === key))
      .filter(Boolean);

    const headers = orderedColumns.map(col => col.key);

    const getItemVal = (item, path) => (item && safe(item, path, "")) ?? "";

    const getColumnValue = (shipment, items, columnKey) => {
      const itemVals = (items ?? []).length > 0 ? items : [];
      switch (columnKey) {
        case "merchant_id": return safe(shipment, "merchant_id", "");
        case "order_reference_number": return safe(shipment, "order_reference_number", "");
        case "awb_number": return safe(shipment, "awb_number", "");
        case "marketplace_channel_order_number": return safe(shipment, "marketplace_channel_order_number", "");
        case "carrier": return safe(shipment, "allocated_carrier_partner_public_name", "");
        case "order_type": return safe(shipment, "order_type", "");
        case "payment_mode": return safe(shipment, "payment_details.payment_mode", "") || safe(shipment, "order_type", "");
        case "drop_name": return safe(shipment, "drop_name", "");
        case "drop_pincode": return safe(shipment, "drop_pincode", "");
        case "drop_city": return safe(shipment, "drop_city", "");
        case "drop_state": return safe(shipment, "drop_state", "");
        case "drop_phone": return safe(shipment, "drop_poc_contact_number", "");
        case "drop_address": return safe(shipment, "drop_address", "");
        case "drop_organisation": return safe(shipment, "drop_organisation", "");
        case "pickup_name": return safe(shipment, "pickup_name", "");
        case "pickup_organisation": return safe(shipment, "pickup_organisation", "");
        case "pickup_address": return safe(shipment, "pickup_address", "");
        case "pickup_city_csv": return safe(shipment, "pickup_city", "");
        case "pickup_state": return safe(shipment, "pickup_state", "");
        case "pickup_pincode": return safe(shipment, "pickup_pincode", "");
        case "pickup_phone": return safe(shipment, "pickup_poc_contact_number", "");
        case "marketplace_channel_name": return safe(shipment, "marketplace_channel_name", "");
        case "shipment_order_type": return safe(shipment, "shipment_order_type", "");
        case "charged_weight_category": return safe(shipment, "charged_weight_category", "");
        case "invoice_number": return safe(shipment, "invoice_number", "");
        case "invoice_value": return safe(shipment, "payment_details.total_amount", "") || "";
        case "total_shipping_charge": return safe(shipment, "total_shipping_charge", "");
        case "total_shipping_tax": return safe(shipment, "total_shipping_tax", "");
        case "dead_weight_in_grams": return safe(shipment, "dead_weight_in_grams", "");
        case "volumetric_weight_in_grams": return safe(shipment, "volumetric_weight_in_grams", "");
        case "latest_order_status": return safe(shipment, "latest_order_status", "") || safe(shipment, "latest_order_status_code", "");
        case "order_placed_date_time": return safe(shipment, "order_placed_date_time", "");
        case "order_label_url": return safe(shipment, "order_label_url", "");
        case "item_sku": return itemVals.map(it => getItemVal(it, "item_general_details.sku")).join(";");
        case "item_name": return itemVals.map(it => getItemVal(it, "item_general_details.item_name")).join(";");
        case "item_quantity": return itemVals.map(it => getItemVal(it, "quantity")).join(";");
        case "item_unit_price": return itemVals.map(it => getItemVal(it, "item_payment_details.unit_price")).join(";");
        case "total_quantity": return itemVals.length === 0 ? "" : String(itemVals.reduce((sum, it) => sum + (Number(getItemVal(it, "quantity")) || 0), 0));
        default: return "";
      }
    };

    const rows = [];
    selectedOnPage.forEach((s) => {
      const items = (s.shipment_packages ?? []).flatMap(pkg => pkg.items ?? []);
      const row = headers.map(key => getColumnValue(s, items, key));
      rows.push(row);
    });

    const escape = (v) => {
      if (v === undefined || v === null) return '""';
      const str = String(v);
      const escaped = str.replace(/"/g, '""');
      return `"${escaped}"`;
    };

    const csvContent = [
      headers.map(escape).join(","),
      ...rows.map(r => r.map(escape).join(","))
    ].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_${timestamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`CSV exported with ${selectedOnPage.length} order(s)`);
  };

  const safe = (obj, path, fallback = "") => {
    try {
      return path.split('.').reduce((a, b) => (a && a[b] !== undefined ? a[b] : undefined), obj) ?? fallback;
    } catch {
      return fallback;
    }
  };

  const handleBypassConfirmation = async (shipment) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      toast.error("Session expired. Please log in again");
      navigate("/login");
      return;
    }
    try {
      const res = await axios.post(
        API.bypassOrderConfirmation(),
        { order_reference_number: shipment.order_reference_number },
        { headers: { Authorization: authToken, "Content-Type": "application/json" } }
      );
      if (res.data?.metadata?.success) {
        toast.success(res.data.metadata.message || "Order placed successfully");
        fetchData(selectedTab, { skip: (currentPage - 1) * limit });
        fetchStatusCounts();
      } else {
        toast.error(res.data?.metadata?.message || "Failed to place order");
      }
    } catch (err) {
      const msg = err?.response?.data?.metadata?.message || err?.response?.data?.message || "Failed to place order";
      toast.error(msg);
    }
  };

  const handleActionClick = (action, shipment, page) => {
    switch (action) {
      case "place_order":
        setConfirmModal({
          open: true,
          title: "Place Order",
          message: `Are you sure you want to place order ${shipment.order_reference_number}? This will bypass the confirmation step and initiate shipment processing.`,
          onConfirm: () => handleBypassConfirmation(shipment),
        });
        break;
      case "details":
        const authToken = localStorage.getItem("authToken");
        const merchantId = localStorage.getItem("merchantId");
        if (!authToken || !merchantId) {
          toast.error("Session expired. Please log in again");
          navigate("/login");
          return;
        }
        const ref = encodeURIComponent(shipment.order_reference_number);
        const url = `${window.location.origin}/shipment-details/${ref}?page=${page}`;
        window.open(url, "_blank", "noopener,noreferrer");
        break;
      case "label":
        downloadLabel(shipment.order_label_url);
        break;
      case "invoice": {
        const orderRef = shipment.order_reference_number;
        if (!orderRef) {
          toast.error("No order reference for this shipment");
          break;
        }
        const authToken = localStorage.getItem("authToken");
        const merchantId = getMerchantId() || localStorage.getItem("merchantId");
        if (!authToken || !merchantId) {
          toast.error("Session expired. Please log in again");
          navigate("/login");
          break;
        }
        (async () => {
          try {
            await downloadShipmentInvoice({
              merchantId,
              orderReferenceNumber: orderRef,
              authToken,
            });
            toast.success("Invoice opened in a new tab");
          } catch (err) {
            toast.error(err?.message || "Failed to download invoice");
          }
        })();
        break;
      }
      case "claim":
        openClaimModalFor(shipment);
        break;
      case "clone": {
        const orderType = (shipment.shipment_order_type || "").toUpperCase();
        const isReverse = orderType === "REVERSE" || orderType === "RVP";
        navigate(isReverse ? "/create-reverse-order" : "/create-forward-order", { state: { cloneFrom: shipment } });
        break;
      }
      case "cancel":
        setConfirmModal({
          open: true,
          title: "Cancel Order",
          message: `Are you sure you want to cancel order with AWB ${shipment.awb_number}? This action cannot be undone.`,
          onConfirm: () => handleCancelOrder(shipment.awb_number)
        });
        break;
      case "support":
        handleOpenSupport(shipment);
        break;
      case "download_qc_image":
      case "download_epod_image":
      case "download_sorter_image": {
        const awb = shipment.awb_number;
        if (!awb) {
          toast.error("AWB number not available for this shipment");
          break;
        }
        const slugMap = {
          download_qc_image: "qc",
          download_epod_image: "epod",
          download_sorter_image: "sorter",
        };
        const labelMap = {
          download_qc_image: "QC image",
          download_epod_image: "Proof of delivery",
          download_sorter_image: "Sorter image",
        };
        const slug = slugMap[action];
        const friendlyLabel = labelMap[action];
        const authToken = localStorage.getItem("authToken");
        const merchantId = getMerchantId() || localStorage.getItem("merchantId");
        if (!authToken || !merchantId) {
          toast.error("Session expired. Please log in again");
          navigate("/login");
          break;
        }
        (async () => {
          try {
            const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/carrier-images/${slug}?awbNumber=${encodeURIComponent(awb)}`;
            const response = await axios.get(apiUrl, {
              headers: {
                "x-merchant-id": merchantId,
                Authorization: authToken,
                "Content-Type": "application/json",
              },
            });
            const presigned =
              response?.data?.presignedUrlForImageAccess ||
              response?.data?.presigned_url_for_image_access;
            const suggestedName =
              response?.data?.suggestedDownloadFileName ||
              response?.data?.suggested_download_file_name;
            const contentType =
              response?.data?.storedImageContentType ||
              response?.data?.stored_image_content_type ||
              "image/jpeg";
            if (!presigned) {
              toast.error(`${friendlyLabel} not yet received from carrier`);
              return;
            }
            const ext = (contentType.split("/")[1] || "jpg").toLowerCase();
            const fallbackName = suggestedName || `${awb}-${slug}.${ext}`;
            await downloadRemoteFile(presigned, fallbackName);
            toast.success(`${friendlyLabel} downloaded`);
          } catch (err) {
            if (err && err.name === "DownloadBlockedError") {
              toast.success(
                `${friendlyLabel} could not be downloaded directly. Please contact support.`,
                "error",
              );
              return;
            }
            const status = err?.response?.status;
            const data = err?.response?.data || {};
            const code = data.errorCode || data.code;
            const message = data.errorMessage || data.message || err?.message;
            const isNotFound =
              status === 404 ||
              code === "carrier_image_not_found_for_awb" ||
              code === "CARRIER_IMAGE_NOT_FOUND_FOR_AWB" ||
              code === "OMS_CARRIER_IMAGE_NOT_FOUND";
            const friendly = isNotFound
              ? `${friendlyLabel} not yet received from carrier`
              : message || `Could not download ${friendlyLabel}. Please try again.`;
            toast.error(friendly);
          }
        })();
        break;
      }
      default:
        break;
    }
  };

  const handleOpenSupport = async (shipment) => {
    const merchantId = getMerchantId();
    if (!merchantId) {
      toast.error("Please log in to create a support ticket");
      return;
    }

    const orderRef = shipment.order_reference_number;
    if (!orderRef) {
      toast.error("Order reference number not found");
      return;
    }

    try {
      const existingTicket = await ticketService.getTicketByOrder(merchantId, orderRef);
      navigate(`/support/tickets/${existingTicket.public_id}`);
    } catch (err) {
      if (err?.response?.status === 404) {
        setSupportOrderContext({
          orderReferenceNumber: orderRef,
          awbNumber: shipment.awb_number,
        });
        setSupportModalOpen(true);
      } else {
        toast.error(err?.response?.data?.message || "Failed to check for existing ticket");
      }
    }
  };

  const handleRowClick = (shipment, e) => {
    if (
      e.target.closest('input[type="checkbox"]') ||
      e.target.closest('button') ||
      e.target.closest('a') ||
      window.getSelection()?.toString()
    ) {
      return;
    }
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!authToken || !merchantId) {
      toast.error("Session expired. Please log in again");
      navigate("/login");
      return;
    }
    const ref = encodeURIComponent(shipment.order_reference_number);
    const url = `${window.location.origin}/shipment-details/${ref}?page=${currentPage}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.orderReferenceNumber) count++;
    if (filters.awbNumber) count++;
    if (filters.paymentMode) count++;
    if (filters.customerPhone) count++;
    if (filters.customerEmail) count++;
    if (filters.itemName) count++;
    if (filters.sku) count++;
    if (filters.courierPartnerName) count++;
    if (filters.orderStatus) count++;
    return count;
  }, [filters]);

  return (
    <div className={`relative min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50 ${showMobileActions ? 'pb-24 md:pb-8' : 'pb-8'} overflow-x-hidden`}>

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
      />

      <div className="w-full max-w-full px-2 sm:px-3 lg:px-2 py-2 overflow-x-hidden">
        <style>{`
          select.select-caret {
            appearance: none;
            -webkit-appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 0.6rem center;
            background-size: 1rem 1rem;
            padding-right: 2.1rem;
          }
          /* Date-range trigger (MUI Paper) matches the h-9 hairline field recipe */
          .date-input-wrapper .MuiPaper-root {
            min-height: 2.25rem !important;
            height: 2.25rem;
            padding: 0 0.75rem !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 0.5rem !important;
            box-shadow: none !important;
            justify-content: space-between;
            transition: border-color 150ms;
          }
          .date-input-wrapper .MuiPaper-root {
            cursor: pointer;
          }
          .date-input-wrapper .MuiPaper-root .MuiTypography-root {
            font-size: 0.875rem !important;
            font-weight: 400 !important;
            font-family: "Inter", sans-serif !important;
            color: #1e293b !important;
            letter-spacing: 0 !important;
          }
          .date-input-wrapper .MuiPaper-root .MuiSvgIcon-root {
            font-size: 1rem !important;
            color: #94a3b8 !important;
            margin-left: 0.5rem !important;
          }
        `}</style>
        <div className="bg-white rounded-xl shadow-sm ring-1 ring-slate-200/80 overflow-hidden w-full max-w-full">

          <div className="sticky top-0 z-20 bg-slate-50/95 border-b border-slate-200/80">
            <div className="md:hidden p-1">
              <div className="grid grid-cols-2 gap-1">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabClick(tab.key)}
                    className={
                      `relative flex flex-col items-center justify-center gap-1 px-2 py-1.5 rounded-lg font-medium transition-colors duration-150 ` +
                      (selectedTab === tab.key
                        ? "bg-white shadow-sm text-slate-900 ring-1 ring-slate-200/80"
                        : "text-slate-500 active:text-slate-800")
                    }
                  >
                    <div className="flex items-center gap-1">
                      <tab.Icon
                        className={
                          `w-3.5 h-3.5 transition-colors flex-shrink-0 ` +
                          (selectedTab === tab.key || (statusCounts[tab.key] ?? 0) > 0 ? tab.color : "text-slate-400")
                        }
                      />
                      <span className="text-[10px] font-semibold truncate max-w-[90px]">{tab.label}</span>
                    </div>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center tabular-nums transition-colors ${tabBadgeClass(tab, statusCounts[tab.key] ?? 0, selectedTab === tab.key)}`}>
                      {statusCounts[tab.key] ?? 0}
                    </span>
                    {selectedTab === tab.key && (
                      <div className={`absolute inset-x-3 bottom-0 h-[2.5px] rounded-t-full ${tab.accent}`}></div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="hidden md:block relative">
              {/* All tabs share the row equally - no horizontal overflow, ever. */}
              <div>
                <div className="flex w-full p-1 gap-0.5">
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => handleTabClick(tab.key)}
                      title={tab.label}
                      className={
                        `group relative flex flex-[1_0_auto] items-center justify-center gap-1 px-2 py-1.5 rounded-lg font-medium transition-colors duration-150 whitespace-nowrap ` +
                        (selectedTab === tab.key
                          ? "bg-white shadow-sm text-slate-900 ring-1 ring-slate-200/80"
                          : "text-slate-500 hover:text-slate-900")
                      }
                    >
                      <tab.Icon
                        className={
                          `hidden xl:block w-3.5 h-3.5 transition-colors flex-shrink-0 ` +
                          (selectedTab === tab.key || (statusCounts[tab.key] ?? 0) > 0
                            ? tab.color
                            : "text-slate-400 group-hover:text-slate-600")
                        }
                      />
                      <span className="text-xs font-semibold">{tab.label}</span>
                      <span className={`flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center tabular-nums transition-colors ${tabBadgeClass(tab, statusCounts[tab.key] ?? 0, selectedTab === tab.key)}`}>
                        {statusCounts[tab.key] ?? 0}
                      </span>
                      {selectedTab === tab.key && (
                        <div className={`absolute inset-x-2.5 bottom-0 h-[2.5px] rounded-t-full ${tab.accent}`}></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="top-[73px] z-20 border-b border-gray-300 bg-white">
            <div className="p-1.5 md:p-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1.5">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`w-full sm:w-auto flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border text-xs font-semibold transition-colors ${showFilters
                  ? 'border-slate-300 bg-slate-100 text-slate-900'
                  : 'border-slate-200 bg-white text-slate-600 hover:text-slate-900'}`}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-[#3877FC] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
                <svg className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${showFilters ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              <div className="w-full sm:w-auto flex items-center gap-1">
                <div className="w-36" title="Select date range for tab badge counts">
                  <SelectMenu
                    value={analyticsDateRange}
                    onChange={setAnalyticsDateRange}
                    buttonClassName="h-8 text-xs"
                    options={[
                      { value: "thisMonth", label: "This Month" },
                      { value: "last30days", label: "Last 30 Days" },
                    ]}
                  />
                </div>

                <button
                  onClick={handleOpenCSVModal}
                  disabled={pageSelectedCount === 0}
                  className={`h-8 px-2.5 rounded-lg border text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${pageSelectedCount === 0
                    ? 'border-slate-200 bg-white text-slate-300 cursor-not-allowed'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  title={pageSelectedCount === 0 ? "Select at least one row" : "Export selected orders as CSV"}
                >
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="hidden lg:inline">CSV</span>
                  {pageSelectedCount > 0 && (
                    <span className="bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pageSelectedCount}</span>
                  )}
                </button>

                <button
                  onClick={handleDownloadSelectedLabels}
                  disabled={pageSelectedCount === 0 || loadingDownloadSelected}
                  className={`h-8 px-2.5 rounded-lg border text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${pageSelectedCount === 0 || loadingDownloadSelected
                    ? 'border-slate-200 bg-white text-slate-300 cursor-not-allowed'
                    : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                    }`}
                  aria-busy={loadingDownloadSelected}
                  title={pageSelectedCount === 0 ? "Select at least one row" : "Download labels"}
                >
                  {loadingDownloadSelected ? (
                    <>
                      <svg className="animate-spin h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      <span className="hidden lg:inline text-xs">Processing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      <span className="hidden lg:inline">Labels</span>
                      {pageSelectedCount > 0 && (
                        <span className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pageSelectedCount}</span>
                      )}
                    </>
                  )}
                </button>

                <button
                  onClick={() => setShowColCustomizer(true)}
                  className="flex-1 sm:flex-none h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-slate-900 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                  title="Customize visible columns"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                  <span className="hidden sm:inline text-xs">Columns</span>
                </button>

                {/* Refresh after Columns, its status caption trailing it: the applied view again, same
                    page, one list query, a 1-minute cooldown drained by the bar under the button */}
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={loading || refreshCooling}
                  title={refreshCooling ? `Refresh again in ${cooldownLeftLabel}` : "Reload this list with the current filters"}
                  aria-label="Refresh shipments"
                  className={`relative overflow-hidden flex-1 sm:flex-none h-8 px-2.5 rounded-lg border text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${loading || refreshCooling
                    ? 'border-slate-200 bg-white text-slate-400 cursor-not-allowed'
                    : 'border-slate-200 bg-white text-slate-600 hover:text-slate-900'}`}
                >
                  <RefreshCw className={`h-3.5 w-3.5 flex-shrink-0 ${loading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline text-xs">Refresh</span>
                  {refreshCooling && (
                    // drains right-to-left with the cooldown; empty = refresh is open again
                    <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[3px] bg-slate-100">
                      <span
                        className="block h-full bg-[#3877FC]/60 transition-[width] duration-1000 ease-linear"
                        style={{ width: `${Math.min(100, (cooldownLeftMs / REFRESH_COOLDOWN_MS) * 100)}%` }}
                      />
                    </span>
                  )}
                </button>
                {refreshSummaryLabel ? (
                  <span className={`hidden lg:inline whitespace-nowrap pl-1 text-[11px] font-semibold tabular-nums ${refreshSummary.updated || refreshSummary.added ? "text-emerald-700" : "text-slate-500"}`} role="status">
                    {refreshSummaryLabel}
                  </span>
                ) : updatedAgo && (
                  <span className="hidden lg:inline whitespace-nowrap pl-1 text-[11px] font-medium text-slate-400 tabular-nums" title={lastFetchedAt ? `Last fetched ${new Date(lastFetchedAt).toLocaleString()}` : undefined}>
                    Updated {updatedAgo}
                  </span>
                )}
              </div>
            </div>

            {selectedTab !== "unprocessed" && selectedTab !== "confirmation" && activeFilterChips.length > 0 && (
              <div className="px-3 pb-2 -mt-0.5 flex flex-wrap items-center gap-1.5">
                {activeFilterChips.map((chip) => (
                  <span
                    key={chip.key}
                    className="inline-flex items-center gap-1 max-w-[16rem] pl-2.5 pr-1 py-1 rounded-full bg-[#3877FC]/[0.08] ring-1 ring-[#3877FC]/20 text-xs text-[#2A59BD]"
                  >
                    <span className="font-semibold flex-shrink-0">{chip.label}:</span>
                    <span className="truncate">{chip.value}</span>
                    <button
                      type="button"
                      onClick={() => removeFilterChip(chip.key)}
                      className="p-0.5 rounded-full text-[#2A59BD]/60 hover:text-rose-600 transition-colors flex-shrink-0"
                      aria-label={`Remove ${chip.label} filter`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={clearAllFilterChips}
                  className="text-xs font-medium text-slate-400 hover:text-rose-600 transition-colors ml-1"
                >
                  Clear all
                </button>
              </div>
            )}

            {showFilters && selectedTab === "unprocessed" && (
              <form
                className="px-4 py-4 bg-rose-50/40 border-t border-rose-200/60 animate-slide-down"
                onSubmit={(e) => { e.preventDefault(); handleUnprocessedFilterClick(); }}
              >
                <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                  <div className="group w-full lg:flex-1 lg:min-w-[160px]">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span>Order Reference Number</span>
                    </label>
                    <input
                      type="text"
                      name="orderReferenceNumber"
                      value={unprocessedFilters.orderReferenceNumber}
                      onChange={handleUnprocessedInputChange}
                      onBlur={(e) => setUnprocessedFilters((prev) => ({ ...prev, orderReferenceNumber: e.target.value.trim() }))}
                      placeholder="Enter order reference"
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all bg-white shadow-sm"
                    />
                  </div>

                  <div className="group w-full lg:w-[130px] lg:flex-shrink-0">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span>Pickup Pincode</span>
                    </label>
                    <input
                      type="text"
                      name="pickupPincode"
                      value={unprocessedFilters.pickupPincode}
                      onChange={handleUnprocessedInputChange}
                      onBlur={(e) => setUnprocessedFilters((prev) => ({ ...prev, pickupPincode: e.target.value.trim() }))}
                      placeholder="e.g. 560001"
                      maxLength={6}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all bg-white shadow-sm"
                    />
                  </div>

                  {/* Drop Pincode */}
                  <div className="group w-full lg:w-[130px] lg:flex-shrink-0">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span>Delivery Pincode</span>
                    </label>
                    <input
                      type="text"
                      name="dropPincode"
                      value={unprocessedFilters.dropPincode}
                      onChange={handleUnprocessedInputChange}
                      onBlur={(e) => setUnprocessedFilters((prev) => ({ ...prev, dropPincode: e.target.value.trim() }))}
                      placeholder="e.g. 400001"
                      maxLength={6}
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all bg-white shadow-sm"
                    />
                  </div>

                  {/* Date Range */}
                  <div className="w-full lg:w-auto lg:min-w-[220px] lg:flex-shrink-0">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span>Date Range</span>
                    </label>
                    <div className="date-input-wrapper w-full [&_.MuiPaper-root]:!p-1 [&_.MuiPaper-root]:!pl-2.5 [&_.MuiPaper-root]:!min-h-0 [&_.MuiPaper-root]:!h-[30px] [&_.MuiTypography-root]:!text-xs [&_.MuiSvgIcon-root]:!text-[16px]">
                      <CustomDateRangePicker
                        selectedRange={[parseISO(unprocessedFilters.fromDate), parseISO(unprocessedFilters.toDate)]}
                        onRangeChange={([from, to]) => {
                          const diffDays = Math.abs((to - from) / (1000 * 60 * 60 * 24));
                          if (diffDays > 30) {
                            toast.error("Date range cannot exceed 1 month");
                            return;
                          }
                          setUnprocessedFilters(prev => ({
                            ...prev,
                            fromDate: from.toLocaleDateString("en-CA"),
                            toDate: to.toLocaleDateString("en-CA")
                          }));
                        }}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      type="submit"
                      className="flex-1 lg:flex-none px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg shadow-sm transition-colors font-semibold flex items-center justify-center gap-1.5 text-xs whitespace-nowrap"
                    >
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>Apply Filters</span>
                    </button>
                    <button
                      type="button"
                      onClick={resetUnprocessedFilters}
                      className="flex-1 lg:flex-none px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg hover:text-slate-900 transition-colors font-semibold flex items-center justify-center gap-1.5 text-xs whitespace-nowrap"
                    >
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Reset All</span>
                    </button>
                  </div>
                </div>
              </form>
            )}

            {showFilters && selectedTab === "confirmation" && (
              <form
                className="px-4 py-4 bg-cyan-50/40 border-t border-cyan-200/60 animate-slide-down"
                onSubmit={(e) => { e.preventDefault(); handleConfirmationFilterClick(); }}
              >
                <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                  <div className="group w-full lg:flex-1 lg:min-w-[160px]">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span>Order Reference</span>
                    </label>
                    <input type="text" name="orderReferenceNumber" value={confirmationFilters.orderReferenceNumber} onChange={handleConfirmationInputChange} onBlur={(e) => setConfirmationFilters((prev) => ({ ...prev, orderReferenceNumber: e.target.value.trim() }))} placeholder="Enter order reference" className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white shadow-sm" />
                  </div>

                  <div className="group w-full lg:w-[150px] lg:flex-shrink-0">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span>Customer Phone</span>
                    </label>
                    <input type="text" name="customerPhone" value={confirmationFilters.customerPhone} onChange={handleConfirmationInputChange} onBlur={(e) => setConfirmationFilters((prev) => ({ ...prev, customerPhone: e.target.value.trim() }))} placeholder="e.g. 9876543210" maxLength={10} className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white shadow-sm" />
                  </div>

                  <div className="group w-full lg:w-[150px] lg:flex-shrink-0">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span>Status</span>
                    </label>
                    <SelectMenu
                      value={confirmationFilters.confirmationStatus}
                      onChange={(v) => setConfirmationFilters((prev) => ({ ...prev, confirmationStatus: v }))}
                      options={[
                        { value: "", label: "All Statuses" },
                        { value: "PENDING", label: "Pending" },
                        { value: "CONFIRMED", label: "Confirmed" },
                        { value: "BYPASSED", label: "Bypassed" },
                        { value: "CANCELLED", label: "Cancelled" },
                        { value: "EXPIRED", label: "Expired" },
                      ]}
                    />
                  </div>

                  <div className="w-full lg:w-auto lg:min-w-[220px] lg:flex-shrink-0">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span>Date Range</span>
                    </label>
                    <div className="date-input-wrapper w-full [&_.MuiPaper-root]:!p-1 [&_.MuiPaper-root]:!pl-2.5 [&_.MuiPaper-root]:!min-h-0 [&_.MuiPaper-root]:!h-[30px] [&_.MuiTypography-root]:!text-xs [&_.MuiSvgIcon-root]:!text-[16px]">
                      <CustomDateRangePicker
                        selectedRange={[parseISO(confirmationFilters.fromDate), parseISO(confirmationFilters.toDate)]}
                        onRangeChange={([from, to]) => {
                          const diffDays = Math.abs((to - from) / (1000 * 60 * 60 * 24));
                          if (diffDays > 30) {
                            toast.error("Date range cannot exceed 1 month");
                            return;
                          }
                          setConfirmationFilters(prev => ({
                            ...prev,
                            fromDate: from.toLocaleDateString("en-CA"),
                            toDate: to.toLocaleDateString("en-CA")
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-1.5 flex-shrink-0">
                    <button type="submit" className="flex-1 lg:flex-none px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg shadow-sm transition-colors font-semibold flex items-center justify-center gap-1.5 text-xs whitespace-nowrap">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>Apply Filters</span>
                    </button>
                    <button type="button" onClick={resetConfirmationFilters} className="flex-1 lg:flex-none px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg hover:text-slate-900 transition-colors font-semibold flex items-center justify-center gap-1.5 text-xs whitespace-nowrap">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Reset All</span>
                    </button>
                  </div>
                </div>
              </form>
            )}

            {showFilters && selectedTab !== "unprocessed" && selectedTab !== "confirmation" && (
              <form
                className="px-4 py-4 bg-slate-50/60 border-t border-slate-200/80 animate-slide-down"
                onSubmit={(e) => { e.preventDefault(); handleFilterClick(); }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-4 mb-4">
                  {[
                    {
                      name: "orderReferenceNumber",
                      label: "Order Reference Number",
                      placeholder: "Enter order reference number",
                      iconPath: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
                    },
                    {
                      name: "awbNumber",
                      label: "AWB Number",
                      placeholder: "Enter AWB / tracking number",
                      iconPath: "M4 6h16M4 10h16M4 14h16M4 18h16",
                    },
                    {
                      name: "customerPhone",
                      label: "Customer Phone",
                      placeholder: "Enter customer phone",
                      iconPath: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
                    },
                    {
                      name: "customerEmail",
                      label: "Customer Email",
                      placeholder: "Enter customer email",
                      iconPath: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
                    },
                    {
                      name: "itemName",
                      label: "Item Name",
                      placeholder: "Search by product name",
                      iconPath: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4",
                    },
                    {
                      name: "sku",
                      label: "SKU",
                      placeholder: "Search by SKU code",
                      iconPath: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z",
                    },
                  ].map(({ name, label, placeholder, iconPath }) => {
                    const FIELD_DOTS = {
                      orderReferenceNumber: "bg-violet-500",
                      awbNumber: "bg-[#3877FC]",
                      customerPhone: "bg-emerald-500",
                      customerEmail: "bg-emerald-500",
                      itemName: "bg-pink-500",
                      sku: "bg-pink-500",
                    };
                    const filled = Boolean(filters[name]);
                    return (
                    <div key={name} className="group w-full">
                      <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${FIELD_DOTS[name] || "bg-slate-300"}`}></span>
                        <span className="truncate">{label}</span>
                      </label>
                      <input
                        type="text"
                        name={name}
                        value={filters[name]}
                        onChange={handleInputChange}
                        onBlur={(e) => setFilters((prev) => ({ ...prev, [name]: e.target.value.trim() }))}
                        placeholder={placeholder}
                        autoComplete="off"
                        className={`w-full h-9 px-3 text-sm text-slate-800 placeholder:text-slate-400 border rounded-lg focus:ring-2 focus:ring-[#3877FC]/25 focus:border-[#3877FC] outline-none transition-colors ${filled
                          ? "border-[#3877FC]/40 bg-[#3877FC]/[0.03]"
                          : "border-slate-200 bg-white"}`}
                      />
                    </div>
                    );
                  })}

                  <div className="group w-full">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-amber-500"></span>
                      <span>Payment Mode</span>
                    </label>
                    <SelectMenu
                      value={filters.paymentMode}
                      onChange={(v) => setFilters((prev) => ({ ...prev, paymentMode: v }))}
                      options={[
                        { value: "", label: "All" },
                        { value: "PREPAID", label: "Prepaid" },
                        { value: "COD", label: "COD" },
                      ]}
                    />
                  </div>

                  <div className="group w-full">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-amber-500"></span>
                      <span>Courier Partner</span>
                    </label>
                    <SelectMenu
                      value={filters.courierPartnerName}
                      onChange={(v) => setFilters((prev) => ({ ...prev, courierPartnerName: v }))}
                      options={[{ value: "", label: "All Couriers" }, ...COURIER_OPTIONS]}
                    />
                  </div>

                  {(STATUS_MAP[selectedTab]?.length || 0) !== 1 && (
                  <div className="group w-full">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-amber-500"></span>
                      <span>Order Status</span>
                    </label>
                    <StatusDropdown
                      value={filters.orderStatus}
                      onChange={(value) => setFilters((prev) => ({ ...prev, orderStatus: value }))}
                      statuses={Object.values(OrderBusinessStatusEnum).filter(
                        // On a family tab only that family's statuses are offered, so a
                        // status pick always narrows WITHIN the tab, never escapes it.
                        (st) => !STATUS_MAP[selectedTab]?.length || STATUS_MAP[selectedTab].includes(st.code)
                      )}
                    />
                  </div>
                  )}

                  <div className="sm:col-span-1 w-full">
                    <div className="flex items-center gap-2 mb-1.5">
                      <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                        <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-amber-500"></span>
                        <span>Created Date</span>
                      </label>
                      {useDateFilter && (
                        <button
                          type="button"
                          onClick={() => setUseDateFilter(false)}
                          className="text-[10px] font-medium text-slate-400 hover:text-rose-600 transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {useDateFilter ? (
                      <div className="date-input-wrapper w-full">
                        <CustomDateRangePicker
                          selectedRange={[parseISO(filters.fromDate), parseISO(filters.toDate)]}
                          onRangeChange={([from, to]) => {
                            const diffDays = Math.abs((to - from) / (1000 * 60 * 60 * 24));
                            if (diffDays > 30) {
                              toast.error("Date range cannot exceed 1 month");
                              return;
                            }
                            setFilters(prev => ({
                              ...prev,
                              fromDate: from.toLocaleDateString("en-CA"),
                              toDate: to.toLocaleDateString("en-CA")
                            }));
                          }}
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setUseDateFilter(true)}
                        className="w-full h-9 border border-dashed border-slate-300 rounded-lg text-xs text-slate-500 hover:text-[#2A59BD] transition-colors font-medium"
                      >
                        + Add Created Date Range
                      </button>
                    )}
                    <div className="mt-1.5 flex items-center gap-1">
                      {DATE_PRESETS.map(({ label, days }) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => applyDatePreset(days)}
                          title={days === 1 ? "Today" : `Last ${days} days`}
                          className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors ring-1 ${isPresetActive(days)
                            ? "bg-[#3877FC] text-white ring-[#3877FC]"
                            : "bg-[#3877FC]/[0.05] text-[#2A59BD] ring-[#3877FC]/20 hover:bg-[#3877FC]/10"
                            }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sm:col-span-1 w-full">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-amber-500"></span>
                      <span>Order Type</span>
                    </label>
                    <SelectMenu
                      value={filters.orderType}
                      onChange={(v) => setFilters((prev) => ({ ...prev, orderType: v }))}
                      disabled={selectedTab === "rvp"}
                      options={[
                        { value: "", label: "No Selection" },
                        { value: "FORWARD", label: "Forward" },
                        { value: "REVERSE", label: "Reverse" },
                      ]}
                    />

                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row flex-wrap gap-2 items-stretch sm:items-center sm:justify-end pt-3 border-t border-slate-200/70">
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-3 py-1.5 bg-[#3877FC] hover:bg-[#2A59BD] text-white rounded-lg shadow-sm shadow-[#3877FC]/25 transition-colors font-semibold flex items-center justify-center gap-1.5 text-xs"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Apply Filters</span>
                  </button>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="w-full sm:w-auto px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg hover:text-slate-900 transition-colors font-semibold flex items-center justify-center gap-1.5 text-xs"
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Reset All</span>
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Mobile sticky action bar */}
          {showMobileActions && (
            <div className="fixed md:hidden bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t-2 border-gray-200 shadow-2xl px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Selected:</span>
                  <span className="bg-[#3877FC] text-white text-sm font-bold px-3 py-1 rounded-full">{pageSelectedCount}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleDownloadSelectedLabels}
                    disabled={pageSelectedCount === 0 || loadingDownloadSelected}
                    className={`px-4 h-12 rounded-xl text-white text-sm font-semibold transition-all shadow-lg ${pageSelectedCount === 0 || loadingDownloadSelected
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800'
                      }`}
                    aria-busy={loadingDownloadSelected}
                  >
                    {loadingDownloadSelected ? "Processing…" : "Download"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== Error message (if any) ===== */}
          {error && (
            <div className="m-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg flex items-center gap-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          )}

          {/* ===== Error / Unprocessed Orders Table ===== */}
          {selectedTab === "unprocessed" && (
            <div className="w-full max-w-full overflow-x-auto" style={{ overflowY: 'visible' }}>
              {loading ? (
                <TableSkeleton />
              ) : shipments.length === 0 ? (
                <EmptyState tab={selectedTab} onReset={resetUnprocessedFilters} />
              ) : (
                <table className="w-full table-auto">
                  <thead className="bg-slate-50">
                    <tr>
                      {unprocessedColConfig.orderedVisibleColumns.map(col => (
                        <th key={col.key} className={`${viewMode === 'compact' ? 'px-2.5 py-2' : 'px-3 py-2.5'} text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap shadow-[inset_0_-1px_0_#e2e8f0]`}>
                          {col.label}
                        </th>
                      ))}
                      <th className={`${viewMode === 'compact' ? 'px-2.5 py-2' : 'px-3 py-2.5'} text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap shadow-[inset_0_-1px_0_#e2e8f0] sticky right-0 bg-slate-50 min-w-[140px]`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {shipments.map((s, i) => {
                      const logs = s.carrier_allocation_attempt_logs || [];
                      const allFailed = logs.every(l => !l.is_successful_allocation);
                      const attemptCount = logs.length;
                      const isExpanded = expandedAllocLogs[i];
                      const isRetrying = retryingOrderIds.has(s.order_reference_number);

                      // Get the most recent failed attempt for error summary
                      const lastFailedLog = [...logs].reverse().find(l => !l.is_successful_allocation);
                      // Use carrierUtils to format names and messages
                      const lastCarrierName = lastFailedLog ? formatCarrierName(lastFailedLog.attempted_carrier_partner_public_name || lastFailedLog.attempted_carrier_partner_id) : '';
                      const lastFormattedError = lastFailedLog ? formatErrorMessage(lastFailedLog.error_message, lastFailedLog.error_code) : '';
                      const lastErrorCategory = lastFailedLog ? getErrorCategory(lastFailedLog.error_code, lastFailedLog.error_message) : '';

                      const renderUnprocessedCell = (col) => {
                        const p = viewMode === 'compact' ? 'p-1.5' : 'p-3';
                        switch (col.key) {
                          case 'order_ref': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col space-y-0.5">
                                <span className="font-semibold text-gray-900 text-xs">{s.order_reference_number}</span>
                                <span className="text-[10px] text-gray-400">{s.updated_date_time ? formatInIST(s.updated_date_time) : '-'}</span>
                                <span className="text-[10px] text-gray-400 font-mono truncate max-w-[140px]" title={s.shipment_order_id}>{s.shipment_order_id}</span>
                              </div>
                            </td>
                          );
                          case 'pickup': return (
                            <td key={col.key} className={p}>
                              {s.pickup_details ? (
                                <div className="flex flex-col space-y-0.5">
                                  <span className="text-xs font-medium text-gray-900">{s.pickup_details.pickup_poc || '-'}</span>
                                  <span className="text-[10px] text-gray-500">{[s.pickup_details.pickup_city, s.pickup_details.pickup_postal_code].filter(Boolean).join(', ')}</span>
                                </div>
                              ) : <span className="text-xs text-gray-400">-</span>}
                            </td>
                          );
                          case 'delivery': return (
                            <td key={col.key} className={p}>
                              {s.drop_details ? (
                                <div className="flex flex-col space-y-0.5">
                                  <span className="text-xs font-medium text-gray-900">{s.drop_details.drop_poc || '-'}</span>
                                  <span className="text-[10px] text-gray-500">{[s.drop_details.drop_city, s.drop_details.drop_postal_code].filter(Boolean).join(', ')}</span>
                                </div>
                              ) : <span className="text-xs text-gray-400">-</span>}
                            </td>
                          );
                          case 'invoice': return (
                            <td key={col.key} className={p}>
                              <span className="text-xs font-semibold text-gray-800">
                                {s.invoice_value ? `₹${parseFloat(s.invoice_value).toLocaleString('en-IN')}` : '-'}
                              </span>
                            </td>
                          );
                          case 'items': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col">
                                {(s.item_names || []).length > 0 ? (
                                  s.item_names.slice(0, 2).map((name, idx) => (
                                    <span key={idx} className="text-xs text-gray-700 truncate max-w-[150px]">{name}</span>
                                  ))
                                ) : <span className="text-xs text-gray-400">No items</span>}
                                {(s.item_names || []).length > 2 && (
                                  <span className="text-[10px] text-gray-500">+{s.item_names.length - 2} more</span>
                                )}
                              </div>
                            </td>
                          );
                          case 'error_summary': return (
                            <td key={col.key} className={`${p} max-w-[280px]`}>
                              <div className="flex flex-col space-y-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${allFailed ? 'bg-red-100 text-red-800 ring-1 ring-red-200' : 'bg-amber-100 text-amber-800 ring-1 ring-amber-200'}`}>
                                    {attemptCount} attempt{attemptCount !== 1 ? 's' : ''} - {allFailed ? 'All Failed' : 'Partial'}
                                  </span>
                                  {lastErrorCategory && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700">{lastErrorCategory}</span>
                                  )}
                                  {attemptCount > 0 && (
                                    <button onClick={() => setExpandedAllocLogs(prev => ({ ...prev, [i]: !prev[i] }))} className="text-pink-600 hover:text-pink-800 transition-colors" title="View full error details">
                                      <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                  )}
                                </div>
                                {lastFailedLog && (
                                  <div className="flex flex-col space-y-0.5">
                                    <span className="text-[10px] font-semibold text-gray-600">{lastCarrierName}</span>
                                    <span className="text-[10px] text-rose-600 leading-snug">{lastFormattedError}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                          case 'pickup_pincode': return (
                            <td key={col.key} className={p}><span className="inline-flex items-center text-xs text-gray-700">{s.pickup_details?.pickup_postal_code || '-'}{s.pickup_details?.pickup_postal_code ? <CellCopy value={s.pickup_details.pickup_postal_code} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /> : null}</span></td>
                          );
                          case 'drop_pincode': return (
                            <td key={col.key} className={p}><span className="inline-flex items-center text-xs text-gray-700">{s.drop_details?.drop_postal_code || '-'}{s.drop_details?.drop_postal_code ? <CellCopy value={s.drop_details.drop_postal_code} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /> : null}</span></td>
                          );
                          case 'shipment_order_id': return (
                            <td key={col.key} className={p}><span className="text-xs font-mono text-gray-600 truncate max-w-[140px] block" title={s.shipment_order_id}>{s.shipment_order_id || '-'}</span></td>
                          );
                          case 'attempt_count': return (
                            <td key={col.key} className={p}><span className="text-xs font-semibold text-gray-800">{attemptCount}</span></td>
                          );
                          default: return <td key={col.key} className={p}><span className="text-xs text-gray-400">-</span></td>;
                        }
                      };

                      return (
                        <React.Fragment key={i}>
                          <tr className={`transition-all hover:bg-pink-50/50 ${viewMode === 'compact' ? 'text-sm' : ''} ${allFailed && attemptCount > 0 ? 'border-l-4 border-l-red-400' : ''}`}>
                            {unprocessedColConfig.orderedVisibleColumns.map(col => renderUnprocessedCell(col))}
                            {/* Actions */}
                            <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} sticky right-0 bg-white border-l border-gray-100`}>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleRetryOrder(s)}
                                  disabled={isRetrying}
                                  className={`px-2 py-1 text-xs font-medium rounded-md border transition-all flex items-center gap-1 ${isRetrying ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed' : 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200'}`}
                                  title="Retry - fetch order data and open in create order form"
                                >
                                  {isRetrying ? (
                                    <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                  ) : (
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                  )}
                                  {isRetrying ? 'Loading...' : 'Retry'}
                                </button>
                                <button
                                  onClick={() => { window.open(`/shipment-details/${s.order_reference_number}`, "_blank"); }}
                                  className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-all"
                                  title="View Details"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Error Details */}
                          {isExpanded && attemptCount > 0 && (
                            <tr className="bg-pink-50/60">
                              <td colSpan={unprocessedColConfig.orderedVisibleColumns.length + 1} className="p-0">
                                <div className="px-4 py-3 space-y-3">
                                  {/* Error Diagnosis Card */}
                                  {lastFailedLog && (
                                    <div className="bg-white rounded-lg border-2 border-rose-200 p-3 shadow-sm">
                                      <h4 className="text-xs font-bold text-rose-700 mb-2 flex items-center gap-1.5">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                        Most Recent Error
                                      </h4>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                        <div>
                                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Courier</span>
                                          <p className="text-xs font-medium text-gray-900 mt-0.5">{lastCarrierName}</p>
                                        </div>
                                        <div>
                                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Error Code</span>
                                          <div className="flex items-center gap-1.5 mt-0.5">
                                            <span className="text-xs font-mono font-semibold text-rose-700">{lastFailedLog.error_code || '-'}</span>
                                            {lastErrorCategory && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700">{lastErrorCategory}</span>}
                                          </div>
                                        </div>
                                        <div className="sm:col-span-2">
                                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Error Message</span>
                                          <p className="text-xs text-gray-800 mt-0.5 leading-relaxed">{lastFormattedError}</p>
                                          {/* Show raw message toggle if it was an OMSException */}
                                          {lastFailedLog.error_message && lastFailedLog.error_message.includes('OMSException') && (
                                            <details className="mt-1.5">
                                              <summary className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-600">Show raw error</summary>
                                              <pre className="mt-1 text-[9px] text-gray-500 bg-gray-50 p-2 rounded border border-gray-200 max-h-24 overflow-auto whitespace-pre-wrap break-all font-mono">{lastFailedLog.error_message}</pre>
                                            </details>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* All Allocation Attempts Table */}
                                  <div>
                                    <h4 className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                                      <svg className="w-3.5 h-3.5 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                      All Carrier Allocation Attempts ({attemptCount})
                                    </h4>
                                    <div className="overflow-hidden rounded-lg border border-pink-200">
                                      <table className="w-full text-xs">
                                        <thead className="bg-pink-100 text-gray-700">
                                          <tr>
                                            <th className="px-3 py-1.5 text-left font-semibold">#</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Courier Name</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Status</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Category</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Error</th>
                                            <th className="px-3 py-1.5 text-left font-semibold">Timestamp</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-pink-100">
                                          {logs.map((log, logIdx) => (
                                            <tr key={logIdx} className={`hover:bg-pink-50 ${!log.is_successful_allocation ? 'bg-red-50/30' : ''}`}>
                                              <td className="px-3 py-1.5 text-gray-500">{log.attempted_carrier_partner_priority_order || logIdx + 1}</td>
                                              <td className="px-3 py-1.5 font-medium text-gray-800">{formatCarrierName(log.attempted_carrier_partner_public_name || log.attempted_carrier_partner_id)}</td>
                                              <td className="px-3 py-1.5">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${log.is_successful_allocation ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                  {log.is_successful_allocation ? 'Success' : 'Failed'}
                                                </span>
                                              </td>
                                              <td className="px-3 py-1.5">
                                                {!log.is_successful_allocation && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700">{getErrorCategory(log.error_code, log.error_message)}</span>}
                                              </td>
                                              <td className="px-3 py-1.5 text-gray-600 max-w-[300px]">
                                                <span className="block text-xs leading-snug" title={log.error_message}>{formatErrorMessage(log.error_message, log.error_code)}</span>
                                              </td>
                                              <td className="px-3 py-1.5 text-gray-500">{log.created_date_time_utc ? formatInIST(log.created_date_time_utc) : '-'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ===== Confirmation Orders Table ===== */}
          {selectedTab === "confirmation" && (
            <div className="w-full max-w-full overflow-x-auto" style={{ overflowY: 'visible' }}>
              {loading ? (
                <TableSkeleton />
              ) : shipments.length === 0 ? (
                <EmptyState tab={selectedTab} onReset={resetConfirmationFilters} />
              ) : (
                <table className="w-full table-auto">
                  <thead className="bg-slate-50">
                    <tr>
                      {confirmationColConfig.orderedVisibleColumns.map(col => (
                        <th key={col.key} className={`${viewMode === 'compact' ? 'px-2.5 py-2' : 'px-3 py-2.5'} text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap shadow-[inset_0_-1px_0_#e2e8f0]`}>
                          {col.label}
                        </th>
                      ))}
                      <th className={`${viewMode === 'compact' ? 'px-2.5 py-2' : 'px-3 py-2.5'} text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap shadow-[inset_0_-1px_0_#e2e8f0] sticky right-0 bg-slate-50 min-w-[160px]`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {shipments.map((s, i) => {
                      const items = (s.shipment_packages || []).flatMap(p => p.items || []);
                      const canPlaceOrder = s.confirmation_status === "PENDING" || s.confirmation_status === "CONFIRMED";
                      const statusColors = {
                        PENDING: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
                        CONFIRMED: 'bg-green-100 text-green-800 ring-1 ring-green-200',
                        BYPASSED: 'bg-blue-100 text-blue-800 ring-1 ring-blue-200',
                        CANCELLED: 'bg-red-100 text-red-800 ring-1 ring-red-200',
                        EXPIRED: 'bg-gray-100 text-gray-800 ring-1 ring-gray-200',
                      };

                      const renderConfirmationCell = (col) => {
                        const p = viewMode === 'compact' ? 'p-1.5' : 'p-3';
                        switch (col.key) {
                          case 'order_ref': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col space-y-0.5">
                                <span className="font-semibold text-gray-900 text-xs">{s.order_reference_number}</span>
                                <span className="text-[10px] text-gray-400">{s.order_created_date_time || '-'}</span>
                                <span className={`inline-flex items-center w-fit px-1.5 py-0.5 rounded-full text-[9px] font-bold ${s.order_type === 'COD' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                                  {s.order_type || '-'}
                                </span>
                              </div>
                            </td>
                          );
                          case 'pickup': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col space-y-0.5">
                                <span className="text-xs font-medium text-gray-900">{s.pickup_name || '-'}</span>
                                <span className="inline-flex items-center text-[10px] text-gray-500">{[s.pickup_city, s.pickup_pincode].filter(Boolean).join(', ')}{s.pickup_pincode ? <CellCopy value={s.pickup_pincode} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /> : null}</span>
                              </div>
                            </td>
                          );
                          case 'delivery': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col space-y-0.5">
                                <span className="text-xs font-medium text-gray-900">{s.drop_name || '-'}</span>
                                <span className="inline-flex items-center text-[10px] text-gray-500">{[s.drop_city, s.drop_pincode].filter(Boolean).join(', ')}{s.drop_pincode ? <CellCopy value={s.drop_pincode} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /> : null}</span>
                                <span className="text-[10px] text-gray-400">{s.drop_poc_contact_number || ''}</span>
                              </div>
                            </td>
                          );
                          case 'items': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col">
                                {items.length > 0 ? items.slice(0, 2).map((item, idx) => (
                                  <span key={idx} className="text-xs text-gray-700 truncate max-w-[150px]">
                                    {item.item_general_details?.item_name || '-'} {item.quantity > 1 ? `×${item.quantity}` : ''}
                                  </span>
                                )) : <span className="text-xs text-gray-400">No items</span>}
                                {items.length > 2 && <span className="text-[10px] text-gray-500">+{items.length - 2} more</span>}
                              </div>
                            </td>
                          );
                          case 'amount': return (
                            <td key={col.key} className={p}>
                              <span className="text-xs font-semibold text-gray-800">
                                {s.payment_details?.total_amount ? `₹${parseFloat(s.payment_details.total_amount).toLocaleString('en-IN')}` : '-'}
                              </span>
                            </td>
                          );
                          case 'status': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col space-y-1">
                                <span className={`inline-flex items-center w-fit px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColors[s.confirmation_status] || 'bg-gray-100 text-gray-600'}`}>
                                  {s.confirmation_status || '-'}
                                </span>
                                {s.confirmation_message_sent && (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-green-600">
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                    WhatsApp sent
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                          case 'order_type': return (
                            <td key={col.key} className={p}>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${s.order_type === 'COD' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                                {s.order_type || '-'}
                              </span>
                            </td>
                          );
                          case 'drop_phone': return (
                            <td key={col.key} className={p}><span className="text-xs text-gray-700">{s.drop_poc_contact_number || '-'}</span></td>
                          );
                          case 'pickup_pincode': return (
                            <td key={col.key} className={p}><span className="inline-flex items-center text-xs text-gray-700">{s.pickup_pincode || '-'}{s.pickup_pincode ? <CellCopy value={s.pickup_pincode} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /> : null}</span></td>
                          );
                          case 'whatsapp_status': return (
                            <td key={col.key} className={p}>
                              {s.confirmation_message_sent ? (
                                <span className="inline-flex items-center gap-1 text-[10px] text-green-600 font-semibold">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  Sent
                                </span>
                              ) : <span className="text-xs text-gray-400">Not sent</span>}
                            </td>
                          );
                          default: return <td key={col.key} className={p}><span className="text-xs text-gray-400">-</span></td>;
                        }
                      };

                      return (
                        <tr key={i} className={`transition-all hover:bg-cyan-50/50 ${viewMode === 'compact' ? 'text-sm' : ''} ${canPlaceOrder ? 'border-l-4 border-l-amber-400' : ''}`}>
                          {confirmationColConfig.orderedVisibleColumns.map(col => renderConfirmationCell(col))}
                          <td className={`${viewMode === 'compact' ? 'p-1.5' : 'p-3'} sticky right-0 bg-white border-l border-gray-100`}>
                            <div className="flex items-center gap-1">
                              {canPlaceOrder && (
                                <button
                                  onClick={() => {
                                    setConfirmModal({
                                      open: true,
                                      title: "Place Order",
                                      message: `Are you sure you want to place order ${s.order_reference_number}? This will bypass the confirmation step and initiate shipment processing.`,
                                      onConfirm: () => handleBypassConfirmation(s),
                                    });
                                  }}
                                  className="px-2 py-1 text-xs font-medium rounded-md border transition-all flex items-center gap-1 text-cyan-700 bg-cyan-50 hover:bg-cyan-100 border-cyan-200"
                                  title="Place order now"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  Place Order
                                </button>
                              )}
                              <button
                                onClick={() => { window.open(`/shipment-details/${s.order_reference_number}`, "_blank"); }}
                                className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-all"
                                title="View Details"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ===== Shipments table ===== */}
          {selectedTab !== "unprocessed" && selectedTab !== "confirmation" && (
            <div ref={mainScrollRef} className="w-full max-w-full overflow-x-auto" style={{ overflowY: 'visible' }}>
              {loading ? (
                <TableSkeleton />
              ) : shipments.length === 0 ? (
                <EmptyState tab={selectedTab} onReset={resetFilters} />
              ) : (
                <table className="w-full table-fixed">
                  <thead className="bg-slate-50">
                    <tr>
                      {/* Select-all checkbox header */}
                      <th className={`${viewMode === 'compact' ? 'px-2.5 py-2' : 'px-3 py-2.5'} w-10 shadow-[inset_0_-1px_0_#e2e8f0]`}>
                        <div className="flex items-center justify-center">
                          <input
                            ref={headerCheckboxRef}
                            type="checkbox"
                            checked={allOnPageSelected}
                            onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
                            className="h-4 w-4 accent-[#3877FC] cursor-pointer rounded"
                            title={allOnPageSelected ? "Unselect all" : "Select all"}
                          />
                        </div>
                      </th>
                      {mainColConfig.orderedVisibleColumns.map(col => (
                        <th key={col.key} className={`${viewMode === 'compact' ? 'px-2.5 py-2' : 'px-3 py-2.5'} ${MAIN_COL_WIDTHS[col.key] || ''} ${col.numeric ? 'text-right' : 'text-left'} text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap shadow-[inset_0_-1px_0_#e2e8f0]`}>
                          {col.label}
                        </th>
                      ))}
                      <th className={`${viewMode === 'compact' ? 'py-2' : 'py-2.5'} px-1 w-14 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap shadow-[inset_0_-1px_0_#e2e8f0] ${hasHScroll ? 'sticky right-0 bg-slate-50' : ''}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {shipments.map((s, i) => {
                      const k = rowKey(s);
                      const checked = selectedRows.has(k);
                      const channelOrderDisplay =
                        s.marketplace_channel_name
                          ? (s.marketplace_channel_order_number ?? "-")
                          : s.marketplace_channel_order_number;

                      const renderMainCell = (col) => {
                        const p = viewMode === 'compact' ? 'px-2.5 py-2.5 align-top' : 'px-3 py-3.5 align-top';
                        switch (col.key) {
                          case 'order_details': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col gap-0.5">
                                <span className="flex items-baseline gap-1 min-w-0">
                                  <span className="w-7 flex-shrink-0 text-[9px] font-bold uppercase tracking-wider text-violet-500">Ref</span>
                                  <span className="font-mono text-[12px] font-semibold text-slate-900 break-all leading-tight min-w-0">
                                    {s.order_reference_number}
<CellCopy value={s.order_reference_number} title="Copy full order reference" className="inline-flex align-middle ml-1 -mt-px p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" />
                                  </span>
                                </span>
                                {s.awb_number ? (
                                  <span className="flex items-baseline gap-1 min-w-0">
                                    <span className="w-7 flex-shrink-0 text-[9px] font-bold uppercase tracking-wider text-[#3877FC]">Awb</span>
                                    <span
                                      data-row-noclick=""
                                      onMouseDown={handleAwbMouseDown}
                                      onMouseUp={(e) => handleAwbMouseUp(e, s.awb_number)}
                                      className="font-mono text-[13px] font-medium text-[#2A59BD] hover:text-[#1C3B7E] cursor-pointer select-text break-all leading-snug min-w-0"
                                      title="Click to open live tracking"
                                    >
                                      {s.awb_number}
                                    </span>
<CellCopy value={s.awb_number} title="Copy AWB number" className="self-start mt-px p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors flex-shrink-0 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" />
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <span className="w-7 flex-shrink-0 text-[9px] font-bold uppercase tracking-wider text-[#3877FC]">Awb</span>
                                    <span className="text-xs text-slate-400">Not assigned</span>
                                  </span>
                                )}
                                {s.awb_number && (
                                  <span className="flex items-center gap-1">
                                    <span className="w-7 flex-shrink-0"></span>
                                    <button
                                      type="button"
                                      onClick={() => window.open(`https://tracking.wayuvega.in/track?awb=${encodeURIComponent(s.awb_number)}`, '_blank', 'noopener,noreferrer')}
                                      className="inline-flex items-center gap-0.5 text-[11px] font-medium text-[#2A59BD] hover:text-[#1C3B7E] hover:underline underline-offset-2 flex-shrink-0"
                                      title="Open live tracking"
                                    >
                                      Track
                                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M7 7h10v10" />
                                      </svg>
                                    </button>
<CellCopy value={`https://tracking.wayuvega.in/track?awb=${encodeURIComponent(s.awb_number)}`} title="Copy tracking link" className="p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors flex-shrink-0 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" />
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <span className="w-7 flex-shrink-0"></span>
                                  <span className="text-[11px] text-slate-500 whitespace-nowrap">
                                    {formatShortIST(s.order_placed_date_time)}
                                    {relTime(s.order_placed_date_time) && (
                                      <span className="text-slate-400"> ({relTime(s.order_placed_date_time)})</span>
                                    )}
                                  </span>
                                </span>
                              </div>
                            </td>
                          );
                          case 'awb_tracking': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-slate-900 leading-snug break-words line-clamp-2" title={s.allocated_carrier_partner_public_name || 'Not allocated'}>
                                  {s.allocated_carrier_partner_public_name ? titleCaseName(s.allocated_carrier_partner_public_name) : <span className="text-slate-400 font-normal">Not allocated</span>}
                                </span>
                                {(s.shipment_order_type || s.marketplace_channel_name) && (
                                <span className="flex items-center gap-1 min-w-0">
                                  {s.shipment_order_type && (
                                    <span className={`inline-flex flex-shrink-0 items-center rounded px-1 py-px text-[10px] font-semibold uppercase tracking-wide ring-1 ${s.shipment_order_type === 'RVP' || s.shipment_order_type === 'REVERSE' ? 'bg-purple-50 ring-purple-200 text-purple-700' : 'bg-sky-50 ring-sky-200 text-sky-700'}`}>
                                      {s.shipment_order_type}
                                    </span>
                                  )}
                                  {s.marketplace_channel_name && (
                                    <span className="inline-block min-w-0 rounded px-1 py-px text-[10px] font-semibold uppercase tracking-wide ring-1 bg-indigo-50 ring-indigo-200 text-indigo-700 truncate" title={s.marketplace_channel_name}>
                                      {s.marketplace_channel_name}
                                    </span>
                                  )}
                                </span>
                                )}
                                {channelOrderDisplay && channelOrderDisplay !== '-' && (
                                  <span className="truncate font-mono text-[11px] text-slate-500" title={channelOrderDisplay}>{channelOrderDisplay}</span>
                                )}
                              </div>
                            </td>
                          );
                          case 'route': return (
                            <td key={col.key} className={p}>
                              {/* Dots live INSIDE their city rows - alignment by construction, no padding math. */}
                              <div className="flex flex-col gap-0.5 min-w-0">
                                <div className="flex flex-col min-w-0" title={`From: ${s.pickup_organisation || s.pickup_name || '—'} · ${cityPinLine(s.pickup_city, s.pickup_pincode)}`}>
                                  <span className="flex items-center gap-1.5 min-w-0">
                                    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-amber-500 ring-2 ring-amber-100 flex-shrink-0"></span>
                                    <span className="flex items-baseline gap-1 min-w-0">
                                      <span className="text-[13px] font-medium text-slate-900 truncate min-w-0">{cityLabel(s.pickup_city) || '—'}</span>
                                      {s.pickup_pincode && <span className="flex-shrink-0 inline-flex items-center text-xs text-slate-500 tabular-nums">{s.pickup_pincode}<CellCopy value={s.pickup_pincode} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" /></span>}
                                    </span>
                                  </span>
                                  <span className="pl-[14px] text-[11px] text-slate-500 truncate">{s.pickup_organisation || s.pickup_name || '—'}</span>
                                </div>
                                <span aria-hidden="true" className="ml-[3.5px] h-2.5 w-px bg-slate-200 flex-shrink-0"></span>
                                <div className="flex flex-col min-w-0" title={`To: ${s.drop_name || '—'} · ${cityPinLine(s.drop_city, s.drop_pincode)}`}>
                                  <span className="flex items-center gap-1.5 min-w-0">
                                    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-emerald-100 flex-shrink-0"></span>
                                    <span className="flex items-baseline gap-1 min-w-0">
                                      <span className="text-[13px] font-medium text-slate-900 truncate min-w-0">{cityLabel(s.drop_city) || '—'}</span>
                                      {s.drop_pincode && <span className="flex-shrink-0 inline-flex items-center text-xs text-slate-500 tabular-nums">{s.drop_pincode}<CellCopy value={s.drop_pincode} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" /></span>}
                                    </span>
                                  </span>
                                  <span className="pl-[14px] text-[11px] text-slate-500 truncate">{s.drop_name || '—'}</span>
                                </div>
                              </div>
                            </td>
                          );
                          case 'pickup': return (
                            <td key={col.key} className={`${p} max-w-[160px]`}>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium text-slate-900 truncate" title={s.pickup_organisation || s.pickup_name || undefined}>
                                  {s.pickup_organisation || s.pickup_name || '—'}
                                </span>
                                <span className="text-xs text-slate-500 truncate" title={cityPinLine(s.pickup_city, s.pickup_pincode)}>
                                  {cityPinLine(s.pickup_city, s.pickup_pincode)}
                                </span>
                              </div>
                            </td>
                          );
                          case 'delivery': return (
                            <td key={col.key} className={`${p} max-w-[160px]`}>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium text-slate-900 truncate" title={s.drop_name || undefined}>
                                  {s.drop_name || '—'}
                                </span>
                                <span className="text-xs text-slate-500 truncate" title={cityPinLine(s.drop_city, s.drop_pincode)}>
                                  {cityPinLine(s.drop_city, s.drop_pincode)}
                                </span>
                              </div>
                            </td>
                          );
                          case 'payment': return (
                            <td key={col.key} className={p}>
                              <div className="flex flex-col items-start gap-1">
                                <span className="text-[13px] font-semibold text-slate-900 tabular-nums whitespace-nowrap" title={`${s.payment_details?.payment_mode === 'COD' ? 'COD - to collect from customer' : 'Declared invoice value (prepaid)'}: ₹${Number(s.payment_details?.total_amount ?? 0).toFixed(2)}`}>₹{Number(s.payment_details?.total_amount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                {s.payment_details?.payment_mode ? (
                                  <>
                                    <span className={`inline-flex max-w-fit items-center rounded px-1 py-px text-[10px] font-semibold uppercase tracking-wide ring-1 ${s.payment_details.payment_mode === 'COD' ? 'bg-amber-50 ring-amber-200 text-amber-700' : 'bg-emerald-50 ring-emerald-200 text-emerald-700'}`}>
                                      {s.payment_details.payment_mode}
                                    </span>
                                    {s.payment_details.payment_mode === 'COD' && (
                                      <span className="text-[10px] text-slate-400 whitespace-nowrap">to collect</span>
                                    )}
                                  </>
                                ) : <span className="text-xs text-slate-400">—</span>}
                              </div>
                            </td>
                          );
                          case 'package': {
                            const volCharged = s.charged_weight_category === 'VOLUMETRIC_WEIGHT';
                            const hasCategory = Boolean(s.charged_weight_category);
                            const weightLine = (label, grams, isBilled) => (grams != null ? (
                              <span
                                className={`flex items-baseline justify-start gap-1 tabular-nums whitespace-nowrap ${isBilled ? 'text-sm font-semibold text-slate-900' : 'text-xs text-slate-500'}`}
                                title={isBilled ? 'Billed on this weight' : undefined}
                              >
                                {isBilled && <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 self-center"></span>}
                                <span className={`text-[9px] font-bold uppercase tracking-wider ${isBilled ? 'text-violet-500' : 'text-slate-400'}`}>{label}</span>
                                {gramsToKg(grams)}
                              </span>
                            ) : null);
                            return (
                              <td key={col.key} className={p}>
                                <div className="flex flex-col items-start gap-0.5">
                                  {weightLine('Dead', s.dead_weight_in_grams, hasCategory && !volCharged)}
                                  {weightLine('Vol', s.volumetric_weight_in_grams, hasCategory && volCharged)}
                                  {(() => {
                                    const d = s.shipment_package_dimensions;
                                    if (!d?.length || !d?.breadth || !d?.height) return null;
                                    const dims = `${d.length} × ${d.breadth} × ${d.height} ${(d.unit || 'cm').toLowerCase()}`;
                                    return (
                                      <span className="max-w-full truncate text-[11px] text-slate-400 tabular-nums" title={dims}>
                                        {dims}
                                      </span>
                                    );
                                  })()}
                                  {!hasCategory && s.dead_weight_in_grams == null && s.volumetric_weight_in_grams == null && (
                                    <span className="text-sm text-slate-400">—</span>
                                  )}
                                </div>
                              </td>
                            );
                          }
                          case 'items': {
                            const allItems = (s.shipment_packages ?? []).flatMap(pkg => pkg.items ?? []);
                            const firstName = allItems[0]?.item_general_details?.item_name || null;
                            const firstQty = allItems[0]?.quantity;
                            const remaining = allItems.length - 1;
                            const countParts = [
                              firstQty > 1 ? `×${firstQty}` : null,
                              remaining > 0 ? `+${remaining} more` : null,
                            ].filter(Boolean);
                            const countLabel = countParts.length > 0 ? countParts.join(' · ') : '1 item';
                            return (
                              <td key={col.key} className={p}>
                                {allItems.length > 0 ? (
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-sm font-medium text-slate-900 truncate" title={firstName || undefined}>
                                      {firstName || '—'}
                                    </span>
                                    <button
                                      onClick={() => toggleRow(i)}
                                      className="inline-flex w-fit max-w-full items-center gap-0.5 text-xs text-slate-500 hover:text-[#2A59BD] transition-colors"
                                      title={expandedRows[i] ? 'Hide item details' : 'Show item details'}
                                    >
                                      <span className="truncate">{countLabel}</span>
                                      <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${expandedRows[i] ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                      </svg>
                                    </button>
                                  </div>
                                ) : <span className="text-xs text-slate-400">No items</span>}
                              </td>
                            );
                          }
                          case 'item_sku_col': {
                            const skuItems = (s.shipment_packages ?? []).flatMap(pkg => pkg.items ?? []);
                            const firstSku = skuItems[0]?.item_general_details?.sku || null;
                            const skuRemaining = skuItems.length - 1;
                            return (
                              <td key={col.key} className={`${p} max-w-[140px]`}>
                                {skuItems.length > 0 ? (
                                  <div className="flex flex-col gap-0.5">
                                    <span className="font-mono text-[13px] text-slate-700 truncate" title={firstSku || undefined}>
                                      {firstSku || '—'}
                                    </span>
                                    {skuRemaining > 0 && (
                                      <button
                                        onClick={() => toggleRow(i)}
                                        className="inline-flex w-fit items-center gap-0.5 text-xs text-slate-500 hover:text-[#2A59BD] transition-colors"
                                        title={expandedRows[i] ? 'Hide item details' : 'Show item details'}
                                      >
                                        +{skuRemaining} more
                                        <svg className={`w-3 h-3 transition-transform ${expandedRows[i] ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                ) : <span className="text-xs text-slate-400">—</span>}
                              </td>
                            );
                          }
                          case 'status': return (
                            <td key={col.key} className={p}>
                              <StatusBadge statusCode={s.latest_order_status_code} shipment={s} onSupportClick={handleOpenSupport} />
                            </td>
                          );
                          case 'invoice_number': return (
                            <td key={col.key} className={p}>{s.invoice_number ? <span className="block truncate text-sm text-slate-700" title={s.invoice_number}>{s.invoice_number}</span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'order_type': return (
                            <td key={col.key} className={p}>
                              {s.shipment_order_type ? (
                                <span className={`inline-flex items-center rounded px-1.5 py-px text-[10px] font-medium uppercase tracking-wide ring-1 ${s.shipment_order_type === 'RVP' ? 'ring-purple-200 text-purple-600' : 'ring-slate-200 text-slate-500'}`}>
                                  {s.shipment_order_type}
                                </span>
                              ) : <span className="text-sm text-slate-400">—</span>}
                            </td>
                          );
                          case 'drop_phone': return (
                            <td key={col.key} className={p}>{s.drop_poc_contact_number ? <span className="text-sm text-slate-700 tabular-nums">{s.drop_poc_contact_number}</span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'drop_state': return (
                            <td key={col.key} className={p}>{s.drop_state ? <span className="text-sm text-slate-700">{s.drop_state}</span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'drop_pincode': return (
                            <td key={col.key} className={p}>{s.drop_pincode ? <span className="inline-flex items-center text-sm text-slate-700 tabular-nums">{s.drop_pincode}<CellCopy value={s.drop_pincode} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" /></span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'invoice_value': return (
                            <td key={col.key} className={`${p} text-right`}>{s.payment_details?.total_amount ? <span className="text-sm font-semibold text-slate-900 tabular-nums whitespace-nowrap">₹{parseFloat(s.payment_details.total_amount).toLocaleString('en-IN')}</span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'shipping_charges': return (
                            <td key={col.key} className={`${p} text-right`}>{s.total_shipping_charge ? <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">₹{parseFloat(s.total_shipping_charge).toLocaleString('en-IN')}</span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'dead_weight': return (
                            <td key={col.key} className={`${p} text-right`}>{s.dead_weight_in_grams != null ? <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{s.dead_weight_in_grams} g</span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'volumetric_weight': return (
                            <td key={col.key} className={`${p} text-right`}>{s.volumetric_weight_in_grams != null ? <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{s.volumetric_weight_in_grams} g</span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'marketplace_channel': return (
                            <td key={col.key} className={p}>{s.marketplace_channel_name ? <span className="text-sm text-slate-700">{s.marketplace_channel_name}</span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'pickup_city': return (
                            <td key={col.key} className={p}>{s.pickup_city ? <span className="block truncate text-sm text-slate-700" title={s.pickup_city}>{s.pickup_city}</span> : <span className="text-sm text-slate-400">—</span>}</td>
                          );
                          case 'shipment_age': {
                            const ageDays = s.order_placed_date_time
                              ? Math.floor((Date.now() - new Date(s.order_placed_date_time).getTime()) / 86400000)
                              : null;
                            const ageColor = ageDays == null ? 'text-slate-400' : ageDays > 7 ? 'text-red-600 font-semibold' : ageDays > 3 ? 'text-amber-600' : 'text-emerald-600';
                            return (
                              <td key={col.key} className={`${p} text-right`}>
                                <span className={`text-sm tabular-nums ${ageColor}`}>{ageDays != null ? `${ageDays}d` : '—'}</span>
                              </td>
                            );
                          }
                          default: return <td key={col.key} className={p}><span className="text-sm text-slate-400">—</span></td>;
                        }
                      };

                      return (
                        <React.Fragment key={i}>
                          <tr
                            onClick={(e) => handleRowNavigate(e, s)}
                            data-changed={changedRefs.has(s.order_reference_number) ? "true" : undefined}
                            className={`group relative transition-colors duration-700 cursor-pointer ${changedRefs.has(s.order_reference_number)
                              ? 'bg-amber-50'
                              : checked ? 'bg-[#3877FC]/[0.07]' : 'hover:bg-[#3877FC]/[0.05]'
                              } ${viewMode === 'compact' ? 'text-sm' : ''}`}
                          >
                            {/* Row checkbox + status-tinted scan edge */}
                            <td data-row-noclick="" className={`${viewMode === 'compact' ? 'px-2.5 py-2.5' : 'px-3 py-3.5'} w-10 relative align-top cursor-default`}>
                              <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-[3px] ${statusEdgeClass(s.latest_order_status_code)}`}></span>
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleSelectOne(k)}
                                  className="h-4 w-4 accent-[#3877FC] cursor-pointer rounded"
                                  title={checked ? "Unselect" : "Select"}
                                />
                              </div>
                            </td>
                            {mainColConfig.orderedVisibleColumns.map(col => renderMainCell(col))}
                            {/* Actions */}
                            <td data-row-noclick="" className={`${viewMode === 'compact' ? 'py-2.5' : 'py-3.5'} px-1 align-top cursor-default ${hasHScroll ? `sticky right-0 shadow-[inset_1px_0_0_#e2e8f0] ${checked ? 'bg-[#F1F5FF]' : 'bg-white group-hover:bg-[#F5F8FF]'}` : ''}`}>
                              <div className="flex flex-col items-center gap-1">
                                <ActionMenu
                                  shipment={s}
                                  onAction={handleActionClick}
                                  currentPage={currentPage}
                                  onOpenChange={(isOpen) => setOpenActionMenuRow(isOpen ? i : null)}
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleRow(i)}
                                  aria-expanded={!!expandedRows[i]}
                                  title={expandedRows[i] ? 'Hide shipment details' : 'Show shipment details'}
                                  className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-400 hover:text-[#2A59BD] transition-colors"
                                >
                                  <svg className={`w-4 h-4 transition-transform duration-150 ${expandedRows[i] ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleActionClick('support', s, currentPage)}
                                  title="Create support ticket"
                                  className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-400 hover:text-[#2A59BD] transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Row - full order details, organised */}
                          {expandedRows[i] && (() => {
                            const expandedItems = (s.shipment_packages ?? []).flatMap(pkg => pkg.items ?? []);
                            const lineTotals = expandedItems.map(item => {
                              const qty = Number(item?.quantity);
                              const unit = Number(item?.item_payment_details?.unit_price);
                              return Number.isFinite(qty) && Number.isFinite(unit) ? qty * unit : null;
                            });
                            const subtotal = lineTotals.some(t => t != null)
                              ? lineTotals.reduce((sum, t) => sum + (t ?? 0), 0)
                              : null;
                            const totalQty = expandedItems.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);
                            const microLabel = "text-[10px] font-semibold uppercase tracking-wider text-slate-400";
                            const refRow = (label, value) => value ? (
                              <div className="flex items-baseline justify-between gap-3">
                                <span className="text-xs text-slate-500 flex-shrink-0">{label}</span>
                                <span className="text-xs font-medium text-slate-900 text-right break-all">{value}</span>
                              </div>
                            ) : null;
                            return (
                              <tr>
                                <td colSpan={mainColConfig.orderedVisibleColumns.length + 2} className="p-0 bg-white">
                                  <div className="px-3 py-3 md:px-4 animate-slide-down">
                                    <div className="rounded-xl bg-slate-50/50 ring-1 ring-slate-200/70 px-4 py-4 md:px-6 md:py-5">
                                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-5">
                                        {/* Pickup address */}
                                        <div className="flex flex-col gap-1 min-w-0">
                                          <span className={microLabel}>Pickup</span>
                                          <span className="text-sm font-medium text-slate-900">{s.pickup_organisation || s.pickup_name || '—'}</span>
                                          {s.pickup_name && s.pickup_organisation && s.pickup_name !== s.pickup_organisation && (
                                            <span className="text-xs text-slate-500">{s.pickup_name}</span>
                                          )}
                                          {s.pickup_address && <span className="text-xs text-slate-600 leading-relaxed">{s.pickup_address}</span>}
                                          <span className="text-xs text-slate-600">
                                            {[s.pickup_city, s.pickup_state].filter(Boolean).join(', ') || '—'}{s.pickup_pincode ? <> {' - '}<span className="tabular-nums">{s.pickup_pincode}</span><CellCopy value={s.pickup_pincode} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /></> : ''}
                                          </span>
                                          {s.pickup_poc_contact_number && <span className="text-xs text-slate-500 tabular-nums">{s.pickup_poc_contact_number}</span>}
                                          {s.pickup_poc_email && <span className="text-xs text-slate-500 break-all">{s.pickup_poc_email}</span>}
                                        </div>
                                        {/* Delivery address */}
                                        <div className="flex flex-col gap-1 min-w-0">
                                          <span className={microLabel}>Delivery</span>
                                          <span className="text-sm font-medium text-slate-900">{s.drop_name || '—'}</span>
                                          {s.drop_address && <span className="text-xs text-slate-600 leading-relaxed">{s.drop_address}</span>}
                                          <span className="text-xs text-slate-600">
                                            {[s.drop_city, s.drop_state].filter(Boolean).join(', ') || '—'}{s.drop_pincode ? <> {' - '}<span className="tabular-nums">{s.drop_pincode}</span><CellCopy value={s.drop_pincode} title="Copy pincode" className="inline-flex align-middle p-0.5 rounded text-slate-300 hover:text-slate-600 transition-colors" /></> : ''}
                                          </span>
                                          {s.drop_poc_contact_number && <span className="text-xs text-slate-500 tabular-nums">{s.drop_poc_contact_number}</span>}
                                        </div>
                                        {/* Package & weights */}
                                        <div className="flex flex-col gap-1.5 min-w-0">
                                          <span className={microLabel}>Package</span>
                                          {refRow('Dead weight', gramsToKg(s.dead_weight_in_grams))}
                                          {refRow('Volumetric', gramsToKg(s.volumetric_weight_in_grams))}
                                          {refRow('Dimensions', (() => {
                                            const d = s.shipment_package_dimensions;
                                            return d?.length && d?.breadth && d?.height
                                              ? `${d.length} × ${d.breadth} × ${d.height} ${(d.unit || 'cm').toLowerCase()}`
                                              : null;
                                          })())}
                                          {s.charged_weight_category && (
                                            <div className="flex items-baseline justify-between gap-3">
                                              <span className="text-xs text-slate-500 flex-shrink-0">Charged on</span>
                                              <span className="flex items-center gap-1 text-xs font-medium text-slate-900">
                                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.charged_weight_category === 'VOLUMETRIC_WEIGHT' ? 'bg-violet-400' : 'bg-slate-400'}`}></span>
                                                {s.charged_weight_category === 'VOLUMETRIC_WEIGHT' ? 'Volumetric' : 'Dead weight'}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                        {/* Charges + References */}
                                        <div className="flex flex-col gap-4 min-w-0">
                                          <div className="flex flex-col gap-1.5 min-w-0">
                                            <span className={microLabel}>Charges</span>
                                            <div className="flex items-baseline justify-between gap-3">
                                              <span className="text-xs text-slate-500 flex-shrink-0">Shipping charge</span>
                                              <span className="text-sm font-semibold text-slate-900 tabular-nums">
                                                {s.total_shipping_charge ? `₹${parseFloat(s.total_shipping_charge).toLocaleString('en-IN')}` : '—'}
                                              </span>
                                            </div>
                                            {s.payment_details?.payment_mode && (
                                              <div className="flex items-baseline justify-between gap-3">
                                                <span className="text-xs text-slate-500 flex-shrink-0">Payment mode</span>
                                                <span className={`inline-flex items-center rounded px-1 py-px text-[10px] font-semibold uppercase tracking-wide ring-1 ${s.payment_details.payment_mode === 'COD' ? 'bg-amber-50 ring-amber-200 text-amber-700' : 'bg-emerald-50 ring-emerald-200 text-emerald-700'}`}>
                                                  {s.payment_details.payment_mode}
                                                </span>
                                              </div>
                                            )}
                                            {refRow(s.payment_details?.payment_mode === 'COD' ? 'To collect (COD)' : 'Declared invoice value',
                                              s.payment_details?.total_amount != null ? `₹${Number(s.payment_details.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : null)}
                                          </div>
                                          <div className="flex flex-col gap-1.5 min-w-0">
                                            <span className={microLabel}>References</span>
                                            {refRow('Placed', s.order_placed_date_time ? formatInIST(s.order_placed_date_time) : null)}
                                            {(() => {
                                              const rowEdd = eddByRef[s.order_reference_number];
                                              if (!rowEdd) return null;
                                              const st = statusTone(s.latest_order_status_code);
                                              // Same pre-pickup gate as the Status column: no EDD before the parcel moved.
                                              if (!eddVisibleForCode(s.latest_order_status_code, rowEdd.kind, rowEdd.deltaDays != null)) return null;
                                              if (rowEdd.deltaDays != null) {
                                                const n = Math.abs(rowEdd.deltaDays);
                                                const verdict = rowEdd.deltaDays > 0 ? `${n} day${n === 1 ? '' : 's'} late`
                                                  : rowEdd.deltaDays < 0 ? `${n} day${n === 1 ? '' : 's'} early` : 'On time';
                                                return refRow(rowEdd.kind === 'RETURN' ? 'Returned' : 'Delivered', `${verdict} · vs ${fmtEdd(rowEdd.date)}`);
                                              }
                                              if (!rowEdd.date) return null;
                                              if (!rowEdd.server && !(st.stage != null && st.stage >= 3 && st.stage < 5)) return null;
                                              const label = rowEdd.kind === 'RETURN'
                                                ? (rowEdd.source === 'ESTIMATED' ? 'Est. return' : 'Return EDD')
                                                : (rowEdd.source === 'ESTIMATED' ? 'Est. delivery' : 'Expected delivery');
                                              const rel = eddRelPhrase(rowEdd.date);
                                              return refRow(label, rel ? `${fmtEdd(rowEdd.date)} (${rel})` : fmtEdd(rowEdd.date));
                                            })()}
                                            {refRow('Courier', s.allocated_carrier_partner_public_name)}
                                            {refRow('Channel', s.marketplace_channel_name)}
                                            {refRow('Channel order', s.marketplace_channel_order_number)}
                                            {refRow('Invoice no.', s.invoice_number)}
                                          </div>
                                        </div>
                                      </div>
                                      {/* Items */}
                                      <div className="mt-5">
                                        <span className={microLabel}>Items</span>
                                        {expandedItems.length > 0 ? (
                                          <div className="mt-1.5 overflow-x-auto rounded-lg ring-1 ring-slate-200 bg-white">
                                            <table className="w-full text-sm">
                                              <thead className="bg-slate-50">
                                                <tr>
                                                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Item</th>
                                                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">SKU</th>
                                                  <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Qty</th>
                                                  <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Unit price</th>
                                                  <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Amount</th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-slate-100">
                                                {expandedItems.map((item, j) => (
                                                  <tr key={j} className="hover:bg-slate-50/60">
                                                    <td className="px-3 py-2 text-sm font-medium text-slate-900">{item?.item_general_details?.item_name || '—'}</td>
                                                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{item?.item_general_details?.sku || '—'}</td>
                                                    <td className="px-3 py-2 text-right text-sm text-slate-700 tabular-nums">{item?.quantity ?? '—'}</td>
                                                    <td className="px-3 py-2 text-right text-sm text-slate-700 tabular-nums whitespace-nowrap">{item?.item_payment_details?.unit_price != null ? `₹${item.item_payment_details.unit_price}` : '—'}</td>
                                                    <td className="px-3 py-2 text-right text-sm font-medium text-slate-900 tabular-nums whitespace-nowrap">{lineTotals[j] != null ? `₹${lineTotals[j].toFixed(2)}` : '—'}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                              <tfoot>
                                                <tr className="bg-slate-50/70 border-t border-slate-200">
                                                  <td colSpan={2} className="px-3 py-2 text-xs font-medium text-slate-500">Subtotal</td>
                                                  <td className="px-3 py-2 text-right text-xs font-medium text-slate-700 tabular-nums">{totalQty}</td>
                                                  <td></td>
                                                  <td className="px-3 py-2 text-right text-sm font-semibold text-slate-900 tabular-nums whitespace-nowrap">{subtotal != null ? `₹${subtotal.toFixed(2)}` : '—'}</td>
                                                </tr>
                                              </tfoot>
                                            </table>
                                          </div>
                                        ) : (
                                          <p className="mt-1.5 text-xs text-slate-400">No items on this order.</p>
                                        )}
                                      </div>
                                      <div className="mt-4 pt-3 border-t border-slate-200/70 flex items-center justify-end gap-2">
                                        <button
                                          type="button"
                                          onClick={() => toggleRow(i)}
                                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors"
                                        >
                                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                          </svg>
                                          Collapse
                                        </button>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            const url = `/shipment-details/${encodeURIComponent(s.order_reference_number)}?page=${currentPage}`;
                                            if (e.metaKey || e.ctrlKey) window.open(url, "_blank");
                                            else navigate(url);
                                          }}
                                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#3877FC]/30 bg-white text-xs font-semibold text-[#2A59BD] hover:bg-[#3877FC]/[0.06] transition-colors"
                                        >
                                          View full details
                                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })()}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ===== Pagination ===== */}
          {!loading && shipments.length > 0 && (() => {
            const rangeStart = (currentPage - 1) * limit + 1;
            const rangeEnd = (currentPage - 1) * limit + shipments.length;
            return (
            <div className="border-t border-slate-200/80 bg-white px-3 md:px-4 py-2.5">
              <div className="flex flex-wrap justify-between items-center gap-3">
                <span className="text-sm text-slate-500 tabular-nums">
                  <span className="font-semibold text-slate-800">{rangeStart.toLocaleString('en-IN')}–{rangeEnd.toLocaleString('en-IN')}</span>
                  {totalRecords > 0 && (
                    <>
                      <span className="mx-1">of</span>
                      <span className="font-semibold text-slate-800">{totalRecords.toLocaleString('en-IN')}</span>
                    </>
                  )}
                  <span className="ml-1">shipments</span>
                </span>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Rows per page</span>
                    <div className="w-20">
                      <SelectMenu
                        value={limit}
                        onChange={(v) => handleLimitChange({ target: { value: v } })}
                        buttonClassName="h-8 text-sm font-semibold"
                        dropUp
                        options={[50, 100, 200].map((n) => ({ value: n, label: String(n) }))}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={prevPage}
                      disabled={currentPage === 1}
                      title="Previous page"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-900 disabled:text-slate-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="text-sm text-slate-500 tabular-nums whitespace-nowrap px-1">
                      Page <span className="font-semibold text-slate-800">{currentPage}</span>
                      {totalPages > 1 && <> of <span className="font-semibold text-slate-800">{totalPages}</span></>}
                    </span>
                    <button
                      onClick={nextPage}
                      disabled={shipments.length < limit}
                      title="Next page"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-900 disabled:text-slate-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            );
          })()}
        </div>
      </div>

      {/* ===== Claim Upload Modal ===== */}
      <ClaimUploadModal
        open={claimModalOpen}
        onClose={(result) => {
          setClaimModalOpen(false);
          setClaimShipment(null);
          setClaimDefaultItem(null);
          if (result?.success) {
            fetchData(selectedTab, { skip: (currentPage - 1) * limit });
            toast.success("Claim submitted successfully");
          }
        }}
        shipment={claimShipment}
        defaultItem={claimDefaultItem}
      />

      {/* ===== Support Ticket Modal ===== */}
      <CreateTicketModal
        isOpen={supportModalOpen}
        onClose={() => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
        }}
        onSuccess={(ticket) => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
          navigate(`/support/tickets/${ticket.public_id}`);
        }}
        orderContext={supportOrderContext}
      />

      {/* Column Customizer Modal */}
      {showColCustomizer && (() => {
        const activeConfig = selectedTab === 'unprocessed' ? unprocessedColConfig
          : selectedTab === 'confirmation' ? confirmationColConfig
            : mainColConfig;
        const activeRegistry = selectedTab === 'unprocessed' ? UNPROCESSED_TABLE_COLUMNS
          : selectedTab === 'confirmation' ? CONFIRMATION_TABLE_COLUMNS
            : MAIN_TABLE_COLUMNS;
        const tableLabel = selectedTab === 'unprocessed' ? 'Unprocessed Orders'
          : selectedTab === 'confirmation' ? 'Confirmation Orders'
            : 'Shipments';
        return (
          <ColumnCustomizerModal
            key={selectedTab}
            open={showColCustomizer}
            onClose={() => setShowColCustomizer(false)}
            registry={activeRegistry}
            config={activeConfig}
            onSave={(newConfig) => activeConfig.updateConfig(newConfig)}
            tableLabel={tableLabel}
          />
        );
      })()}

      {/* CSV Export Modal */}
      {showCSVColumnModal && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-gradient-to-br from-slate-900/90 via-slate-800/85 to-slate-900/90 backdrop-blur-lg p-0 sm:p-2 md:p-4 lg:p-6 animate-fade-in overflow-hidden"
          onClick={() => setShowCSVColumnModal(false)}
        >
          <div
            className="bg-white rounded-none sm:rounded-2xl md:rounded-3xl shadow-2xl w-full h-full sm:h-auto sm:max-w-6xl sm:max-h-[96vh] lg:max-h-[95vh] overflow-hidden flex flex-col animate-scale-in border-0 sm:border-2 border-slate-200/80 shadow-slate-900/30 max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-[#3877FC] via-[#2A59BD] to-[#1C3B7E] px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 md:py-5 lg:py-6 flex items-center justify-between border-b-0 relative overflow-hidden w-full min-w-0">
              <div className="absolute inset-0 bg-gradient-to-r from-[#3877FC]/20 via-transparent to-[#2A59BD]/20"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500/10 rounded-full -ml-24 -mb-24 blur-2xl"></div>

              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 flex-1 min-w-0 pr-1 sm:pr-2 relative z-10">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 rounded-lg sm:rounded-xl md:rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-xl shadow-blue-900/30 flex-shrink-0">
                  <CSVIcon.Download className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 lg:h-7 lg:w-7 xl:h-8 xl:w-8 text-white" />
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <h3 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-white mb-0.5 sm:mb-1 truncate drop-shadow-sm">Export Data to CSV</h3>
                  <p className="text-[10px] sm:text-xs md:text-sm text-blue-100 truncate">
                    {shipments.filter(isSelectedOnPage).length} {shipments.filter(isSelectedOnPage).length === 1 ? 'shipment' : 'shipments'} selected
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCSVColumnModal(false)}
                className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-11 lg:h-11 rounded-lg sm:rounded-xl md:rounded-2xl bg-white/15 hover:bg-white/25 active:bg-white/35 backdrop-blur-sm border border-white/20 text-white hover:text-white flex items-center justify-center transition-all duration-200 flex-shrink-0 shadow-lg relative z-10"
                aria-label="Close modal"
              >
                <CSVIcon.X className="h-4 w-4 sm:h-5 sm:w-5 md:h-5 md:w-5 lg:h-6 lg:w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col bg-gradient-to-b from-slate-50/50 via-white to-slate-50/30 min-w-0">
              <div className="px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 md:py-5 border-b border-slate-200/60 bg-gradient-to-b from-white to-slate-50/50 overflow-x-hidden min-w-0 w-full">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5 md:gap-3 mb-3 sm:mb-4 w-full min-w-0">
                  <div className="flex-1 relative min-w-0 w-full">
                    <CSVIcon.Search className="absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400 pointer-events-none flex-shrink-0 z-10" />
                    <input
                      type="text"
                      placeholder="Search columns..."
                      value={csvColumnSearch}
                      onChange={(e) => setCsvColumnSearch(e.target.value)}
                      onBlur={(e) => setCsvColumnSearch(e.target.value.trim())}
                      className="w-full max-w-full pl-9 sm:pl-10 md:pl-12 pr-2.5 sm:pr-3 md:pr-4 py-2 sm:py-2.5 md:py-3 rounded-xl sm:rounded-2xl border-2 border-slate-200 focus:border-[#3877FC] focus:ring-4 focus:ring-[#3877FC]/10 outline-none transition-all duration-200 bg-white text-xs sm:text-sm md:text-base text-slate-900 placeholder:text-slate-400 shadow-sm font-medium min-w-0"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 min-w-0">
                    <button
                      onClick={() => {
                        const allKeys = allCSVColumns.map(col => col.key);
                        setSelectedCSVColumns(allKeys);
                        setCsvColumnOrder(allKeys);
                      }}
                      className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg sm:rounded-xl md:rounded-2xl bg-gradient-to-r from-[#3877FC] to-[#2A59BD] hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900 text-white font-bold transition-all duration-200 text-xs sm:text-sm flex items-center gap-1 sm:gap-1.5 border-0 shadow-lg whitespace-nowrap flex-shrink-0"
                    >
                      <CSVIcon.Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span className="hidden sm:inline">Select All</span>
                      <span className="sm:hidden">All</span>
                    </button>
                    <button
                      onClick={() => {
                        const defaultKeys = allCSVColumns.filter(col => col.defaultSelected).map(col => col.key);
                        setSelectedCSVColumns(defaultKeys);
                        setCsvColumnOrder(defaultKeys);
                      }}
                      className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg sm:rounded-xl md:rounded-2xl bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-700 hover:text-slate-900 font-bold transition-all duration-200 text-xs sm:text-sm border-2 border-slate-300 shadow-sm whitespace-nowrap flex-shrink-0"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5 flex-wrap w-full min-w-0">
                  {csvColumnCategories.map((cat) => {
                    const isActive = csvColumnCategory === cat.key;
                    const categoryColumns = cat.key === "all"
                      ? allCSVColumns
                      : allCSVColumns.filter(col => col.category === cat.key);
                    const selectedInCategory = categoryColumns.filter(col => selectedCSVColumns.includes(col.key)).length;

                    const getCategoryClasses = (color, active) => {
                      const colorMap = {
                        slate: active
                          ? 'bg-gradient-to-r from-slate-700 to-slate-800 text-white shadow-lg border-slate-600 ring-2 ring-slate-500/30'
                          : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300 shadow-sm',
                        indigo: active
                          ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-lg border-indigo-500 ring-2 ring-indigo-500/30'
                          : 'bg-white text-indigo-700 hover:bg-indigo-50 border-indigo-200 shadow-sm',
                        blue: active
                          ? 'bg-gradient-to-r from-[#3877FC] to-[#2A59BD] text-white shadow-lg border-[#3877FC] ring-2 ring-[#3877FC]/30'
                          : 'bg-white text-blue-700 hover:bg-blue-50 border-blue-200 shadow-sm',
                        emerald: active
                          ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg border-emerald-500 ring-2 ring-emerald-500/30'
                          : 'bg-white text-emerald-700 hover:bg-emerald-50 border-emerald-200 shadow-sm',
                        amber: active
                          ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-lg border-amber-500 ring-2 ring-amber-500/30'
                          : 'bg-white text-amber-700 hover:bg-amber-50 border-amber-200 shadow-sm',
                        purple: active
                          ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg border-purple-500 ring-2 ring-purple-500/30'
                          : 'bg-white text-purple-700 hover:bg-purple-50 border-purple-200 shadow-sm',
                        rose: active
                          ? 'bg-gradient-to-r from-rose-600 to-rose-700 text-white shadow-lg border-rose-500 ring-2 ring-rose-500/30'
                          : 'bg-white text-rose-700 hover:bg-rose-50 border-rose-200 shadow-sm',
                      };
                      return colorMap[color] || colorMap.slate;
                    };

                    const getBadgeClasses = (color, active) => {
                      const colorMap = {
                        slate: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-slate-100 text-slate-700',
                        indigo: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-indigo-100 text-indigo-800',
                        blue: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-blue-100 text-blue-800',
                        emerald: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-emerald-100 text-emerald-800',
                        amber: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-amber-100 text-amber-800',
                        purple: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-purple-100 text-purple-800',
                        rose: active ? 'bg-white/30 text-white backdrop-blur-sm' : 'bg-rose-100 text-rose-800',
                      };
                      return colorMap[color] || colorMap.slate;
                    };

                    return (
                      <button
                        key={cat.key}
                        onClick={() => setCsvColumnCategory(cat.key)}
                        className={`px-2 sm:px-2.5 md:px-3 lg:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg sm:rounded-xl md:rounded-2xl font-bold text-[10px] sm:text-xs md:text-sm transition-all duration-200 flex items-center gap-1 sm:gap-1.5 md:gap-2 border-2 flex-shrink-0 max-w-full ${getCategoryClasses(cat.color, isActive)}`}
                      >
                        <span className="text-sm sm:text-base md:text-lg flex-shrink-0">{cat.icon}</span>
                        <span className="whitespace-nowrap text-xs sm:text-sm">{cat.label}</span>
                        <span className={`text-[9px] sm:text-[10px] md:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-extrabold flex-shrink-0 ${getBadgeClasses(cat.color, isActive)}`}>
                          {selectedInCategory}/{categoryColumns.length}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 sm:p-3 md:p-4 lg:p-6 xl:p-8 custom-scrollbar min-w-0 w-full">
                {(() => {
                  const filteredColumns = allCSVColumns.filter(col => {
                    const matchesSearch = !csvColumnSearch ||
                      col.label.toLowerCase().includes(csvColumnSearch.toLowerCase()) ||
                      col.description?.toLowerCase().includes(csvColumnSearch.toLowerCase());
                    const matchesCategory = csvColumnCategory === "all" || col.category === csvColumnCategory;
                    return matchesSearch && matchesCategory;
                  });

                  const orderedColumns = csvColumnOrder.length > 0
                    ? [...csvColumnOrder.map(key => filteredColumns.find(col => col.key === key)).filter(Boolean),
                    ...filteredColumns.filter(col => !csvColumnOrder.includes(col.key))]
                    : filteredColumns;

                  if (orderedColumns.length === 0) {
                    return (
                      <div className="text-center py-12 sm:py-16">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                          <CSVIcon.Search className="h-8 w-8 text-slate-400" />
                        </div>
                        <p className="text-slate-600 text-base sm:text-lg font-medium">No columns found</p>
                        <p className="text-slate-500 text-sm mt-1">Try adjusting your search or filter</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-2 sm:space-y-2.5">
                      {orderedColumns.map((column, index) => {
                        const isSelected = selectedCSVColumns.includes(column.key);
                        const category = csvColumnCategories.find(cat => cat.key === column.category);

                        return (
                          <div
                            key={column.key}
                            draggable
                            onDragStart={(e) => {
                              setDraggedColumn(column.key);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (draggedColumn && draggedColumn !== column.key) {
                                const newOrder = [...csvColumnOrder];
                                const draggedIndex = newOrder.indexOf(draggedColumn);
                                const targetIndex = newOrder.indexOf(column.key);
                                if (draggedIndex !== -1 && targetIndex !== -1) {
                                  newOrder.splice(draggedIndex, 1);
                                  newOrder.splice(targetIndex, 0, draggedColumn);
                                  setCsvColumnOrder(newOrder);
                                }
                              }
                              setDraggedColumn(null);
                            }}
                            className={`group flex items-center gap-2 sm:gap-3 md:gap-4 p-2.5 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl border-2 transition-all duration-200 cursor-move ${isSelected
                                ? 'border-[#3877FC] bg-gradient-to-r from-blue-50/80 to-indigo-50/80 hover:from-blue-50 hover:to-indigo-50 shadow-md shadow-[#3877FC]/20 ring-2 ring-[#3877FC]/10'
                                : 'border-slate-200 bg-white hover:bg-slate-50/80 shadow-sm'
                              } ${draggedColumn === column.key ? 'opacity-40 scale-95' : ''}`}
                          >
                            <div className="flex-shrink-0 text-slate-400 group-hover:text-slate-600 transition-colors cursor-grab active:cursor-grabbing">
                              <CSVIcon.GripVertical className="h-4 w-4 sm:h-5 sm:w-5" />
                            </div>

                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCSVColumns([...selectedCSVColumns, column.key]);
                                  if (!csvColumnOrder.includes(column.key)) {
                                    setCsvColumnOrder([...csvColumnOrder, column.key]);
                                  }
                                } else {
                                  setSelectedCSVColumns(selectedCSVColumns.filter(k => k !== column.key));
                                }
                              }}
                              className="h-4 w-4 sm:h-5 sm:w-5 accent-[#3877FC] cursor-pointer flex-shrink-0 rounded border-2 border-slate-300 focus:ring-2 focus:ring-[#3877FC]/30 transition-all"
                            />

                            <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center text-base sm:text-lg md:text-xl shadow-sm ${isSelected ? 'bg-gradient-to-br from-blue-100 to-indigo-100 border-2 border-blue-300' : 'bg-slate-100 border-2 border-slate-200'
                              }`}>
                              {column.icon}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                                <span className={`font-bold text-sm sm:text-base md:text-lg ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                                  {column.label}
                                </span>
                                {column.defaultSelected && (
                                  <span className="text-[10px] sm:text-xs px-2 sm:px-2.5 py-0.5 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 font-bold border-2 border-blue-200">
                                    Default
                                  </span>
                                )}
                                {category && (() => {
                                  const categoryColorMap = {
                                    slate: 'bg-slate-100 text-slate-700 border-slate-200',
                                    indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
                                    blue: 'bg-blue-100 text-blue-700 border-blue-200',
                                    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                                    amber: 'bg-amber-100 text-amber-700 border-amber-200',
                                    purple: 'bg-purple-100 text-purple-700 border-purple-200',
                                    rose: 'bg-rose-100 text-rose-700 border-rose-200',
                                  };
                                  return (
                                    <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-semibold border ${categoryColorMap[category.color] || categoryColorMap.slate}`}>
                                      {category.label}
                                    </span>
                                  );
                                })()}
                              </div>
                              {column.description && (
                                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{column.description}</p>
                              )}
                            </div>

                            {isSelected && (
                              <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-[#3877FC] to-[#2A59BD] flex items-center justify-center shadow-lg shadow-[#3877FC]/40 ring-2 ring-[#3877FC]/20">
                                <CSVIcon.Check className="h-4 w-4 sm:h-5 sm:w-5 md:h-5 md:w-5 text-white" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {selectedCSVColumns.length > 0 && (
                <div className="px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 border-t-2 border-slate-200/60 bg-gradient-to-b from-blue-50/30 via-white to-slate-50/30 w-full min-w-0 overflow-x-hidden">
                  <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5 mb-2 sm:mb-2.5 md:mb-3 flex-wrap min-w-0">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30 flex-shrink-0">
                      <CSVIcon.Eye className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 lg:h-4.5 lg:w-4.5 text-white" />
                    </div>
                    <span className="text-xs sm:text-sm md:text-base font-bold text-slate-900 whitespace-nowrap">Export Preview</span>
                    <span className="text-xs sm:text-sm md:text-base font-bold text-blue-700 bg-blue-100 px-1.5 sm:px-2 py-0.5 rounded-lg whitespace-nowrap flex-shrink-0">({selectedCSVColumns.length} columns)</span>
                  </div>
                  <div className="flex flex-wrap gap-1 sm:gap-1.5 md:gap-2 max-h-24 sm:max-h-28 overflow-y-auto custom-scrollbar w-full min-w-0">
                    {csvColumnOrder.length > 0
                      ? csvColumnOrder
                        .filter(key => selectedCSVColumns.includes(key))
                        .map(key => {
                          const col = allCSVColumns.find(c => c.key === key);
                          return col ? (
                            <span
                              key={key}
                              className="inline-flex items-center gap-0.5 sm:gap-1 md:gap-1.5 px-1.5 sm:px-2 md:px-2.5 lg:px-3 py-0.5 sm:py-1 md:py-1.5 rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 text-[10px] sm:text-xs md:text-sm text-blue-900 font-bold shadow-sm hover:from-blue-50 hover:to-indigo-50 transition-all flex-shrink-0 max-w-full"
                            >
                              <span className="text-[10px] sm:text-xs md:text-sm flex-shrink-0">{col.icon}</span>
                              <span className="whitespace-nowrap truncate max-w-[120px] sm:max-w-[150px] md:max-w-none">{col.label}</span>
                            </span>
                          ) : null;
                        })
                      : selectedCSVColumns.map(key => {
                        const col = allCSVColumns.find(c => c.key === key);
                        return col ? (
                          <span
                            key={key}
                            className="inline-flex items-center gap-0.5 sm:gap-1 md:gap-1.5 px-1.5 sm:px-2 md:px-2.5 lg:px-3 py-0.5 sm:py-1 md:py-1.5 rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 text-[10px] sm:text-xs md:text-sm text-blue-900 font-bold shadow-sm hover:from-blue-50 hover:to-indigo-50 transition-all flex-shrink-0 max-w-full"
                          >
                            <span className="text-[10px] sm:text-xs md:text-sm flex-shrink-0">{col.icon}</span>
                            <span className="whitespace-nowrap truncate max-w-[120px] sm:max-w-[150px] md:max-w-none">{col.label}</span>
                          </span>
                        ) : null;
                      })}
                  </div>
                </div>
              )}
            </div>

            <div className="px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 md:py-5 border-t-2 border-slate-200/60 bg-gradient-to-b from-white to-slate-50/50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3 md:gap-4 w-full min-w-0 overflow-x-hidden">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 flex-1 min-w-0">
                <div className="text-xs sm:text-sm md:text-base text-slate-700 font-medium whitespace-nowrap flex-shrink-0">
                  <span className="font-extrabold text-blue-700 text-base sm:text-lg md:text-xl">{selectedCSVColumns.length}</span>
                  <span className="mx-1 sm:mx-1.5">of</span>
                  <span className="font-bold">{allCSVColumns.length}</span>
                  <span className="ml-1 sm:ml-1.5">columns selected</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 min-w-0">
                  <label className="text-xs sm:text-sm text-slate-700 font-bold whitespace-nowrap flex-shrink-0">Format:</label>
                  <div className="w-44 min-w-0 flex-shrink-0">
                    <SelectMenu
                      value={csvExportFormat}
                      onChange={setCsvExportFormat}
                      buttonClassName="h-9 text-xs sm:text-sm font-semibold"
                      options={[
                        { value: "csv", label: "CSV (.csv)" },
                        { value: "excel", label: "Excel Compatible" },
                      ]}
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-shrink-0 min-w-0">
                <button
                  onClick={() => setShowCSVColumnModal(false)}
                  className="px-3 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-bold rounded-lg sm:rounded-xl md:rounded-2xl bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-all duration-200 shadow-md flex-1 sm:flex-none whitespace-nowrap min-w-0"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (selectedCSVColumns.length === 0) {
                      toast.error("Please select at least one column");
                      return;
                    }
                    setDownloadingCSV(true);
                    try {
                      await new Promise(resolve => setTimeout(resolve, 300));
                      handleDownloadSelectedCSV();
                      setShowCSVColumnModal(false);
                      toast.success(`CSV exported successfully with ${selectedCSVColumns.length} columns`);
                    } catch (error) {
                      toast.error("Failed to export CSV");
                    } finally {
                      setDownloadingCSV(false);
                    }
                  }}
                  disabled={selectedCSVColumns.length === 0 || downloadingCSV}
                  className="px-4 sm:px-6 md:px-8 lg:px-10 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-extrabold rounded-lg sm:rounded-xl md:rounded-2xl bg-gradient-to-r from-[#3877FC] via-[#2A59BD] to-[#1C3B7E] text-white hover:from-blue-500 hover:via-blue-600 hover:to-indigo-600 disabled:from-slate-400 disabled:via-slate-500 disabled:to-slate-500 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 transition-colors duration-200 flex items-center justify-center gap-1.5 sm:gap-2 flex-1 sm:flex-none min-w-[100px] sm:min-w-[120px] md:min-w-[140px] ring-2 ring-blue-500/20 whitespace-nowrap"
                >
                  {downloadingCSV ? (
                    <>
                      <span className="inline-block w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0"></span>
                      <span className="hidden sm:inline">Exporting...</span>
                      <span className="sm:hidden">Export...</span>
                    </>
                  ) : (
                    <>
                      <CSVIcon.Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 flex-shrink-0" />
                      <span>Export CSV</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }

        /* Hide scrollbar but keep functionality */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        /* Professional Custom Scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #94a3b8 #f8fafc;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #cbd5e1, #94a3b8);
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #94a3b8, #64748b);
          border-color: #cbd5e1;
        }

        .custom-scrollbar::-webkit-scrollbar-corner {
          background: #f8fafc;
        }

        .date-input-wrapper > .MuiPaper-root {
          width: 100%;
          height: 32px;
          padding: 0 0.75rem;
          border: 2px solid #e5e7eb !important;
          border-radius: 0.5rem !important;
          background: #ffffff !important;
          box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05) !important;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          transition: all 0.2s;
        }


        .date-input-wrapper .MuiTypography-root {
          font-size: 0.75rem;
          color: #111827;
          font-weight: 500;
        }

        .date-input-wrapper .MuiSvgIcon-root {
          font-size: 16px;
          color: #6b7280;
        }
      `}</style>
    </div>
  );
}
